import { defaultConsentLabels } from '@ventawiz/web-kit'

/**
 * English copy. The source language, and the shape every other locale matches.
 *
 * Nested by feature rather than flat, so a key reads as a path to where it is
 * used and an unused one is visible when the feature is deleted.
 *
 * The AMS pages keep their copy in modules under `./en/`, one per work unit,
 * wired in here — so units building those pages side by side never edit the
 * same file.
 */
import { forbidden, myAccess, requestAccess, scoped, shell } from './en/accessUx'
import { notifications } from './en/notifications'
import { amsActions } from './en/amsActions'
import { amsShared } from './en/amsShared'
import { amsViewCharts } from './en/amsViewCharts'
import { amsViewDrill } from './en/amsViewDrill'
import { amsViewKit } from './en/amsViewKit'
import { amsViewList } from './en/amsViewList'
import { amsViewPage } from './en/amsViewPage'
import { bidSimulator } from './en/bidSimulator'
import { autoOrdering } from './en/autoOrdering'
import { dashboard } from './en/dashboard'
import { dashboardInfo } from './en/dashboardInfo'
import { focusTerms } from './en/focusTerms'
import { productPriceSync } from './en/productPriceSync'
import { shopifySync } from './en/shopifySync'
import { reviewAms } from './en/reviewAms'
import { stockAgeing } from './en/stockAgeing'
import { stock } from './en/stock'
import { cashflow } from './en/cashflow'
import { poFulfilment } from './en/poFulfilment'
import { retailerPoFulfilment } from './en/retailerPoFulfilment'
import { spInvoices } from './en/spInvoices'
import { spPayments } from './en/spPayments'
import { vendorPayments } from './en/vendorPayments'
import { vendorInvoices } from './en/vendorInvoices'
import { retailerReceipts } from './en/retailerReceipts'
import { sellerFlex } from './en/sellerFlex'
import { replen } from './en/replen'
import { globalShipments } from './en/globalShipments'
import { creditDebitNotes } from './en/creditDebitNotes'
import { boxHistory } from './en/boxHistory'
import { purchaseOrders } from './en/purchaseOrders'
import { vendorOrders } from './en/vendorOrders'
import { salesInvoices } from './en/salesInvoices'
import { opsReview } from './en/opsReview'
import { boxes } from './en/boxes'
import { blockStock } from './en/blockStock'
import { warehouseTransfer } from './en/warehouseTransfer'
import { aiAnalyst } from './en/aiAnalyst'
import { aiAnalystComposer } from './en/aiAnalystComposer'
import { aiAnalystHistory } from './en/aiAnalystHistory'
import { aiAnalystMessage } from './en/aiAnalystMessage'
import { aiAnalystParts } from './en/aiAnalystParts'
import { aiAnalystShell } from './en/aiAnalystShell'

export const en = {
  common: {
    storeKinds: { seller: 'Seller', vendor: 'Vendor', df: 'Direct fulfilment', offline: 'Offline' },
    // A Dagster run's status, in words (components/common/RunStatusBadge).
    runStatus: {
      QUEUED: 'Queued',
      NOT_STARTED: 'Queued',
      STARTING: 'Starting',
      STARTED: 'Running',
      SUCCESS: 'Done',
      FAILURE: 'Failed',
      CANCELING: 'Cancelling',
      CANCELED: 'Cancelled',
    },
    appName: 'VentaWiz',
    // The shared page shell (components/common/page/DataPage).
    page: {
      breadcrumb: 'Breadcrumb',
      moreActions: 'More actions for {{title}}',
      moreFilters: 'More filters',
      activeFilters: 'Active filters',
      clearAll: 'Clear all',
      showChart: 'Show',
      hideChart: 'Hide',
    },
    retry: 'Try again',
    loading: 'Loading…',
    skipToContent: 'Skip to content',
    reference: 'Reference: {{id}}',
    breadcrumb: 'Breadcrumb',
    somethingWentWrong: 'Something went wrong.',
    yes: 'Yes',
    no: 'No',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} of {{pages}}',
    // A plural rule, not string concatenation. English needs two forms;
    // Polish needs three and Arabic six, and only i18next knows which.
    totalCount_one: '{{count}} total',
    totalCount_other: '{{count}} total',
    // "1–50 of 2,341": the numbers arrive formatted, so grouping follows the locale.
    range: '{{from}}–{{to}} of {{total}}',
    firstPage: 'First page',
    lastPage: 'Last page',
    pageNumber: 'Page {{page}}',
    pagination: 'Pagination',
    loadFailed: "Couldn't load",
    rowsPerPage: 'Rows per page',
    close: 'Close',
    cancel: 'Cancel',
    // A form's own buttons, for every dialog that checks and saves.
    save: 'Save',
    check: 'Check',
    working: 'Working…',
    // What a document's Check found (components/common/CheckProblems).
    checkResult: { onLine: 'Line {{line}}: {{message}}' },
    // Sample values in empty inputs: what shape to type, never a default.
    placeholder: {
      boxCodes: 'BX-1001\nBX-1002',
      orderCodes: 'VO-1043\nVO-1044',
    },
    copy: 'Copy',
    copyValue: 'Copy {{value}}',
    copied: '{{value}} copied',
    copyFailed: 'Could not copy.',
    // The zone a page's times read in: the header's button and its dialog
    // (components/layout/TimezoneButton, TimezoneDialog).
    timezone: {
      label: 'Timezone:',
      button: 'Time zone: {{zone}}. Change time zones',
      loading: 'Working out the time zone',
      title: 'Time zones',
      description:
        'Choose the time zone a page shows its times in — for all stores, or for one store. Only you see these.',
      backTo: 'Back to {{page}}',
      addTitle: 'Add a time zone',
      page: 'Page',
      store: 'Store',
      zone: 'Time zone',
      allStores: 'All stores',
      otherMarketplace: 'Other',
      pickPage: 'Pick a page',
      pickZone: 'Pick a time zone',
      searchPages: 'Search pages',
      searchStores: 'Search stores',
      search: 'Search time zones',
      noMatches: 'No matches',
      add: 'Add',
      update: 'Update',
      saving: 'Saving…',
      yours: 'Your time zones',
      yoursHint:
        'A page you have not added shows its store’s own time zone, or your profile’s time zone.',
      none: 'You have not added a time zone yet.',
      rule: '{{page}} on {{store}}',
      count_one: '{{count}} time zone',
      count_other: '{{count}} time zones',
      zoneFor: 'Time zone for {{label}}',
      remove: 'Delete {{label}}',
      actions: 'Actions',
      unknownStore: 'Store {{id}}',
      failed: 'Could not save the time zone. Try again.',
      // The header's box for the page on screen.
      popover: {
        allStores: 'All stores',
        zoneFor: 'Time zone for {{label}}',
        reset: 'Reset',
        allPages: 'All pages',
        fallback: {
          default: 'Default · {{zone}}',
          page: 'Same as all stores · {{zone}}',
          storeZone: 'Store’s own zone · {{zone}}',
          adsZone: 'Ads account zone · {{zone}}',
        },
      },
      // Where the zone came from, most specific first.
      source: {
        storePage: 'Your choice for this page on {{store}}.',
        page: 'Your choice for this page.',
        profile: 'Your profile’s default.',
        storeZone: '{{store}}’s own zone.',
        adsZone: '{{store}}’s ads profile zone.',
        browser: 'Your browser’s zone.',
      },
    },
    // The shared filter fields (components/common/FilterField).
    filter: {
      all: 'All',
      search: 'Search {{label}}',
      searchPlaceholder: 'Search…',
      noMatches: 'No matches',
      noneAvailable: 'None available',
      noneAvailableHint: 'There is nothing to filter by here yet.',
      loadFailed: 'Could not load',
      loadFailedHint: 'The options could not be loaded. Reload the page to try again.',
      picked: '{{label}} ({{count}})',
      remove: 'Remove {{label}}',
      more: '+{{count}}',
      moreHint_one: '{{count}} more picked',
      moreHint_other: '{{count}} more picked',
      any: 'Any',
      clear: 'Clear filters',
      clearField: 'Clear {{label}}',
      clearAll: 'Clear all',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      active_one: '{{count}} filter active',
      active_other: '{{count}} filters active',
      // The primary bar (components/common/filters/PrimaryFilterBar), in the
      // order every data page uses.
      primary: {
        label: 'Filters',
        warehouse: 'Warehouse',
        marketplace: 'Marketplace',
        store: 'Store',
        brand: 'Brand',
        dateRange: 'Date range',
        sku: 'SKU',
        skuPlaceholder: 'Contains…',
        asin: 'ASIN',
        asinPlaceholder: 'Contains…',
      },
      date: {
        from: '{{label}} from',
        to: '{{label}} to',
        presets: 'Presets',
        presetsFor: '{{label}} presets',
        any: 'Any date',
        since: 'From {{day}}',
        until: 'Until {{day}}',
        pick: 'Pick a date',
        pickEnd: 'From {{day}} · pick the end date',
        previousMonth: 'Previous month',
        nextMonth: 'Next month',
        month: 'Month',
        year: 'Year',
        custom: 'Custom',
        preset: {
          last7: 'Last 7 days',
          last15: 'Last 15 days',
          last30: 'Last 30 days',
          last60: 'Last 60 days',
          last90: 'Last 90 days',
          thisMonth: 'This month',
          thisYear: 'This year',
        },
      },
    },
    // The Operations pages' shared controls: filters, line editor, sheet upload.
    optional: '{{label}} (optional)',
    editor: {
      addLine: 'Add line',
      remove: 'Remove',
      removeLine: 'Remove line {{line}}',
      cell: '{{column}}, line {{line}}',
      pasteHint: 'Tip: paste rows copied from a spreadsheet into the first cell.',
    },
    requestId: 'Request ID',
    all: 'All',
    clearFilters: 'Clear filters',
    filters: 'Filters',
    getDetails: 'Get details',
    pageOfRows: 'Page {{page}} of {{pages}} · {{total}} rows',
    any: 'Any',
    search: 'Search…',
    noMatches: 'No matches',
    pageN: 'Page {{page}}',
    valid: 'Valid',
    void: 'Void',
    chooseWarehouse: 'Choose a warehouse',
    chooseCurrency: 'Choose a currency',
    // A typed amount the form could not read (lib/decimalInput).
    amountInput: {
      empty: 'Type an amount.',
      invalid: 'Type the amount as digits, like {{example}}.',
      ambiguous: 'This could be read two ways. Type it as {{example}}.',
      negative: 'Type an amount above zero.',
      precision: 'Use at most {{digits}} decimal places.',
      zero: 'Type an amount above zero.',
    },
    chooseWarehouseHint: 'This view is shown one warehouse at a time.',
    sheetUpload: {
      template: 'Download the template',
      column: 'Column',
      needed: 'Needed?',
      what: 'What to put in it',
      example: 'Example',
      guideLoading: 'Loading what goes in each column',
      requirement: {
        required: 'Required',
        optional: 'Optional',
        either: 'One of two',
      },
    },
  },
  forbidden,
  myAccess,
  requestAccess,
  scoped,
  shell,
  notifications,
  viewLevel: {
    label: 'View by',
    sku: 'SKU',
    brand: 'Brand',
    category: 'Category',
    subcategory: 'Subcategory',
  },
  // Signed in, but not to VentaWiz (components/layout/NoAppAccess).
  noAppAccess: {
    title: 'Your account can’t open VentaWiz',
    body: 'You are signed in, but no organisation you belong to gives you VentaWiz. Ask your administrator for access, or sign in as someone who has it.',
    back: 'Go back',
    signInAgain: 'Sign in as someone else',
  },
  chart: {
    noData: 'No data',
    moreDetails: 'More details: {{title}}',
    table: '{{title}}: data',
    label: 'Label',
    series: 'Series',
    value: 'Value',
    share: 'Share',
    other: 'Other',
    legend: 'Legend',
    empty: 'No data for this range',
    emptyHint: 'Try a wider date range, or fewer filters.',
    point: '{{label}}: {{value}}',
    seriesPoint: '{{series}} · {{label}}: {{value}}',
  },
  // The command menu (layout/CommandMenu).
  search: {
    open: 'Search pages and tickets',
    title: 'Search',
    description: 'Find a page or a ticket and open it.',
    placeholderShort: 'Search…',
    placeholder: 'Search pages, or a ticket by number, SKU or words…',
    results: 'Results',
    groups: { actions: 'Actions', pages: 'Pages', tickets: 'Tickets' },
    raise: 'Raise a ticket',
    searching: 'Searching…',
    empty: 'Nothing matches “{{query}}”',
    ticketsFailed: "Couldn't search tickets",
    hintMove: '↑ ↓ to move',
    hintOpen: '↵ to open',
    hintClose: 'esc to close',
  },
  stat: {
    delta: { up: 'Up', down: 'Down', flat: 'No change' },
    trend: '{{metric}} over the period, day by day',
  },
  export: {
    button: 'Export',
    pending: 'Exporting…',
    failed: "Couldn't export",
  },
  auth: {
    // Sign-in lives in VentaWiz-Auth's own UI now, and its copy went with it.
    // What is left is what this app says about a session it already has.
    signOut: 'Sign out',
    signedInAs: 'Signed in as',
    account: 'Account',
  },
  density: {
    // Each names the state the toggle is in, not what clicking does.
    compact: 'Rows: compact',
    comfortable: 'Rows: comfortable',
  },
  theme: {
    // Names the theme in use, not what clicking does.
    current: 'Theme: {{name}}',
    title: 'Appearance',
    description: 'Choose your theme. It applies to every page and is saved in this browser.',
    label: 'Theme',
    mode: { light: 'Light', dark: 'Dark', auto: 'Auto' },
    names: {
      system: 'System',
      default: 'Default',
      ocean: 'Ocean',
      emerald: 'Emerald',
      indigo: 'Indigo',
      amber: 'Amber',
      slate: 'Slate',
      dark: 'Dark',
      midnight: 'Midnight',
    },
    // What each theme looks like, not what it promises.
    descriptions: {
      system: 'Default by day, Dark by night — follows your device.',
      default: 'Warm grey, crimson actions, dark sidebar.',
      ocean: 'Cool grey, blue actions.',
      emerald: 'Grey-green, green actions, light sidebar.',
      indigo: 'Cool grey, indigo actions.',
      amber: 'Warm cream, gold actions.',
      slate: 'Slate grey, black actions. The quietest.',
      dark: 'Charcoal, crimson actions.',
      midnight: 'Navy, indigo actions.',
    },
  },
  nav: {
    home: 'Home',
    dashboard: 'Dashboard',
    tickets: 'Tickets',
    forum: 'Forum',
    myAccess: 'My access',
    aiAnalyst: 'Talk with AI',
    focusedSkus: 'Focused SKUs',
    focusedSkusItems: {
      manage: 'Manage',
      analysis: 'Analysis',
      searchTerm: 'Search term sheet',
      searchTermData: 'Search term data',
    },
    // A group inside Data Vault now, and the page trail's middle step for
    // the mail and ticket exceptions.
    exceptions: 'Exceptions',
    exceptionsItems: {
      ruleExclusions: 'Rule exclusions',
    },
    scrapedData: 'Scraped data',
    dataVault: 'Data Vault',
    dataVaultItems: {
      catalogue: 'Catalogue',
      ordersPayouts: 'Orders & payouts',
      overview: 'Overview',
      costs: 'Costs',
      retailerCosts: 'Retailer Costs',
      fees: 'Fees',
      internalSkus: 'Internal SKUs',
      mailExceptions: 'Mail exceptions',
      marketplaceData: 'Marketplace API data',
      orders: 'Orders',
      productPriceSync: 'Product/price sync',
      products: 'Products',
      returns: 'Returns',
      settlements: 'Settlements',
      ticketExceptions: 'Ticket exceptions',
    },
    // The key stays `marketing`: page trails across the ads pages use it.
    marketing: 'Advertising',
    marketingItems: {
      performance: 'Performance',
      automation: 'Automation',
      setup: 'Setup',
      amsHistory: 'Change history',
      amsView: 'AMS View',
      reviewAmsData: 'Review AMS data',
      bidSimulator: 'Bid simulator',
      // Not in the menu: the simulator links to it in-page.
      bidAccuracy: 'Bid Forecast Accuracy',
      searchAnalytics: 'Search analytics',
      adGroupMaster: 'Ad groups',
      executions: 'Rule runs',
      rules: 'Rules',
      rulesHealth: 'Rule health',
      dayparting: 'Dayparting',
      newTargeting: 'New targeting',
      reportSync: 'Report sync',
      optimization: 'Optimization',
    },
    business: 'Business',
    businessItems: {
      businessView: 'Business view',
      lossMakingSkus: 'Loss-making SKUs',
      partnerView: 'Partner view',
      paymentReconciliation: 'Payment reconciliation',
      reviewData: 'Review data',
      sellerOrders: 'Live seller orders',
    },
    aiListing: 'AI Listing',
    operations: 'Operations',
    opsItems: {
      stock: 'Stock',
      shipments: 'Shipments',
      purchasing: 'Purchasing',
      financeDocs: 'Finance documents',
      autoOrdering: 'Auto ordering',
      blockStock: 'Block stock',
      boxDetail: 'Box detail',
      boxHistory: 'Box history',
      cashflow: 'Cashflow',
      creditDebitNotes: 'Credit/debit notes',
      getStock: 'Get stock',
      globalShipments: 'Global shipments',
      inventorySync: 'Inventory sync',
      poFulfilment: 'PO fulfilment',
      purchaseOrders: 'Purchase orders',
      replen: 'Replenishment',
      retailerReceipts: 'Retailer receipts',
      review: 'Review order/invoice',
      salesInvoices: 'Sales invoices',
      sellerFlexStock: 'SellerFlex',
      spInvoices: 'SP invoices',
      spPayments: 'SP payments',
      stockAgeing: 'Ageing',
      vendorInvoices: 'Vendor invoices',
      vendorOrders: 'Vendor orders',
      vendorPayments: 'Vendor payments',
      warehouseTransfer: 'Warehouse transfer',
    },
    main: 'Main',
    // Overline labels over the sidebar's runs of sections.
    groups: { analyse: 'Analyse', run: 'Run' },
    // A page in the map that is not built yet: the tag on its row, and its
    // name as a screen reader and the icon strip's tooltip give it.
    soon: 'Soon',
    comingSoon: '{{name}}: coming soon',
    toggleSidebar: 'Toggle sidebar',
    // The sidebar's own button, named for what it does in the state it is in.
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    // On a phone the sidebar is a drawer.
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    // The phone drawer, as a screen reader names it.
    drawerTitle: 'Navigation',
    drawerDescription: 'Every section of VentaWiz.',
  },
  // The dashboard (./en/dashboard), and its cards' help panels (./en/dashboardInfo).
  dashboard,
  dashboardInfo,
  scrapedData: {
    openOn: 'Open {{id}} on {{site}} (opens in a new tab)',
    title: 'Scraped data',
    description: 'What the review and ads scrapers read off each marketplace.',
    tabs: { reviews: 'Reviews', ads: 'Ads' },
    marketplace: {
      amazon: 'Amazon',
      flipkart: 'Flipkart',
      noon: 'noon',
      otto: 'Otto',
      bol: 'Bol',
      walmart: 'Walmart',
      trendyol: 'Trendyol',
      shopify: 'Shopify',
    },
    filters: {
      marketplace: 'Marketplace',
      allMarketplaces: 'All marketplaces',
      store: 'Store',
      allStores: 'All stores',
      dates: 'Dates',
      search: 'Search',
      searchPlaceholder: 'ASIN, FSN or search term',
      pageSize: 'Rows',
    },
    columns: {
      details: 'Details',
      product: 'Product',
      store: 'Store',
      rating: 'Rating',
      ratingCount: 'Ratings',
      aplus: 'A+',
      delivery: 'Delivery',
      critical: 'Critical reviews',
      updated: 'Scraped',
      searchTerm: 'Search term',
      sponsored: 'Sponsored',
      organic: 'Organic',
      price: 'Price',
      scraped: 'Scraped',
    },
    // The scrapers report "unknown" separately from "no"; the table shows a
    // dash for unknown.
    yes: 'Yes',
    no: 'No',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    clearFilters: 'Clear filters',
    emptyFilteredHint: 'Nothing matches these filters. Widen the dates or clear them.',
    reviews: {
      show: 'View critical reviews for {{product}}',
      criticalHeading: 'Critical reviews — {{product}}',
      noCritical: 'No critical reviews on the page.',
      stars: '{{rating}}★',
      empty: 'Nothing scraped in the last 30 days',
      emptyHint:
        'Ratings appear once a SKU is marked as focused and the reviews scraper has run. Pick earlier dates to look further back.',
      emptyFiltered: 'No scraped products match',
    },
    ads: {
      show: 'View results for “{{term}}”',
      sponsoredHeading: 'Sponsored',
      organicHeading: 'Organic',
      none: 'None on the page.',
      empty: 'No searches scraped in the last 30 days',
      emptyHint: 'Searches run once a day. Pick earlier dates to look further back.',
      emptyFiltered: 'No searches match',
    },
  },
  products: {
    title: 'Products (Listings)',
    subtitle: 'What each store lists, linked to your SKUs, with its price.',
    subtitleStore: '{{store}} on {{marketplace}}: every listing, its SKU and its price.',
    query: 'Listing filters',
    store: 'Store',
    chooseStore: 'Choose a store',
    storeOption: '{{name}} · {{marketplace}}',
    search: 'Search',
    searchPlaceholder: 'Product ID, SKU or title',
    load: 'Load data',
    rowsPerPage: 'Rows per page',
    yes: 'Yes',
    no: 'No',
    pickTitle: 'Pick a store to see its listings',
    pickHint:
      'Each marketplace names its products differently, so listings load one store at a time.',
    empty: {
      title: 'No listings here',
      hint: 'This store has no listings yet. Upload its Product Data to add them.',
      searchHint: 'Nothing matches that search. Try a shorter product ID, SKU or title.',
    },
    columns: {
      productKey: 'Product ID',
      skuCode: 'SKU Code',
      mpSkuCode: 'Marketplace SKU Code',
      condition: 'Condition',
      title: 'Title',
      manufacturer: 'Manufacturer',
      category: 'Category',
      subcategory: 'Subcategory',
      description: 'Description',
      isFba: 'Is FBA',
      focusedSku: 'Focused SKU',
      drr: 'DRR',
      currency: 'Currency',
      regularPrice: 'Regular Price',
      salePrice: 'Sale Price',
      saleStart: 'Sale Start Date',
      saleEnd: 'Sale End Date',
      minPrice: 'Min Price',
      maxPrice: 'Max Price',
      backMargin: 'Back Margin',
      costMultiple: 'Cost Multiple',
      rrpMultiple: 'RRP Multiple',
      mfnShipping: 'MFN Shipping Price',
      vatRate: 'VAT Rate (%)',
    },
    upload: {
      upload: 'Upload listings',
      uploadDescription:
        'Add or update listings and their prices from the store’s Product Data sheet. Nothing is saved until you check it.',
      drr: 'Upload DRR',
      drrDescription:
        'Set each product’s daily run rate. Every listing of a product id in the store takes the rate, and is focused if it was not.',
      store: 'Store',
      chooseStore: 'Choose a store',
      storeOption: '{{name}} · {{marketplace}}',
      step1: 'Which store are these listings for?',
      step1Hint:
        'Each marketplace has its own Product Data sheet, so listings are uploaded one store at a time.',
      step2: 'Fill in the template',
      step2Template: 'Fill in the {{template}} Product Data template',
      step2Hint:
        'One row per listing. A listing already in the store is updated and gets a new current price. DRR is read but saved on the Focus SKU sheet.',
      templateAfterStore: 'Choose a store to see its marketplace’s template.',
      template: 'Download template',
      step3: 'Upload and check',
      step3Hint: 'Each {{id}} and SKU Code is checked before anything is saved.',
      step3NoStore: 'Drop the file now; it is checked once a store is chosen.',
      drop: 'Drop the filled-in template here, or click to choose it',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      checking: 'Checking the file…',
      remove: 'Remove',
      emptyFileTitle: 'This file has no listings in it yet',
      emptyFileHint: 'Fill in one row per listing under the header, then drop it here again.',
      counts: {
        new_one: '{{formatted}} new listing',
        new_other: '{{formatted}} new listings',
        updated_one: '{{formatted}} update',
        updated_other: '{{formatted}} updates',
        errors_one: '{{formatted}} error',
        errors_other: '{{formatted}} errors',
      },
      row: 'Row',
      result: 'Result',
      actions: { new: 'New', update: 'Update', ready: 'Ready' },
      more: '{{count}} more rows are not listed.',
      cancel: 'Cancel',
      readOnly:
        'Listings are read from a read-only database, so uploads are previewed but not saved.',
      saveIdle: 'Save listings',
      save_one: 'Save {{count}} listing',
      save_other: 'Save {{count}} listings',
      saved: 'Saved: {{inserted}} new, {{updated}} updated.',
    },
  },
  ingest: {
    store: 'Store',
    chooseStore: 'Choose a store',
    storeOption: '{{name}} · {{marketplace}}',
    steps: {
      store: 'Store',
      storeHint: 'Each marketplace has its own template, and rows are checked against this store.',
      template: 'Template',
      templateFor: '{{marketplace}} template',
      templateHint: 'Keep the header row as it is and fill one row per record.',
      templateWaiting: 'Available once you choose a store.',
      file: 'Upload and check',
      fileHint: 'Every row is checked against your data. Nothing is saved until you press Save.',
    },
    template: {
      summary_one: '{{count}} column, {{required}} required',
      summary_other: '{{count}} columns, {{required}} required',
      showColumns: 'Columns',
      hideColumns: 'Hide columns',
      download: 'Download template',
      required: 'Required',
      optional: 'Optional',
    },
    mode: {
      label: 'Rows already saved',
      options: { modify: 'Update them', insert: 'Add new rows only' },
      hints: {
        modify: 'New rows are added and rows already saved are updated with the file’s values.',
        insert: 'Rows already saved are reported as errors and left unchanged.',
      },
    },
    file: {
      clickToUpload: 'Click to upload',
      orDrag: 'or drag and drop the filled-in template',
      dropNow: 'Drop it to upload',
      ready: 'Ready',
      formats: 'Excel (.xlsx) or CSV, up to 20 MB',
      waitingStore: 'Choose a store first',
      waitingChoices: 'Make the choices above first',
      checking: 'Checking every row…',
      size: '{{size}} KB',
      replace: 'Replace',
      remove: 'Remove {{name}}',
      removeShort: 'Remove file',
    },
    preview: {
      notTemplate: 'This file is not the template for this store',
      notTemplateHint: 'Download the template above, copy your rows into it, and upload it again.',
      whatIsWrong: 'What is wrong with the file',
      expectedColumns: 'Columns the template expects',
      fileColumns: 'Columns in your file',
      required: 'required',
      ignoredColumns: 'Some columns are not in the template and will be ignored',
      emptyTitle: 'There are no rows under the header',
      emptyHint: 'Fill in one row per record below the header row, then upload it again.',
      duplicate: 'This exact file was saved before. Saving it again changes nothing.',
      tiles: {
        rows: 'Rows',
        new: 'New',
        update: 'Updates',
        unchanged: 'Unchanged',
        errors: 'Errors',
      },
      fixAndRetry_one:
        '{{count}} row has errors, so nothing can be saved yet. Fix or delete it with Edit, or fix the file and upload it again.',
      fixAndRetry_other:
        '{{count}} rows have errors, so nothing can be saved yet. Fix or delete them with Edit, or fix the file and upload it again.',
      filterLabel: 'Show rows',
      filter: {
        all: 'All',
        error: 'Errors',
        new: 'New',
        update: 'Updates',
        unchanged: 'Unchanged',
      },
      warnings_one: '{{formatted}} warning',
      warnings_other: '{{formatted}} warnings',
      table: { row: 'Row', status: 'Status', notes: 'Problems' },
      actions: { new: 'New', update: 'Update', unchanged: 'Unchanged', error: 'Error' },
      blank: 'blank',
      noneMatch: 'No rows match this filter.',
      truncated: 'Only part of the file is listed here; the counts above cover every row.',
    },
    outcome: {
      stages: {
        saved: 'Saved',
        approval: 'Approved',
        loading: 'Updating the tables',
        done: 'Done',
      },
      awaitingTitle: 'Sent for approval · Ticket #{{ticket}}',
      awaitingHint:
        'A reviewer accepts or rejects the file on its ticket. The rows reach the tables only once it is accepted; this panel follows it.',
      raising: 'Raising the approval ticket…',
      openTicket: 'Open ticket',
      rejectedTitle: 'Rejected · Ticket #{{ticket}}',
      rejectedHint: 'Nothing was written to the tables. The reason is on the ticket.',
      loaded_one: '{{formatted}} row is in',
      loaded_other: '{{formatted}} rows are in',
      loadedHint: 'The page behind this panel shows them now.',
      loadedWithRejects_one: '{{loaded}} row is in; {{rejected}} could not be placed',
      loadedWithRejects_other: '{{loaded}} rows are in; {{rejected}} could not be placed',
      rejectsHint:
        'These rows name something that is not in the store yet. Fix them and upload them again.',
      failedTitle: 'The tables could not be updated',
      failedHint: 'It is tried again automatically. If this stays, send support upload #{{id}}.',
      table: { row: 'Row', reason: 'Why it was not placed' },
      reasons: {
        notThere: '{{thing}} {{value}} is not in this store yet.',
        notThereGeneric: 'It names something that is not in this store yet.',
        alreadyExists: 'It is already saved; upload it with “Update them” to change it.',
        incomplete: 'A value it needs to be identified is missing.',
      },
      things: {
        order: 'Order',
        orderLine: 'Order line',
        listing: 'Listing',
        settlement: 'Settlement',
        sku: 'SKU',
        brand: 'Brand',
        warehouse: 'Warehouse',
        retailer: 'Retailer',
        vendor: 'Vendor',
      },
    },
    footer: {
      idle: 'Choose a file to check it.',
      checking: 'Checking the file…',
      notTemplate: 'This file is not the template — nothing can be saved.',
      empty: 'The file has no rows to save.',
      blocked_one: '{{formatted}} row needs fixing before you can save.',
      blocked_other: '{{formatted}} rows need fixing before you can save.',
      ready_one: '{{formatted}} row ready to save.',
      ready_other: '{{formatted}} rows ready to save.',
      nothingNew: 'Every row is already saved as it is.',
      loading: 'Saved. Updating the tables…',
      awaitingApproval:
        'Sent for approval as ticket #{{ticket}}. Nothing changes until it is approved.',
      rejected: 'Rejected by a reviewer. Nothing was saved to the tables.',
      done: 'Done.',
      failed: 'Saved, but the tables could not be updated yet.',
    },
    cancel: 'Cancel',
    close: 'Close',
    save_one: 'Save {{formatted}} row',
    save_other: 'Save {{formatted}} rows',
    saveIdle: 'Save',
    saving: 'Saving…',
    requestId: 'Request id: {{id}}',
    previewTitle: 'Preview: {{name}}',
    // The Operations pages' upload sheet (components/common/upload).
    operations: {
      notTemplate: 'This file is not the template for this sheet',
      awaitingHint:
        'A reviewer accepts or rejects the sheet on its ticket. Nothing on the page changes until it is accepted; this panel follows the ticket and refreshes the page once the rows are in. You can close it — the ticket goes on without it.',
      rejectsHint:
        'The rows below were refused when they were applied; every other row is in. Fix them and upload them again.',
      notThere: 'It names something that is not in VentaWiz yet.',
    },
    approvalToast: {
      title: 'Sent for approval',
      ticket: 'Ticket #',
      hint: 'Nothing reaches the tables until a reviewer accepts it.',
    },
    previewEdit: {
      label: 'Preview mode',
      view: 'View',
      edit: 'Edit',
      hint: 'Fix any cell here, then check the file again. A row you do not need can be deleted. Nothing is saved until you press Save.',
      changed_one: '{{count}} cell changed',
      changed_other: '{{count}} cells changed',
      deleted_one: '{{count}} row deleted',
      deleted_other: '{{count}} rows deleted',
      delete: 'Delete row',
      deleteRow: 'Delete row {{row}}',
      restore: 'Undo delete',
      restoreRow: 'Undo delete of row {{row}}',
      deleteErrors_one: 'Delete the row with errors',
      deleteErrors_other: 'Delete all {{formatted}} rows with errors',
      discard: 'Discard',
      recheck: 'Check again',
      rechecking: 'Checking…',
      tooLarge: 'This file is too large to edit here. Fix it in the file and upload it again.',
    },
    previewHint:
      'Every row as the file has it. Cells bordered red need fixing before you can save.',
    // The preview's summary of what is wrong, and the way from it to each cell.
    problems: {
      title_one: '{{formatted}} row needs fixing before you can save',
      title_other: '{{formatted}} rows need fixing before you can save',
      titleSheet_one: 'The file has a problem',
      titleSheet_other: 'The file has {{count}} problems',
      header: 'Column header {{column}}:',
      sheet: 'The file:',
      wholeRow: 'Whole row',
      rows_one: '— {{formatted}} row:',
      rows_other: '— {{formatted}} rows:',
      rowButton: 'Row {{row}}',
      goCell: 'Go to {{column}} on row {{row}}',
      goRow: 'Go to row {{row}}',
      more_one: '+{{formatted}} more',
      more_other: '+{{formatted}} more',
      navigate: 'Errors',
      previous: 'Previous error',
      next: 'Next error',
      position: 'Error {{at}} of {{total}}',
      cells_one: '{{formatted}} cell to fix',
      cells_other: '{{formatted}} cells to fix',
    },
    // VentaWiz-Ingest's issue codes said plainly; every other code keeps the service's words.
    issues: {
      missing_value: 'Required — this cell is empty',
      invalid_number: 'Must be a number',
      invalid_integer: 'Must be a whole number',
      invalid_date: 'Must be a date, like 31/12/2026 or 2026-12-31',
      invalid_currency: 'Must be a currency code, like EUR',
      invalid_boolean: 'Must be yes or no',
    },
    openPreview: 'Open preview',
    back: 'Back',
  },
  settlements: {
    title: 'Settlements',
    subtitle: 'What each marketplace paid out, and the order lines, fees and refunds behind it.',
    subtitleStore:
      '{{store}} on {{marketplace}}: every payout and the lines it is made of, latest first.',
    query: 'Settlement filters',
    store: 'Store',
    chooseStore: 'Choose a store',
    storeOption: '{{name}} · {{marketplace}}',
    depositDate: 'Deposit date',
    search: 'Search',
    searchPlaceholder: 'Settlement, order, SKU or type',
    load: 'Load data',
    rowsPerPage: 'Rows per page',
    pickTitle: 'Pick a store to see its settlements',
    pickHint:
      'Each marketplace pays out on its own schedule, so settlements load one store at a time.',
    empty: {
      title: 'No settlements recorded for this store',
      hint: 'Upload the store’s Settlement Data sheet to record its payouts.',
      filteredTitle: 'No settlement lines match',
      filteredHint: 'Widen the deposit dates or try a shorter search.',
    },
    columns: {
      settlement_id: 'Settlement ID',
      start_date: 'Start Date',
      end_date: 'End Date',
      deposit_date: 'Deposit Date',
      amount_total: 'Total Amount',
      order_id: 'Order ID',
      sku_code: 'SKU Code',
      order_item_id: 'Order Item ID',
      product_key: 'Product ID',
      transaction_type: 'Transaction Type',
      shipment_id: 'Shipment ID',
      amount_type: 'Amount Type',
      amount: 'Amount',
    },
    upload: {
      upload: 'Upload settlements',
      uploadDescription:
        'Record payouts from the store’s Settlement Data sheet. Nothing is saved until you check it.',
      store: 'Store',
      chooseStore: 'Choose a store',
      storeOption: '{{name}} · {{marketplace}}',
      step1: 'Which store are these settlements for?',
      step1Hint:
        'Each marketplace has its own Settlement Data sheet, and lines are matched to that store’s orders.',
      step2: 'Fill in the template',
      step2Template: 'Fill in the {{template}} Settlement Data template',
      step2Hint:
        'One row per line of a payout; one sheet can hold several payouts. A payout already saved is replaced with the file’s lines.',
      templateAfterStore: 'Choose a store to see its marketplace’s template.',
      template: 'Download template',
      step3: 'Upload and check',
      step3Hint: 'Each Order ID and product is matched to the store before anything is saved.',
      step3NoStore: 'Drop the file now; it is checked once a store is chosen.',
      drop: 'Drop the filled-in template here, or click to choose it',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      checking: 'Checking the file…',
      remove: 'Remove',
      emptyFileTitle: 'This file has no settlement lines in it yet',
      emptyFileHint: 'Fill in one row per payout line under the header, then drop it here again.',
      counts: {
        new_one: '{{formatted}} new settlement',
        new_other: '{{formatted}} new settlements',
        updated_one: '{{formatted}} replaced',
        updated_other: '{{formatted}} replaced',
        lines_one: '{{formatted}} line',
        lines_other: '{{formatted}} lines',
        errors_one: '{{formatted}} error',
        errors_other: '{{formatted}} errors',
      },
      row: 'Row',
      lines: 'Lines',
      result: 'Result',
      actions: { new: 'New', update: 'Replace', ready: 'Ready' },
      more: '{{count}} more rows are not listed.',
      cancel: 'Cancel',
      readOnly:
        'Settlements are read from a read-only database, so uploads are previewed but not saved.',
      saveIdle: 'Save settlements',
      save_one: 'Save {{count}} line',
      save_other: 'Save {{count}} lines',
      saved: 'Saved: {{inserted}} new and {{updated}} replaced settlements, {{lines}} lines.',
    },
    // Money recorded against an operations invoice (components/common/settlements).
    form: {
      hint: 'Name the {{document}} by its number in its warehouse. The amount is added to what has been {{settled}} against it, in the invoice’s currency.',
      warehouse: 'Warehouse',
      invoice: '{{document}} number',
      chooseInvoice: 'Choose the {{document}}',
      amount: 'Amount',
      date: 'Date',
      comment: 'Comment',
      save: 'Save',
      needWarehouse: 'Choose the warehouse.',
      needInvoice: 'Choose the {{document}}.',
      saved: '{{noun}} recorded: {{invoice}} now has {{total}} {{settled}} of {{of}}',
    },
    statement: {
      title: 'Upload a statement',
      hint: 'One {{noun}} per row, across any warehouses. Every row is checked first, and a row with a problem holds the sheet back until it is fixed. Then it goes for approval, and the rows are recorded once it is accepted.',
    },
    void: {
      action: 'Void',
      title: 'Void the {{noun}} {{label}}?',
      description:
        'Its amount comes off what has been {{settled}} against the invoice. It stays in the list as void.',
      done: '{{noun}} voided',
      row: 'Void {{label}}',
      column: 'Actions',
    },
    actions: {
      upload: 'Upload statement',
    },
  },
  returns: {
    title: 'Returns',
    subtitle: 'What came back from each store, where it was boxed, and what was claimed for it.',
    subtitleStore: '{{store}} on {{marketplace}}: every return, its box, claim and credit note.',
    query: 'Return filters',
    store: 'Store',
    chooseStore: 'Choose a store',
    storeOption: '{{name}} · {{marketplace}}',
    search: 'Search',
    searchPlaceholder: 'Order ID, SKU, box, claim or credit note',
    load: 'Load data',
    rowsPerPage: 'Rows per page',
    readOnly: 'Returns are read from a read-only database, so they cannot be changed here.',
    pickTitle: 'Pick a store to see its returns',
    pickHint: 'Returns are matched to each store’s orders, so they load one store at a time.',
    empty: {
      title: 'No returns recorded for this store',
      hint: 'Upload the store’s Return Data sheet to record what came back.',
      searchTitle: 'No returns match',
      searchHint: 'Try a shorter order ID, SKU, box, claim or credit note number.',
    },
    columns: {
      order_id: 'Order ID',
      sku_code: 'SKU Code',
      box_id: 'Box ID',
      quantity: 'Quantity',
      item_condition: 'Item Condition',
      claim_id: 'Claim ID',
      claim_result: 'Claim Result',
      credit_note_num: 'Credit Note #',
      price: 'Price',
      actions: 'Actions',
    },
    edit: {
      action: 'Edit return',
      open: 'Edit the return on order {{order}}',
      title: 'Edit return · {{order}}',
      description: 'SKU {{sku}}. The order and SKU stay as they are.',
      quantityInvalid: 'Enter a whole number, zero or more.',
      priceInvalid: 'Enter an amount with up to four decimals.',
      cancel: 'Cancel',
      save: 'Save changes',
      saved: 'Return on order {{order}} saved.',
    },
    upload: {
      upload: 'Upload returns',
      uploadDescription:
        'Record returns from the store’s Return Data sheet. Nothing is saved until you check it.',
      store: 'Store',
      chooseStore: 'Choose a store',
      storeOption: '{{name}} · {{marketplace}}',
      step1: 'Which store are these returns for?',
      step1Hint:
        'Each marketplace has its own Return Data sheet, and returns are matched to that store’s orders.',
      step2: 'Fill in the template',
      step2Template: 'Fill in the {{template}} Return Data template',
      step2Hint:
        'One row per returned order line. A line that already has a return is updated. Claim Result is read but not saved yet.',
      templateAfterStore: 'Choose a store to see its marketplace’s template.',
      template: 'Download template',
      step3: 'Upload and check',
      step3Hint: 'Each Order ID and SKU is matched to an order line before anything is saved.',
      step3NoStore: 'Drop the file now; it is checked once a store is chosen.',
      drop: 'Drop the filled-in template here, or click to choose it',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      checking: 'Checking the file…',
      remove: 'Remove',
      emptyFileTitle: 'This file has no returns in it yet',
      emptyFileHint:
        'Fill in one row per returned order line under the header, then drop it here again.',
      counts: {
        new_one: '{{formatted}} new return',
        new_other: '{{formatted}} new returns',
        updated_one: '{{formatted}} update',
        updated_other: '{{formatted}} updates',
        errors_one: '{{formatted}} error',
        errors_other: '{{formatted}} errors',
      },
      row: 'Row',
      result: 'Result',
      actions: { new: 'New', update: 'Update', ready: 'Ready' },
      more: '{{count}} more rows are not listed.',
      cancel: 'Cancel',
      readOnly:
        'Returns are read from a read-only database, so uploads are previewed but not saved.',
      saveIdle: 'Save returns',
      save_one: 'Save {{count}} return',
      save_other: 'Save {{count}} returns',
      saved: 'Saved: {{inserted}} new, {{updated}} updated.',
    },
  },
  orders: {
    title: 'Orders',
    subtitle: 'Every marketplace order from {{from}} to {{to}}.',
    filters: 'Order filters',
    statusTabs: 'Order status',
    rowsPerPage: 'Rows per page',
    chart: 'Orders per day',
    noLines: 'No lines yet',
    moreLines_one: '+{{count}} more item',
    moreLines_other: '+{{count}} more items',
    filter: {
      stores: 'Stores',
      dates: 'Date range',
      fulfilment: 'Fulfilment',
      search: 'Order or SKU',
      searchPlaceholder: 'Order ID, SKU or ASIN',
      any: 'Any',
      getDetails: 'Get details',
    },
    cards: {
      orders: 'Orders',
      ordersHint: 'Placed in the period',
      units: 'Units',
      unitsHint: 'Ordered across every line',
      revenue: 'Revenue',
      revenueHint: 'Order totals, in their own currency',
      revenueMore_one: 'Largest currency · {{count}} more currency',
      revenueMore_other: 'Largest currency · {{count}} more currencies',
      average: 'Average order',
      averageHint: 'In {{currency}}',
    },
    columns: {
      date: 'Date',
      order: 'Order',
      store: 'Store',
      items: 'Items',
      units: 'Units',
      total: 'Total',
      fulfilment: 'Fulfilment',
      status: 'Status',
    },
    fulfilmentHint: { FBA: 'Fulfilled by the marketplace', FBM: 'Fulfilled by you' },
    status: {
      all: 'All',
      pending: 'Pending',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      returned: 'Returned',
      other: 'Other',
    },
    empty: {
      title: 'No orders match these filters',
      hint: 'Widen the date range, choose other stores, or clear the search.',
    },
    uploadButton: 'Upload orders',
    columns2: {
      orderId: 'Order ID',
      orderItemId: 'Order Item ID',
      asin: 'ASIN',
      skuCode: 'SKU Code',
      category: 'SKU Category',
      subcategory: 'SKU SubCategory',
      brand: 'Brand',
      purchaseDate: 'Purchase Date',
      lastUpdate: 'Last Update',
      orderStatus: 'Order Status',
      isFba: 'Is FBA',
      orderType: 'Order Type',
      qtyOrdered: 'Qty Ordered',
      qtyShipped: 'Qty Shipped',
      itemPrice: 'Item Price',
      taxAmount: 'Tax Amount',
      shippingAmount: 'Shipping Amount',
      discountAmount: 'Discount Amount',
      buyingCost: 'Buying Cost',
    },
    yes: 'Yes',
    no: 'No',
    upload: {
      upload: 'Upload orders',
      uploadDescription:
        'Add or update orders from the store’s marketplace report. Each row is one order line. Nothing is saved until you check it.',
      store: 'Store',
      chooseStore: 'Choose a store',
      storeOption: '{{name}} · {{marketplace}}',
      step1: 'Which store are these orders from?',
      step1Hint:
        'Each marketplace exports orders differently, so orders are uploaded one store at a time.',
      step2: 'Fill in the template',
      step2Template: 'Fill in the {{template}} template',
      step2Hint:
        'One row per order line; lines with the same Order ID make one order. An order already here is replaced whole. Addresses are never saved.',
      templateAfterStore: 'Choose a store to see its marketplace’s template.',
      template: 'Download template',
      step3: 'Upload and check',
      step3Hint: 'Each {{id}} is checked against this store’s listings before anything is saved.',
      step3NoStore: 'Drop the file now; it is checked once a store is chosen.',
      drop: 'Drop the filled-in template here, or click to choose it',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      checking: 'Checking the file…',
      remove: 'Remove',
      emptyFileTitle: 'This file has no orders in it yet',
      emptyFileHint: 'Fill in one row per order line under the header, then drop it here again.',
      counts: {
        orders_one: '{{formatted}} order',
        orders_other: '{{formatted}} orders',
        new_one: '{{formatted}} new line',
        new_other: '{{formatted}} new lines',
        updated_one: '{{formatted}} replacing a line',
        updated_other: '{{formatted}} replacing lines',
        errors_one: '{{formatted}} error',
        errors_other: '{{formatted}} errors',
      },
      row: 'Row',
      result: 'Result',
      actions: { new: 'New', update: 'Replaces order', ready: 'Ready' },
      more: '{{count}} more rows are not listed.',
      cancel: 'Cancel',
      readOnly:
        'Orders are read from a read-only database, so uploads are previewed but not saved.',
      saveIdle: 'Save orders',
      save_one: 'Save {{count}} line',
      save_other: 'Save {{count}} lines',
      saved: 'Saved {{orders}} orders, {{lines}} lines.',
    },
    detail: {
      loading: 'Loading the order…',
      total: 'Total',
      shipping: 'Shipping',
      tax: 'Tax',
      discount: 'Discount',
      lines_one: '{{count}} item',
      lines_other: '{{count}} items',
      noLines: 'The marketplace has not sent this order’s lines yet.',
      quantities: '{{ordered}} ordered · {{shipped}} shipped',
      cancelled: '{{count}} cancelled',
      secondary: 'Marketplace reference',
      method: 'Shipping method',
      updated: 'Last updated',
      marketplaceStatus: 'Marketplace status',
    },
  },
  skus: {
    title: 'SKU Management',
    subtitle: 'Every product you sell, and what it costs at each warehouse.',
    tabs: 'SKU Management sections',
    tab: { skus: 'SKUs', costs: 'SKU Costs' },
    filters: 'SKU filters',
    filter: {
      warehouse: 'Warehouse',
      vendor: 'Vendor',
      store: 'Store',
      brand: 'Brand',
      category: 'Category',
      currency: 'Currency',
      search: 'SKU',
      searchPlaceholder: 'Code or name',
      any: 'Any',
      getDetails: 'Get details',
    },
    rowsPerPage: 'Rows per page',
    cancel: 'Cancel',
    readOnlyShort: 'Read-only database: previewed, not saved',
    columns: {
      skuCode: 'SKU Code',
      skuName: 'SKU Name',
      brand: 'Brand',
      category: 'Category',
      subcategory: 'Sub Category',
      model: 'Model',
      dimensions: 'Dimensions',
      // Edit mode splits Dimensions into its three, each an input.
      length: 'Length (cm)',
      width: 'Width (cm)',
      height: 'Height (cm)',
      weight: 'Weight',
      stores: 'Stores',
      status: 'Status',
    },
    fields: {
      manufacturer: 'Manufacturer',
      countryOfOrigin: 'Country of origin',
      color: 'Color',
      size: 'Size',
      material: 'Material',
      description: 'Description',
      bulletPoints: 'Bullet points',
      keywords: 'Keywords',
    },
    flags: {
      active: 'Active',
      is_eol: 'End of life',
      is_exclusive: 'Exclusive',
      is_fragile: 'Fragile',
      is_hazardous: 'Hazardous',
    },
    detail: {
      noName: 'No name yet',
      stores_one: 'Listed in {{count}} store',
      stores_other: 'Listed in {{count}} stores',
      catalogue: 'Catalogue',
      identifiers: 'Identifiers',
      physical: 'Size and weight',
      content: 'Listing content',
      lookingUp: 'Looking up this SKU…',
      notFound: 'This SKU is not in the catalogue any more.',
      listings: 'Marketplace listings',
      noListings: 'Not listed in any store yet.',
      store: 'Store',
      listingId: 'ASIN or listing SKU',
      price: 'Price',
      costs: 'Current costs',
      noCosts: 'No current cost at any warehouse.',
    },
    empty: {
      skus: 'No SKUs match these filters',
      costs: 'No SKU costs match these filters',
      hint: 'Change the filters and press Get details, or upload a sheet.',
    },
    costs: {
      columns: {
        warehouse: 'Warehouse',
        vendor: 'Vendor',
        currency: 'Currency',
        purchase_cost: 'Purchase Cost',
        shipping_cost: 'Shipping',
        additional_cost: 'Additional',
        vendor_vat_rate: 'VAT Rate',
        vendor_rrp: 'RRP',
        status: 'Status',
        valid_from: 'Valid From',
        valid_to: 'Valid To',
        actions: 'Actions',
      },
      edit: 'Edit the cost of {{sku}}',
      editShort: 'Edit',
      editTitle: 'Edit {{sku}}',
      save: 'Save',
      edited: '{{sku}} updated.',
    },
    upload: {
      skusTitle: 'Upload SKUs',
      skusDescription:
        'Add new SKUs or update existing ones, matched on SKU Code. Nothing is saved until you check the file.',
      dimensionsTitle: 'Upload dimensions',
      dimensionsDescription:
        'Update the size and weight of SKUs you already have. Nothing is saved until you check the file.',
      costsTitle: 'Upload SKU costs',
      costsDescription:
        'Add or update what each SKU costs at a warehouse, from a vendor. Nothing is saved until you check the file.',
      step1: 'Fill in the template',
      step1Hint: 'Keep the header row as it is. Hover a column for what it holds.',
      required: 'Required columns',
      optional: 'Optional columns',
      template: 'Download template',
      step2: 'Upload and check',
      step2Hint: 'Every row is checked against your SKUs, brands, vendors and warehouses.',
      drop: 'Drop the filled-in template here, or click to choose it',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      checking: 'Checking the file…',
      remove: 'Remove',
      emptyTitle: 'This file has no rows in it yet',
      emptyHint: 'Fill in one row per SKU under the header, then drop it here again.',
      counts: {
        new_one: '{{formatted}} new',
        new_other: '{{formatted}} new',
        updated_one: '{{formatted}} update',
        updated_other: '{{formatted}} updates',
        errors_one: '{{formatted}} error',
        errors_other: '{{formatted}} errors',
      },
      row: 'Row',
      result: 'Result',
      actions: { new: 'New', update: 'Update' },
      more: '{{count}} more rows are not listed.',
      saveIdle: 'Save',
      save_one: 'Save {{count}} row',
      save_other: 'Save {{count}} rows',
      saved: 'Saved: {{inserted}} new, {{updated}} updated.',
    },
  },
  exceptions: {
    ticketExceptions: {
      subtitle: 'SKUs held back from a ticket type on purpose, and why.',
      pick: {
        title: 'Choose a store to see its ticket exceptions',
        hint: 'Exceptions are kept per store. Pick one above and press Load data.',
      },
      none: {
        title: 'No ticket exceptions for {{store}}',
        hint: 'Every SKU in this store raises tickets as normal. Upload exceptions to hold a ticket type back for chosen SKUs.',
      },
    },
    mailExceptions: {
      subtitle: 'Ad groups kept off the missing ad groups mail on purpose, and why.',
      pick: {
        title: 'Choose a store to see its mail exceptions',
        hint: 'Exceptions are kept per store. Pick one above and press Load data.',
      },
      none: {
        title: 'No mail exceptions for {{store}}',
        hint: 'Every missing ad group in this store is reported in the mail. Upload exceptions to leave chosen ad groups out.',
      },
    },
  },
  marketplaceData: {
    title: 'Marketplace API Data',
    subtitle:
      'See what Amazon shows right now for a list of ASINs: listing details, offers or fees.',
    query: 'Marketplace lookup',
    store: 'Store',
    chooseStore: 'Choose an Amazon store',
    storeOption: '{{name}} · {{marketplace}} · {{kind}}',
    storeKind: { seller: 'Seller', vendor: 'Vendor' },
    option: 'Look up',
    options: { listing: 'Listing', pricing: 'Pricing', fee_estimate: 'Fee estimate' },
    optionHints: {
      listing: 'Catalog details: title, brand, bullets, images, dimensions and ranks.',
      pricing:
        'Offer count, our current price and the top three offers on each ASIN. Seller accounts only.',
      fee_estimate: "Amazon's fees for each ASIN at the price you give. Seller accounts only.",
    },
    vendorNote:
      'This is a vendor account. Amazon gives pricing and fee estimates to seller accounts only, so Listing is the one option here.',
    asins: 'ASINs',
    asinsPlaceholder: 'B0XXXXXXXX, B0YYYYYYYY — separated by commas, spaces or new lines',
    count_one: '{{entered}} of {{limit}} ASIN',
    count_other: '{{entered}} of {{limit}} ASINs',
    notAsins: 'not ASINs, will be skipped: {{list}}',
    uniqueImages: 'Largest image of each slot only',
    feeItems: 'ASINs and selling prices',
    feePlaceholder: 'One per line: the ASIN, then the selling price — paste straight from Excel',
    feeTemplate: 'Download template',
    feeFile: 'Load from CSV or Excel',
    feeFileError: 'That file could not be read. Save it as .xlsx or .csv and try again.',
    feeProblems: 'check line {{lines}}: an ASIN and a price above zero',
    currency: 'Currency',
    fba: 'Fulfilled by Amazon',
    getDetails: 'Get details',
    asking: 'Asking Amazon…',
    askingHint:
      'Large pricing lookups take up to two minutes: Amazon answers one batch every ten seconds.',
    empty: {
      title: 'Nothing looked up yet',
      hint: 'Choose a store and what to look up, enter the ASINs, then press Get details.',
      pickTitle: 'Pick an Amazon store to start',
      pickHint: 'Each store answers for its own marketplace, so a lookup runs one store at a time.',
    },
    resultTitle: '{{option}} · {{store}} ({{marketplace}})',
    rows_one: '{{formatted}} row',
    rows_other: '{{formatted}} rows',
    noData_one: '{{count}} with no data',
    noData_other: '{{count}} with no data',
    errorCount_one: '{{count}} refused',
    errorCount_other: '{{count}} refused',
    download: 'Download CSV',
    refusedTitle: 'Amazon refused part of this lookup',
    showAll_one: 'Show all',
    showAll_other: 'Show all {{count}}',
    showLess: 'Show less',
    missing: 'Amazon returned nothing for: {{list}}',
    skipped: 'Skipped, not ASINs: {{list}}',
    noRows: {
      title: 'No rows came back',
      hint: 'Amazon had nothing for these ASINs in this marketplace. Check the ASINs and the store.',
    },
  },
  fees: {
    title: 'Fees',
    subtitle: 'What each marketplace charges per listing, store by store.',
    query: 'Fee filters',
    store: 'Store',
    storeOption: '{{name}} · {{marketplace}}',
    rowsPerPage: 'Rows per page',
    chooseStore: 'Choose a store',
    searchStores: 'Search stores…',
    noMatches: 'No matches',
    loading: 'Loading…',
    load: 'Load data',
    template: 'Download template',
    templateFor: 'The {{name}} fee template',
    upload: 'Upload fees',
    export: 'Export',
    pickTitle: 'Pick a store to see its fees',
    pickHint: 'Each marketplace charges differently, so fees are loaded one store at a time.',
    empty: {
      title: 'No fees for {{store}} yet',
      hint: 'Download its template, fill in one row per product, and upload it.',
    },
    columns: {
      product: 'Product',
      sellingPrice: 'Selling Price',
      fixedFee: 'Fixed Fee',
      variableFee: 'Variable Fee (%)',
      returnFee: 'Return Fee (%)',
      shippingFee: 'Shipping Fee',
      acos: 'ACoS (%)',
      totalFee: 'Total Fee charged',
      estimateDate: 'Estimate Date',
    },
    pageOf: 'Page {{page}} of {{pages}} · {{total}} fees',
    previous: 'Previous page',
    next: 'Next page',
    uploadDescription:
      'Add or update many fees at once from the store’s marketplace template. Nothing is saved until you check it.',
    step1: 'Which store are these fees for?',
    step1Hint: 'Each marketplace has its own template, so fees are uploaded one store at a time.',
    step2: 'Fill in the template',
    step2Template: 'Fill in the {{template}} template',
    step2Hint:
      'One row per product. A product that already has a fee gets the new one; the old one is kept as history.',
    templateAfterStore: 'Choose a store to see its marketplace’s template.',
    step3: 'Upload and check',
    step3Hint: 'Each {{id}} is checked against this store’s listings before anything is saved.',
    step3NoStore: 'Drop the file now; it is checked once a store is chosen.',
    drop: 'Drop the filled-in template here, or click to choose it',
    formats: 'Excel (.xlsx, .xlsm) or CSV',
    checking: 'Checking the file…',
    remove: 'Remove',
    emptyFileTitle: 'This file has no fees in it yet',
    emptyFileHint: 'Under the header, add one row per product, then drop it here again.',
    counts: {
      new_one: '{{formatted}} new',
      new_other: '{{formatted}} new',
      updated_one: '{{formatted}} update',
      updated_other: '{{formatted}} updates',
      errors_one: '{{formatted}} error',
      errors_other: '{{formatted}} errors',
    },
    row: 'Row',
    result: 'Result',
    actions: { new: 'New fee', update: 'Replaces current fee', ready: 'Ready' },
    more: '{{count}} more rows are not listed.',
    cancel: 'Cancel',
    readOnly: 'Fees are read from a read-only database, so uploads are previewed but not saved.',
    saveIdle: 'Save fees',
    save_one: 'Save {{count}} fee',
    save_other: 'Save {{count}} fees',
    saved: 'Saved: {{inserted}} new, {{updated}} updated.',
  },
  costs: {
    title: 'Costs',
    subtitle: 'What each SKU costs from a vendor, or sells at to a retailer, per warehouse.',
    kind: 'Cost type',
    filters: 'Cost filters',
    warehouse: 'Warehouse',
    brand: 'Brand',
    currency: 'Currency',
    skuCode: 'SKU code',
    skuPlaceholder: 'e.g. 01-PZ8I',
    any: 'Any',
    choose: 'Choose',
    getDetails: 'Get details',
    cancel: 'Cancel',
    rowsPerPage: 'Rows per page',
    vendor: {
      tab: 'Vendor costs',
      upload: 'Upload vendor costs',
      party: 'Vendor',
      partyColumn: 'Vendor Name',
      choose:
        'The warehouse the stock goes to, the vendor you buy from, and the currency you pay in.',
      explainer: 'What you pay each vendor for a SKU, per warehouse.',
      count_one: '{{formatted}} vendor cost',
      count_other: '{{formatted}} vendor costs',
      emptyHint:
        'No vendor costs match these filters. Change them, or upload costs for this vendor.',
    },
    retailer: {
      tab: 'Retailer costs',
      upload: 'Upload retailer costs',
      party: 'Retailer',
      partyColumn: 'Retailer Name',
      choose:
        'The warehouse you ship from, the retailer you sell to, and the currency they pay in.',
      explainer: 'What each retailer pays you for a SKU, per warehouse.',
      count_one: '{{formatted}} retailer cost',
      count_other: '{{formatted}} retailer costs',
      emptyHint:
        'No retailer costs match these filters. Change them, or upload costs for this retailer.',
    },
    columns: {
      skuCode: 'SKU Code',
      skuName: 'SKU Name',
      brand: 'Brand',
      warehouse: 'Warehouse',
      currency: 'Currency',
      skuCost: 'SKU Cost',
      retailerCost: 'Retailer Cost',
      vendorVat: 'Vendor VAT Rate',
      vatRate: 'VAT Rate',
      vendorRrp: 'Vendor RRP',
      rrp: 'RRP',
      actions: 'Actions',
    },
    empty: { title: 'No costs here yet' },
    template: {
      skuCode: 'SKU Code',
      skuCost: 'SKU Cost',
      vatRate: 'VAT Rate',
      rrp: 'RRP',
      optional: ' · optional',
    },
    upload: {
      description:
        'Add or update many SKU costs at once from a spreadsheet. Nothing is saved until you check it.',
      step1: 'Where do these costs apply?',
      step2: 'Fill in the template',
      step2Hint:
        'One row per SKU. A SKU that already has a cost here gets the new one; the old one is kept as history.',
      template: 'Download template',
      step3: 'Upload and check',
      step3Hint: 'Each row is checked against your SKUs before anything is saved.',
      drop: 'Drop the filled-in template here, or click to choose it',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      chooseFirst: 'Choose a warehouse, a party and a currency first',
      warehouseCleared: 'Warehouse cleared: this vendor has no terms with it.',
      vendorWarehousesError:
        'Could not read which warehouses this vendor works with, so every warehouse is offered.',
      vendorNoWarehouses:
        'This vendor has no warehouses set up yet. Add one on the vendor before uploading its costs.',
      checking: 'Checking the file…',
      expected: 'The first row must be the template header: {{columns}}.',
      emptyTitle: 'This file has no costs in it yet',
      emptyHint:
        'Under the header, add one row per SKU: its SKU Code and SKU Cost, and VAT Rate and RRP if you have them. Then drop it here again.',
      remove: 'Remove',
      readyHint:
        'Choose a warehouse, a party and a currency in step 1 to see which rows are new and which replace a current cost.',
      counts: {
        ready_one: '{{formatted}} row ready',
        ready_other: '{{formatted}} rows ready',
        new_one: '{{formatted}} new',
        new_other: '{{formatted}} new',
        updated_one: '{{formatted}} update',
        updated_other: '{{formatted}} updates',
        errors_one: '{{formatted}} error',
        errors_other: '{{formatted}} errors',
      },
      row: 'Row',
      result: 'Result',
      actions: { new: 'New cost', update: 'Replaces current cost', ready: 'Ready' },
      more: '{{count}} more rows are not listed.',
      saveIdle: 'Save costs',
      save_one: 'Save {{count}} cost',
      save_other: 'Save {{count}} costs',
      saved: 'Saved: {{inserted}} new, {{updated}} updated.',
    },
    edit: {
      open: 'Edit the cost of {{sku}}',
      title: 'Edit',
      heading: 'Edit {{sku}}',
      save: 'Save',
      done: '{{sku}} updated.',
    },
    remove: {
      open: 'Remove the cost of {{sku}}',
      title: 'Remove',
      confirmTitle: 'Remove the cost of {{sku}}?',
      confirmBody: 'It stops being the current cost. The row is kept as history.',
      confirm: 'Remove',
      done: 'Cost of {{sku}} removed.',
    },
  },
  business: {
    total: 'Total',
    skuDocuments: {
      close: 'Close SKU details',
      openSku: 'Open the orders of {{sku}}',
      loading: 'Loading the documents behind this SKU',
      heading: '{{place}} · {{from}} – {{to}} · {{currency}}',
      adSpend:
        '{{amount}} of advertising spend is part of this SKU’s profit but belongs to no single order, so it is not in the totals below.',
      tabs: { label: 'SKU documents', orders: 'Orders', invoices: 'Invoice lines' },
      orders: {
        label: 'Orders for this SKU',
        order: 'Order',
        date: 'Date',
        status: 'Status',
        units: 'Units',
        sales: 'Sales',
        cost: 'Cost',
        commission: 'Commission',
        received: 'Received',
        toReceive: 'To receive',
        returned: 'Returned',
        settlement: 'Settlement',
        awaitingPayout: 'Awaiting payout',
        footer_one: 'The {{count}} order line in range',
        footer_other: 'All {{count}} order lines in range',
        emptyTitle: 'No orders in this range',
        emptyBody:
          'Nothing was ordered for this SKU at this store in the selected period and currency.',
      },
      invoices: {
        label: 'Invoice lines for this SKU',
        invoice: 'Invoice',
        date: 'Date',
        channel: 'Channel',
        retailer: 'Retailer',
        units: 'Units',
        lineAmount: 'Line amount',
        cost: 'Cost',
        invoiceTotal: 'Invoice total',
        received: 'Received',
        outstanding: 'Outstanding',
        footer_one: 'The {{count}} line in range',
        footer_other: 'All {{count}} lines in range',
        note: 'Invoice total, Received and Outstanding are for the whole invoice, which may cover other SKUs, so they are not totalled.',
        emptyTitle: 'No invoice lines in this range',
        emptyBody:
          'This SKU was not billed through the DF, Offline or Invoice channels at this store in the selected period and currency.',
      },
    },
    partnerView: {
      title: 'Partner view',
      subtitle: 'Partner-scoped sales, costs and profit, from marketplaces down to each SKU.',
      filters: 'Partner view filters',
      generate: {
        label: 'Generate report',
        working: 'Generating…',
      },
      prompt: {
        title: 'Generate the partner view',
        hint: 'Pick a warehouse, a marketplace, stores or brands and a date range, then Generate report.',
      },
      summary: {
        title: 'Performance summary',
        titleOf: 'Performance summary · {{name}}',
        channels: 'Channel sales',
        performance: 'Performance',
      },
      stats: {
        seller: 'Seller',
        df: 'DF',
        offline: 'Offline',
        invoice: 'Invoice sales',
        total: 'Total sales',
        units: '{{units}} units',
        profit: 'Net profit / loss',
        profitHint: '{{margin}} of total sales',
        ofSales: 'Of total sales',
        received: 'Payment received',
        receivedHint: '{{amount}} to receive',
        cost: 'Cost of goods',
        costHint: '{{amount}} commission',
        returns: 'Returns',
        returnsHint: 'Units returned',
        ams: 'AMS performance',
        amsHint: '{{spend}} spend',
        otherCurrencies: 'Also {{amounts}}',
      },
      levels: {
        marketplace: 'Marketplace performance',
        store: 'Stores · {{name}}',
        sku: 'SKUs · {{name}}',
      },
      card: {
        marketplace_one: '{{count}} marketplace',
        marketplace_other: '{{count}} marketplaces',
        store_one: '{{count}} store',
        store_other: '{{count}} stores',
        sku_one: '{{count}} SKU',
        sku_other: '{{count}} SKUs',
      },
      trail: {
        label: 'Where you are',
        all: 'All marketplaces',
        back: 'Back',
      },
      tree: {
        open: 'Open {{name}}',
        allSkus_one: 'View its {{count}} SKU',
        allSkus_other: 'View all {{count}} SKUs',
        hint: 'Open a marketplace for its stores, and a store for its best-selling SKUs.',
      },
      search: {
        placeholder: 'Search SKU, name, brand or store…',
      },
      export: {
        marketplaces: 'Marketplaces on screen',
        stores: 'All stores',
        skus: 'All SKUs (every store)',
      },
      columns: {
        skuName: 'SKU Name',
        replen: 'Replen',
        marketplace: 'Marketplace',
        store: 'Store',
        sku: 'SKU',
        seller: 'Seller',
        df: 'DF',
        offline: 'Offline',
        invoice: 'Invoice',
        total: 'Total',
        units: 'Units',
        commission: 'Commission',
        cogs: 'Cost of Goods',
        returns: 'Returns',
        received: 'Payment Received',
        toReceive: 'To Receive',
        profit: 'Profit/Loss',
        amsSales: 'AMS Sales',
        amsSpend: 'AMS Spend',
        currency: 'Currency',
        brand: 'Brand',
      },
      hints: {
        offline: 'Invoiced to offline retailers.',
        invoice: 'Invoiced to the marketplace as its vendor.',
        commission:
          'What the marketplace kept: commission, fees and promotions, less what it credited.',
        profit:
          'Profit of the lines whose cost is known, less the AMS spend; under it, that over total sales.',
        toReceive: 'What the marketplace is still expected to pay.',
      },
      unattributed: 'Unattributed',
      unattributedHint: 'Invoices whose retailer sells through no store linked to the warehouse.',
      noSku: 'Ad spend on no SKU',
      footnote:
        'Rows sum to the level above, per currency. Amounts in different currencies are never added together.',
      empty: {
        title: 'Nothing sold',
        hint: 'Nothing was sold in this scope between these dates. Widen the dates or the filters.',
        searchTitle: 'No SKUs match',
        searchHint: 'Nothing matches the search. Clear it, or widen the dates.',
      },
    },
    reviewData: {
      title: 'Review data',
      subtitle:
        'SKU-level inventory, sales, purchase-order and marketing performance compared across two periods.',
      filters: 'Review data filters',
      periods: {
        previous: 'Previous period',
        current: 'Current period',
        presets: 'Quick periods',
        '7d': '7d',
        '30d': '30d',
        month: 'Month',
        presetHint_7d: 'The last 7 days against the 7 before',
        presetHint_30d: 'The last 30 days against the 30 before',
        presetHint_month: 'Last month against the month before',
      },
      focusedOnly: 'Focused only',
      hideInactive: 'Hide inactive',
      hideInactiveHint: 'Leave out SKUs that neither sold nor hold stock',
      generate: {
        label: 'Generate',
        working: 'Generating…',
        needsWarehouse: 'Pick a warehouse first',
      },
      prompt: {
        title: 'Generate the review',
        hint: 'Pick a warehouse and both periods, then Generate. The current period is compared with the previous.',
      },
      summary: {
        title: 'Summary',
        byChannel: 'Sales by channel',
        noSales: 'Nothing was sold in either period.',
        unitsSold: 'Units sold',
        byChannelHint: 'Previous against current, in the currency most was sold in',
        previousPeriod: 'Previous',
        currentPeriod: 'Current',
        channelSeller: 'Seller',
        channelDf: 'DF',
        channelOf: 'Offline',
        channelInvoice: 'Invoice',
        skusUnit: 'SKUs',
        reviewed: 'were reviewed across both periods',
        shareOfSales: 'Current sales by channel',
        changes: 'Period over period',
        stockAndMoney: 'Stock and money',
        p1: 'P1 warehouses',
        nonP1: 'Non-P1 warehouses',
        fba: 'FBA',
        received: 'Received',
        toReceive: 'To receive',
        returns: 'Units returned',
        skus: 'Total SKUs',
        inventory: 'Total inventory',
        inventoryHint: 'P1 {{p1}} · Non-P1 {{other}} · FBA {{fba}}',
        salesAmount: 'Sales amount',
        salesUnits: 'Sales units',
        dfSales: 'DF sales',
        dfUnits: 'DF units',
        ofSales: 'OF sales',
        invoiceSales: 'Invoice sales',
        totalSales: 'Total sales',
        purchaseOrders: 'Purchase orders',
        payments: 'Payments',
        paymentsHint: '{{received}} received · {{toReceive}} to receive',
        loss: 'Loss & returns',
        lossHint: '{{returns}} units returned',
        marketingSpend: 'Marketing spend',
        marketingSales: 'Marketing sales',
        previous: 'Previous {{value}}',
        unitsPrevious: '{{units}} units · previous {{value}}',
        currency: 'Currency',
        stores: 'Stores',
        fillRate: 'Average fill rate',
      },
      card_one: '{{count}} SKU',
      card_other: '{{count}} SKUs',
      search: {
        placeholder: 'Search by SKU code…',
      },
      groups: {
        sku: 'SKU Information',
        quality: 'Offer Quality',
        inventory: 'Inventory',
        po: 'Purchase Orders',
        sales: 'Sales',
        df: 'DF Sales',
        of: 'OF Sales',
        vo: 'Invoice Sales',
        total: 'Total (Seller + DF + OF + Invoice)',
        payments: 'Payments',
        ranking: 'Amazon Ranking',
        marketing: 'Marketing',
        secondary: 'Secondary Data (Weekly)',
      },
      columns: {
        sku_code: 'SKU Code',
        sku_name: 'SKU Name',
        brand_name: 'Brand',
        category: 'Category',
        subcategory: 'Subcategory',
        is_focus: 'Focus',
        has_a_plus: 'Has A+',
        idq_score: 'IDQ Score',
        lbb: 'LBB',
        is_prime: 'Is Prime',
        is_stranded: 'Stranded',
        p1_warehouse_units: 'P1 WH Stock',
        other_warehouse_units: 'Non-P1 WH Stock',
        fba_units: 'FBA Stock',
        stock_cover_days: 'Stock Cover (Days)',
        overage_90: 'Overage (90+ days)',
        overage_180: 'Overage (180+ days)',
        overage_fee: 'Overage Fee',
        po_previous: 'Previous',
        fill_rate_previous: 'Fill Rate (Prev)',
        po_current: 'Current',
        fill_rate_current: 'Fill Rate (Curr)',
        units_previous: 'Units (Prev)',
        units_current: 'Units (Curr)',
        amount_previous: 'Amount (Prev)',
        amount_current: 'Amount (Curr)',
        df_units_previous: 'DF Units (Prev)',
        df_units_current: 'DF Units (Curr)',
        df_amount_previous: 'DF Amount (Prev)',
        df_amount_current: 'DF Amount (Curr)',
        of_units_previous: 'OF Units (Prev)',
        of_units_current: 'OF Units (Curr)',
        of_amount_previous: 'OF Amount (Prev)',
        of_amount_current: 'OF Amount (Curr)',
        vo_units_previous: 'Invoice Units (Prev)',
        vo_units_current: 'Invoice Units (Curr)',
        vo_amount_previous: 'Invoice Amount (Prev)',
        vo_amount_current: 'Invoice Amount (Curr)',
        total_units_previous: 'Total Units (Prev)',
        total_units_current: 'Total Units (Curr)',
        total_amount_previous: 'Total Amount (Prev)',
        total_amount_current: 'Total Amount (Curr)',
        received: 'Received',
        to_receive: 'To Receive',
        loss: 'Loss',
        returns: 'Returns',
        rank_previous: 'Previous',
        rank_current: 'Current',
        clicks_previous: 'Clicks (Prev)',
        clicks_current: 'Clicks (Curr)',
        ad_units_previous: 'Units (Prev)',
        ad_units_current: 'Units (Curr)',
        ad_sales_previous: 'Sales (Prev)',
        ad_sales_current: 'Sales (Curr)',
        ad_spend_previous: 'Spend (Prev)',
        ad_spend_current: 'Spend (Curr)',
        acos_previous: 'ACOS (Prev)',
        acos_current: 'ACOS (Curr)',
        ordered_units_previous: 'Ordered Units (Prev)',
        ordered_units_current: 'Ordered Units (Curr)',
        ordered_revenue_previous: 'Ordered Rev (Prev)',
        ordered_revenue_current: 'Ordered Rev (Curr)',
        customer_returns_previous: 'Cust. Returns (Prev)',
        customer_returns_current: 'Cust. Returns (Curr)',
        glance_views_previous: 'Glance Views (Prev)',
        glance_views_current: 'Glance Views (Curr)',
        buy_box_previous: 'Buy Box % (Prev)',
        buy_box_current: 'Buy Box % (Curr)',
        conversion_previous: 'Conversion % (Prev)',
        conversion_current: 'Conversion % (Curr)',
      },
      hints: {
        is_focus: 'A focused SKU at one of the stores in scope.',
        idq_score: 'Amazon’s item data quality score at the last catalogue check.',
        lbb: 'How often our offer held the Buy Box in the current period.',
        p1_warehouse_units: 'Our stock now in warehouses that are a store’s first choice.',
        other_warehouse_units: 'Our stock now in the other linked warehouses.',
        stock_cover_days: 'Warehouse and FBA stock over the current period’s daily sales.',
        overage_fee: 'Amazon’s estimated aged-inventory surcharge now.',
        fill_rate: 'Amazon’s receive fill rate over the period’s weeks.',
        to_receive: 'What the marketplace still owes for the current period’s sales.',
        loss: 'Received and expected, less the cost of goods, where below zero.',
        rank: 'The best best-sellers rank the SKU’s listings showed in the period.',
        change: '{{change}} on the previous period',
      },
      yes: 'Yes',
      no: 'No',
      empty: {
        title: 'No SKUs',
        hint: 'Nothing sold or was held in this scope in either period.',
        searchTitle: 'No SKUs match',
        searchHint: 'Nothing matches the search. Clear it, or widen the scope.',
      },
    },
    sellerOrders: {
      title: 'Live seller orders',
      subtitle:
        'Lifecycle, fulfilment and shipping of every order across your stores, as it comes in.',
      live: 'Live · updated {{time}}',
      liveNow: 'Live',
      filters: 'Live seller orders filters',
      analytics: 'Order analytics',
      tabs: 'Order status',
      search: 'Order ID, ASIN or SKU…',
      groups: {
        dates: 'Dates',
        scope: 'Scope',
        attributes: 'Order attributes',
        search: 'Search',
        shipping: 'Shipping',
      },
      orderTypes: {
        standardorder: 'Standard order',
        standard: 'Standard',
        order: 'Order',
        rechnung: 'Invoice (Rechnung)',
        sale: 'Sale',
        return: 'Return',
      },
      filter: {
        range: 'Date range',
        orderType: 'Order type',
        anyOrderType: 'Any order type',
        orderRef: 'Order ID',
        orderRefPlaceholder: 'Search order ID',
        product: 'ASIN / SKU',
        productPlaceholder: 'ASIN or SKU',
        shipCountry: 'Ship country',
        anyCountry: 'Any country',
        purchaseRange: 'Purchased between',
        updatedRange: 'Updated between',
        datedBy: 'Filter dates by',
        purchase: 'Purchase date',
        updated: 'Last update',
        fulfilment: 'Fulfilment',
        anyFulfilment: 'Any fulfilment',
        fba: 'FBA — marketplace ships',
        fbm: 'FBM — we ship',
      },
      level: {
        label: 'Count by',
        orders: 'Orders',
        units: 'Units',
      },
      status: {
        all: 'All',
        pending: 'Pending',
        shipped: 'Shipped',
        delivered: 'Delivered',
        cancelled: 'Cancelled',
        returned: 'Returned',
        other: 'Other',
      },
      kpi: {
        orders: 'Orders',
        ordersNote: '{{value}} units',
        units: 'Units',
        unitsNote: '{{value}} orders',
        revenue: 'Gross revenue',
        perOrder: '{{value}} per order',
        perUnit: '{{value}} per unit',
        moreCurrencies_one: '+{{count}} currency',
        moreCurrencies_other: '+{{count}} currencies',
        cancelled: 'Cancellations',
        cancelledNote: '{{value}} of orders',
        shippedPending: 'Shipped / pending',
        shippedPendingNote: '{{share}} of orders shipped or delivered',
        trend: 'Each day of the range',
      },
      daily: {
        ordersTitle: 'Daily order volume',
        unitsTitle: 'Daily units volume',
        subtitle: 'Each day of the range, stacked by where the orders stand now',
        total: 'Total',
      },
      mix: {
        title: 'Where orders stand',
        subtitle: 'Each status’s share of the orders',
      },
      map: {
        title: 'Shipping destinations',
        subtitle: 'Orders by the country they ship to. Pick one to filter the page.',
        clear: 'Clear {{country}}',
        country: 'Country',
        ordersColumn: 'Orders',
        share: 'Share',
        cities: 'Top cities',
        unitsColumn: 'Units',
        allCountries_one: '{{count}} country',
        allCountries_other: 'All {{count}} countries',
        allCities_one: '{{count}} city',
        allCities_other: 'Top {{count}} cities',
        noCities: 'The addresses name no city.',
        city: 'City',
        orders_one: '{{formatted}} order',
        orders_other: '{{formatted}} orders',
      },
      products: {
        title: 'Top products',
        eighty_one: '{{count}} product makes 80% of the units sold',
        eighty_other: '{{count}} products make 80% of the units sold',
        under_one: 'The top {{count}} product makes {{share}} of the units sold',
        under_other: 'The top {{count}} products make {{share}} of the units sold',
        running: 'With every product above it: {{share}} of the units sold',
        detailEighty: 'products make 80% of the {{total}} units sold',
        detailUnder: 'products make {{share}} of the {{total}} units sold',
        product: 'Product',
        unitsColumn: 'Units',
        ordersColumn: 'Orders',
        share: 'Share',
        runningColumn: 'Running share',
      },
      categories: {
        title: 'Top categories',
        subtitle: 'Each category’s share of the units sold',
        units: '{{value}} units',
      },
      time: {
        title: 'Time to resolve',
        subtitle:
          'From the purchase to the marketplace’s last update: the median, the average under it',
        purchased: 'Purchased',
        under: 'avg {{average}} · {{orders}} orders',
        to_cancelled: 'Purchase to cancellation',
        to_returned: 'Purchase to return',
        none: 'None reached yet',
        orders_one: '{{formatted}} order',
        orders_other: '{{formatted}} orders',
        minutes_one: '{{count}} min',
        minutes_other: '{{count}} min',
        hours: '{{value}} hrs',
        days: '{{value}} days',
      },
      cancel: {
        title: 'Cancellations by category',
        subtitle_one: '{{formatted}} cancelled order · {{rate}} of orders',
        subtitle_other: '{{formatted}} cancelled orders · {{rate}} of orders',
        none: 'No cancellations',
        noneHint_one: 'Its {{formatted}} order went through.',
        noneHint_other: 'All {{formatted}} orders in this range went through.',
      },
      columns: {
        order_id: 'Order ID',
        purchase_date: 'Purchase date',
        last_update_date: 'Last updated',
        status: 'Status',
        amount_total: 'Amount',
        noAmount: 'The marketplace has not given the order’s total yet',
        fulfilment: 'Fulfilment',
        store_name: 'Store',
        marketplace_name: 'Marketplace',
        lines: 'Items',
        units: 'Units',
        ship_to: 'Ship to',
      },
      panel: {
        timeline: 'Order timeline',
        purchased: 'Purchased',
        unknown: 'Unknown',
        minutes_one: '{{count}} min',
        minutes_other: '{{count}} mins',
        hours_one: '{{count}} hr',
        hours_other: '{{count}} hrs',
        days_one: '{{count}} day',
        days_other: '{{count}} days',
        shipTo: 'Ship to',
        fulfilment: 'Ship / deliver',
        shippedOf: '{{shipped}} of {{ordered}} units shipped',
        subtotal: 'Subtotal (incl. ship + tax + disc.)',
        subtotalNote: 'Ship {{shipping}} · Tax {{tax}} · Disc {{discount}}',
        orderType: 'Order',
        items_one: 'Order item',
        items_other: 'Order items · {{count}}',
        noItems: 'The marketplace sent no items for this order.',
        sku: 'SKU {{sku}}',
        quantities: 'Qty {{ordered}} ordered · {{shipped}} shipped',
        cancelledUnits_one: '{{count}} cancelled',
        cancelledUnits_other: '{{count}} cancelled',
        tax: 'Tax {{value}}',
      },
      empty: {
        title: 'No orders',
        hint: 'No order matches these filters. Widen the dates, or clear a filter or the search.',
        noOrders: 'No orders in this range.',
      },
    },
    reconciliation: {
      title: 'Payment reconciliation',
      subtitle: 'Match marketplace payouts against orders and identify variances.',
      filters: 'Payment reconciliation filters',
      generate: {
        label: 'Generate report',
        working: 'Generating…',
      },
      prompt: {
        title: 'Generate the reconciliation',
        hint: 'Pick a warehouse, a marketplace, stores or brands and a date range, then Generate report.',
      },
      stats: {
        orders: 'Total orders',
        ordersHint: '{{orders}} orders · {{partial}} partially matched',
        matched: 'Matched orders',
        matchedHint: 'Paid the line price in full',
        unmatched: 'Unmatched orders',
        unmatchedHint: 'Nothing settled yet',
        returned: 'Returned orders',
        returnedHint: 'Returned or refunded',
        discrepancy: 'Total discrepancy',
        discrepancyHint: 'Expected less received',
        showOnly: 'Show only {{status}} lines',
        showAll: 'Show every line',
      },
      status: {
        matched: 'Matched',
        unmatched: 'Unmatched',
        partially_matched: 'Partially matched',
        returned: 'Returned',
      },
      statusChip: 'Status: {{status}}',
      search: {
        placeholder: 'Search Order ID or SKU',
      },
      card_one: '{{count}} order line',
      card_other: '{{count}} order lines',
      groups: {
        order: 'Order',
        settlement: 'What was paid',
        reconciliation: 'Reconciliation',
        profit: 'Profit',
      },
      columns: {
        order_date: 'Order Date',
        platform_order_id: 'Order ID',
        sku_code: 'SKU',
        sku_name: 'SKU Name',
        reconciliation_status: 'Reconciliation Status',
        product_ref: 'ASIN',
        store_name: 'Store',
        original_item_price: 'Original Price',
        original_tax: 'Tax',
        original_quantity: 'Original Qty',
        returned_quantity: 'Returned Qty',
        settlement_id: 'Settlement ID',
        settlement_date: 'Settlement Date',
        received_amount: 'Received Amount',
        received_tax: 'Received Tax',
        commission: 'Commission',
        fee: 'Fee',
        expected_net_amount: 'Expected Net',
        actual_net_received: 'Actual Net',
        discrepancy: 'Discrepancy',
        buying_cost: 'Buying Cost',
        margin: 'Margin',
        margin_pct: 'Margin %',
        currency: 'Currency',
        replen: 'Replen',
      },
      hints: {
        original_item_price: 'The line price the order was placed at.',
        received_amount: 'Principal the settlements paid for the sale.',
        commission: 'The marketplace’s commission, signed as taken.',
        fee: 'Its other fees on the sale, signed as taken.',
        expected_net_amount: 'The line price for the units kept.',
        actual_net_received: 'Every settlement line’s net for the order line, together.',
        discrepancy: 'Expected less the principal received: still owed, or overpaid when negative.',
        buying_cost: 'Units times landed cost.',
        margin:
          'What the line earned less its buying cost: the net paid once settled, the expected net until then.',
        margin_pct: 'The margin over the line price.',
      },
      replen: {
        R: 'Replen SKU',
        N: 'Non-replen SKU',
        T: 'Trial SKU',
      },
      empty: {
        noneTitle: 'No order lines',
        noneHint: 'No orders were placed in this scope between these dates.',
        filteredTitle: 'No order lines match',
        filteredHint: 'Nothing matches the search or status. Clear one, or widen the dates.',
      },
    },
    insights: {
      title: 'Insights',
      show: 'Show charts',
      hide: 'Hide charts',
      currency: 'Chart currency',
      currencyHint:
        'Which currency the charts draw. Amounts are never converted or added across currencies.',
    },
    help: {
      open: 'What this page shows',
      businessView: {
        title: 'What Business View includes',
        lines: [
          'Every SKU that sold in the date range, across the warehouses and marketplaces picked, and every SKU that holds stock now.',
          'The Data Source column says which: Sales when it sold, Stock when it only holds stock. Sales wins when both apply.',
          'Amounts stay in their own currency on every row and in every total. Nothing is converted or added across currencies.',
          'Listing view splits a SKU into its listings (ASIN and seller SKU). The SKU’s own figures — stock, DF, OF, invoices — repeat on each, shown quieter.',
          'Amazon’s own columns (FBA, traffic, vendor) appear only when the result holds any of their figures.',
        ],
      },
      lossMaking: {
        title: 'How profit is worked out',
        lines: [
          'One row per SKU at each store. DF, OF and Invoice sales count at the store their retailer sells through.',
          'Profit / Loss is VentaHub’s: what the marketplace paid or is still to pay for the sales, plus DF, OF and Invoice sales (without VAT), less the cost of goods and the AMS spend. A line with no cost of goods counts it as nothing.',
          'Margin % is the profit over the cost of goods: the markup a store’s expected margin is set in.',
          'Loss making SKUs lost money, or made a margin five points or more under their store’s expected one.',
          'Stock is what the SKU holds now in the store’s warehouses and at the marketplace; cover is how many days of the period’s sales it lasts.',
        ],
      },
      partnerView: {
        title: 'How Partner View drills',
        lines: [
          'Open a marketplace for its stores, and a store for its SKUs. The trail above the board takes you back up.',
          'DF, Offline and Invoice sales count at the store their retailer sells through; invoices with no such store sit under Unattributed.',
          'Profit/Loss is VentaHub’s: what the marketplace paid or is still to pay, plus DF, OF and Invoice sales (without VAT), less the cost of goods and the AMS spend; the percentage under it is that over total sales.',
          'Every level is one row per currency: amounts in different currencies are never added together.',
        ],
      },
      reconciliation: {
        title: 'How a line is reconciled',
        lines: [
          'One row per marketplace order line placed in the range, against the settlement lines linked to it.',
          'Unmatched: nothing settled yet. Returned: returned, or refunded. Matched: the principal paid equals the line price, to the cent. Partially matched: paid, but not the line price.',
          'Discrepancy is the line price for the units kept, less the principal received.',
          'Margin is what the line earned less its buying cost — the net paid once settled, the expected net until then — and Margin % is that over the line price.',
          'R marks a replenished SKU at the store’s warehouses.',
        ],
      },
      sellerOrders: {
        title: 'What Live Seller Orders shows',
        lines: [
          'Every marketplace order in the range, read again every minute. Dates are the purchase, or the marketplace’s last update.',
          'The analytics count every status; the tabs narrow the table only. Revenue is each order’s total, per currency, never added across them.',
          'Top products and categories are VentaHub’s 80% of sales: the few that make most of the units sold.',
          'Time to resolve is from the purchase to the marketplace’s last update — the closest measure kept, as no status history is.',
          'Ship-to is the address’s country where the marketplace gives one, else the store’s marketplace country. Never the buyer’s name or street.',
        ],
      },
      reviewData: {
        title: 'How the periods compare',
        lines: [
          'One row per SKU that sold or held stock in either period, with each period’s figures side by side.',
          'Each figure is read as the Business View reads it, over that period, with the same filters.',
          'Focus, A+, IDQ, Prime, Stranded and stock are what the SKU is now; LBB, payments and loss are the current period’s.',
          'Amounts in different currencies are never added together; the summary’s change is the current period over the previous.',
        ],
      },
    },
    search: {
      label: 'Search SKUs',
      placeholder: 'SKU code or ASIN…',
      clear: 'Clear the search',
    },
    reset: 'Reset',
    pnl: {
      noSku: 'Ad spend on no SKU',
      stats: {
        sales: 'Sales',
        profit: 'Profit / loss',
        margin: 'Margin',
        marginHint: 'In the main currency',
        received: 'Received',
        receivedHint: '{{amount}} still to receive',
      },
      insights: {
        flow: 'Where the money went',
        flowHint:
          'From sales to profit, in {{currency}}: what the goods cost, what the marketplaces kept and what the ads spent.',
        losses: 'Biggest losses',
        profits: 'Biggest profits',
        rankedHint: 'Profit or loss of each row on this page, in {{currency}}.',
        share: 'Share of sales',
        shareHint: 'Each row’s part of the sales on this page, in {{currency}}.',
      },
      summary: {
        sales: 'Sales',
        salesHint:
          'Units and what they sold for in the range; returns are what was refunded, net is after VAT.',
        costs: 'Costs',
        costsHint:
          'Cost of goods for the units sold, the marketplaces’ commission and fees, and the ads spent.',
        profit: 'Profit',
        profitHint:
          'Contribution profit: the profit of the lines whose cost of goods is known, less every ad spent. The margin divides it by those lines’ net sales.',
        payments: 'Payments',
        paymentsHint:
          'What the marketplaces have paid for these sales, and what is still expected from them.',
        excluded: 'Some lines have no cost of goods yet, so they are left out of the profit.',
      },
      groups: {
        sales: 'Sales',
        costs: 'Costs',
        profit: 'Profit',
        payments: 'Payments',
      },
      columns: {
        currency: 'Currency',
        sku_code: 'SKU',
        sku_name: 'Title',
        brand_name: 'Brand',
        category: 'Category',
        subcategory: 'Subcategory',
        units: 'Units',
        sales_amount: 'Sales',
        returned_sales_amount: 'Returns',
        net_sales_amount: 'Net sales',
        cogs_amount: 'Cost of goods',
        marketplace_charges_amount: 'Commission & fees',
        ad_spend_amount: 'Ad spend',
        cp_amount: 'Profit / loss',
        cp_margin: 'Margin',
        settled_amount: 'Received',
        expected_amount: 'To receive',
      },
    },
    lossMaking: {
      title: 'Loss making SKUs',
      subtitle: {
        loss: 'SKUs that lost money, or earned less than their store expects. Worst first.',
        profitability:
          'Every SKU traded at each store, profit and loss in full. Biggest sellers first.',
      },
      tabs: {
        label: 'Which SKUs',
        loss: 'Loss making SKUs',
        profitability: 'Profitability',
      },
      filters: 'Loss making SKUs filters',
      action: {
        loss: 'Find losses',
        profitability: 'Show profitability',
        working: 'Loading…',
      },
      rangeTooLong: {
        title: 'Pick a shorter range',
        hint: 'Loss making SKUs reads at most {{max}} days at a time, as VentaHub did.',
      },
      prompt: {
        title: 'Pick where to look',
        loss: 'Choose a warehouse, a marketplace, stores or brands and a date range, then Find losses.',
        profitability:
          'Choose a warehouse, a marketplace, stores or brands and a date range, then Show profitability.',
      },
      card: {
        loss_one: '{{count}} under-performing SKU',
        loss_other: '{{count}} under-performing SKUs',
        profitability_one: '{{count}} SKU row',
        profitability_other: '{{count}} SKU rows',
      },
      search: {
        label: 'Search SKUs',
        placeholder: 'SKU, name, brand or store…',
      },
      stats: {
        underPerforming: 'Under-performing SKUs',
        underPerformingHint: '{{skus}} SKUs · {{stores}} stores',
        losing: 'Losing money',
        losingHint: 'Profit below zero',
        underMargin: 'Under margin',
        underMarginHint: 'Five points or more under the store’s margin',
        totalLoss: 'Profit / loss',
        totalLossHint: 'Over every row shown',
        sales: 'Total sales',
        salesHint: '{{units}} units',
        cost: 'Cost of goods',
        costHint: '{{amount}} commission',
        received: 'Payment received',
        receivedHint: '{{amount}} to receive',
        profit: 'Net profit / loss',
        profitHint: '{{margin}} of cost of goods',
        skus: 'SKUs',
        skusHint: '{{rows}} rows · {{stores}} stores',
      },
      groups: {
        product: 'Product',
        place: 'Where sold',
        units: 'Units sold',
        sales: 'Sales',
        money: 'Sales and costs',
        costs: 'Costs and payments',
        ads: 'Advertising',
        result: 'Result',
        stock: 'Stock',
      },
      columns: {
        sku: 'SKU',
        sku_code: 'SKU Code',
        sku_name: 'SKU Name',
        brand_name: 'Brand',
        category: 'Category',
        subcategory: 'Sub-Category',
        marketplace_name: 'Marketplace',
        store_name: 'Store',
        seller_units: 'Seller',
        df_units: 'DF',
        of_units: 'OF',
        vo_units: 'Invoice',
        units: 'Total units',
        sales_amount: 'Total sales',
        cogs_amount: 'Cost',
        commission_amount: 'Commission',
        ad_spend_amount: 'AMS spend',
        profit_amount: 'Profit / Loss',
        margin: 'Margin %',
        stock_units: 'Stock',
        cover_days: 'Cover (days)',
        seller_amount: 'Seller Sales',
        df_amount: 'DF Sales',
        of_amount: 'Offline Sales',
        vo_amount: 'Invoice Sales',
        seller_units_long: 'Units',
        df_units_long: 'DF Units',
        of_units_long: 'OF Units',
        vo_units_long: 'Invoice Units',
        units_long: 'Total Units',
        sales_amount_long: 'Total Sales',
        cogs_amount_long: 'Cost of Goods',
        returned_units: 'Returns',
        settled_amount: 'Payment Received',
        expected_amount: 'To Receive',
        ad_sales_amount: 'AMS Sales',
        ad_spend_amount_long: 'AMS Spend',
        profit_amount_long: 'Profit/Loss',
        cover_days_long: 'Stock Coverage Days',
        replen: 'Replen',
        status: 'Status',
        currency: 'Currency',
        loss_reason: 'Why',
      },
      hints: {
        seller_units: 'Units the store sold on the marketplace.',
        df_units: 'Units invoiced on direct fulfilment.',
        of_units: 'Units invoiced to offline retailers.',
        vo_units: 'Units invoiced to the marketplace as its vendor.',
        cogs_amount: 'Units times landed cost, where the cost is known.',
        commission_amount:
          'What the marketplace kept: commission, fees and promotions, less what it credited.',
        ad_spend_amount: 'Advertising spent on the SKU at the store.',
        ad_sales_amount: 'Sales the ads are credited with.',
        profit_amount:
          'Profit of the lines whose cost is known, less the AMS spend. Blank when no line has a known cost.',
        margin: 'Profit over the cost of goods: the markup.',
        stock_units: 'Held now in the store’s warehouses and at the marketplace.',
        cover_days: 'How many days the stock lasts at this period’s daily sales.',
        returned_units: 'Marketplace units returned.',
        settled_amount: 'What the marketplace has paid for these sales.',
        expected_amount: 'What it is still expected to pay.',
        replen: 'The SKU’s replenishment class at the store’s warehouses.',
      },
      replen: {
        R: 'Replen',
        N: 'Non-replen',
        T: 'Trial',
      },
      reason: {
        loss: 'Losing money',
        under_margin: 'Under margin',
        underMarginHint: 'Margin {{margin}} against the store’s {{expected}}',
      },
      noSku: 'Ad spend on no SKU',
      unattributed: 'Unattributed',
      unattributedHint: 'Invoices whose retailer sells through no store linked to the warehouse.',
      lowCover: 'Under {{days}} days of stock',
      empty: {
        noLossTitle: 'No SKU lost money',
        noLossHint:
          'Every SKU covered its cost of goods, charges and ads, and met its store’s margin, in this period.',
        noneTitle: 'No SKUs match',
        noneHint:
          'Try another warehouse, marketplace, store or brand, a different search, or a longer date range.',
      },
    },
  },
  reportFilters: {
    warehouse: 'Warehouse',
    allWarehouses: 'All warehouses',
    marketplace: 'Marketplace',
    allMarketplaces: 'All marketplaces',
    pickMarketplace: 'Pick a marketplace',
    store: 'Store',
    allStores: 'All stores',
    brand: 'Brand',
    allBrands: 'All brands',
    dates: 'Date range',
  },
  businessView: {
    title: 'Business view',
    subtitle: 'Retailer SKU-level analytics for the selected marketplace and date range.',
    filters: 'Business View filters',
    filter: {
      stores: 'Stores',
      store: 'Store',
      allStores: 'All stores',
      marketplace: 'Marketplace',
      pickMarketplace: 'Marketplace',
      warehouse: 'Warehouse',
      allWarehouses: 'All warehouses',
      marketplaces: 'Marketplaces',
      brands: 'Brands',
      brandsOptional: 'Brands (Optional)',
      brand: 'Brand',
      allBrands: 'All brands',
      compare: 'Compare with',
      dates: 'Date range',
      apply: 'Show results',
      clear: 'Clear filters',
    },
    grain: {
      label: 'Rows',
      sku: 'SKU view',
      listing: 'ASIN view',
    },
    // Shared with Partner View and Review Data, which roll up by level.
    groupBy: 'Group rows by',
    searchLabel: 'Search by {{level}}',
    searchPlaceholder: 'Search {{level}}…',
    currency: 'Currency',
    rowsPerPage: 'Rows',
    cardTitle: { sku: 'SKUs', listing: 'ASINs' },
    search: {
      label: 'Search the Business View',
      placeholder: 'Search ASIN, SKU, title…',
      chip: 'Search: {{text}}',
    },
    generate: {
      label: 'Generate Business View',
      again: 'Regenerate',
      working: 'Generating…',
      pickFirst: 'Pick a marketplace or a warehouse first',
    },
    operators: {
      contains: 'Contains',
      equals: 'Equals',
      startsWith: 'Starts with',
      endsWith: 'Ends with',
      isEmpty: 'Is empty',
      isNotEmpty: 'Is not empty',
      isAnyOf: 'Is any of',
      isNoneOf: 'Is none of',
      '!=': 'Not equals',
      '>': 'Greater than',
      '>=': 'Greater than or equal',
      '<': 'Less than',
      '<=': 'Less than or equal',
    },
    filterMenu: {
      label: 'Filters',
      title: 'Filter rows',
      none: 'No filters yet: every row that sold or holds stock is shown.',
      where: 'Where',
      and: 'And',
      field: 'Filter column {{place}}',
      operator: 'Filter operator {{place}}',
      valueFor: 'Filter value {{place}}',
      value: 'Value',
      list: 'a, b, c',
      remove: 'Remove filter on {{field}}',
      add: 'Add filter',
      clear: 'Clear all',
      oneCurrency: 'Money columns filter only when the view has one currency: pick a marketplace.',
    },
    sortMenu: {
      label: 'Sort',
      title: 'Sort rows',
      sortBy: 'Sort by',
      thenBy: 'Then by',
      field: 'Sort column {{place}}',
      direction: 'Sort direction {{place}}',
      desc: 'Descending',
      asc: 'Ascending',
      remove: 'Remove sort by {{field}}',
      add: 'Add sort (max {{max}})',
      reset: 'Reset to default',
      ties: 'Rows that tie are ordered by warehouse stock, then FBA stock.',
      oneCurrency: 'Money columns sort only when the view has one currency: pick a marketplace.',
    },
    columnsMenu: {
      label: 'Columns',
      groups: 'Column groups',
      unavailable: 'Not reported for this marketplace',
    },
    dataSource: {
      sales: 'Sales',
      stock: 'Stock',
    },
    openProduct: 'Open product {{asin}}',
    viewAdGroups: 'View Adgroups',
    viewAdGroupsFor: 'View ad groups for {{asin}}',
    jump: {
      toListing: 'ASIN view',
      toSku: 'SKU view',
      toListingFor: 'Open {{sku}} in ASIN view',
      toSkuFor: 'Open {{sku}} in SKU view',
    },
    moreListings: '+{{count}}',
    moreListingsHint_one: 'Listed under {{count}} ASIN — ASIN view shows the split',
    moreListingsHint_other: 'Listed under {{count}} ASINs — ASIN view shows the split',
    sameSku: 'The same SKU as the row above: a SKU figure, repeated on each of its listings',
    unassigned: 'Unassigned',
    stockAsOf: 'Stock as of {{when}}',
    drawer: {
      title: 'Product Details',
      openOnAmazon: 'Open on {{marketplace}}',
      viewOn: 'View on {{marketplace}}',
      viewSku: 'View SKU',
      tabs: 'Product details sections',
      detailsTab: 'Details',
      paymentsTab: 'Payments',
      payments: {
        scope: '{{store}} · {{from}} – {{to}} · {{currency}}',
        units: 'Units',
        sales: 'Sales',
        commission: 'Commission',
        received: 'Received',
        toReceive: 'To receive',
        unavailableTitle: 'No payments to show',
        unavailableHint:
          'This ASIN has no listing with a SKU and currency in the marketplace’s stores, so its orders cannot be read.',
      },
      storePicker: 'Store',
      imageAlt: 'Product image of {{asin}}',
      noImage: 'No image',
      asin: 'ASIN',
      sku: 'SKU',
      specifications: 'Specifications',
      dimensions: 'Dimensions (cm)',
      dimensionsValue: '{{length}} × {{width}} × {{height}}',
      weight: 'Weight',
      weightValue: '{{grams}} g',
      listing: 'Listing',
      listings: 'Listings',
      noListing: 'No listing for this ASIN in the marketplace’s stores.',
      focused: 'Focused',
      sellerSku: 'Seller SKU',
      condition: 'Condition',
      price: 'Price',
      salePrice: 'Sale price',
      priceBand: 'Min – max',
      drr: 'DRR',
      cost: 'Cost',
      warehouse: 'Warehouse',
      purchase: 'Purchase',
      shipping: 'Shipping',
      noCost: 'No cost on file for this SKU.',
      adGroups: 'Marketing Adgroups',
      adGroup: 'Ad group',
      store: 'Store',
      spend: 'Spend',
      clicks: 'Clicks',
      openInAms: 'Open {{adGroup}} in AMS View',
      noAdGroups: 'No ad group spent on this ASIN in the selected range.',
    },
    summary: {
      title: 'Dataset Summary',
      products_one: '{{count}} Product',
      products_other: '{{count}} Products',
      sales: 'Sales',
      salesHint: 'Total units sold and revenue generated during the selected period',
      financial: 'Financial',
      financialHint: 'Payment received and returns',
      inventory: 'Inventory',
      inventoryHint: 'Warehouse and FBA stock levels',
      marketing: 'Marketing',
      marketingHint: 'Advertising performance metrics',
      marketingWarning: 'Tentative data — exact values in the AMS Data page',
      performance: 'Performance',
      performanceHint: 'Buy Box win rate and customer ratings',
      secondary: 'Secondary',
      secondaryHint:
        'Amazon weekly Sales & Traffic reports, covering the Sunday–Saturday weeks overlapping the selected range. Vendor accounts report ordered figures and glance views; seller accounts report the conversion percentage. "-" = not reported. Not comparable with the invoice/order-based Sales figures.',
      secondaryInventory: 'Sec. Inventory',
      secondaryInventoryHint:
        'Amazon’s weekly vendor inventory report. On-hand and open-PO figures are a snapshot from the most recent Sunday–Saturday week overlapping the range — not a total for the period. "-" = not reported.',
      seller: 'Seller',
      df: 'DF',
      po: 'PO',
      of: 'OF',
      invoice: 'Invoice',
      total: 'Total',
      aov: 'AOV (Total)',
      payment: 'Payment',
      returns: 'Returns',
      warehouse: 'Warehouse',
      fba: 'FBA',
      cover: 'Stock cover',
      coverValue: '{{days}} days',
      adSales: 'Sales',
      units: 'Units',
      impressions: 'Impr.',
      ctr: 'CTR',
      cvr: 'CVR',
      spend: 'Spend',
      clicks: 'Clicks',
      cpc: 'CPC',
      clicksPerOrder: 'C/Order',
      roas: 'ROAS',
      adAov: 'AOV (Mkt)',
      acos: 'ACOS',
      buyBox: 'Buybox',
      sessions: 'Sessions',
      rating: 'Rating',
      reviews: 'reviews',
      ordered: 'Ordered',
      glanceViews: 'Glance V.',
      conversion: 'Conv. %',
      onHand: 'On Hand',
      openPo: 'Open PO',
      sellThrough: 'Sell-Through',
    },
    groups: {
      salesQty: 'Sales — Quantity',
      salesQtyHint:
        'Units in the range, by channel. Total is Seller + DF + OF + Invoice (PO excluded).',
      revenue: 'Sales — Revenue',
      revenueHint: 'What the units sold for, per currency; nothing is added across currencies.',
      financial: 'Financial',
      financialHint: 'What the marketplaces paid for the range’s orders, and the units returned.',
      stock: 'Stock',
      stockHint:
        'Stock on hand now: in our warehouses and at FBA, and how long it lasts at the range’s rate of sale.',
      warehouseAgeing: 'Warehouse Ageing',
      warehouseAgeingHint:
        'How long the warehouse stock beside it has been on hand, dated from each box’s receipt (latest receipt wins). The eight columns add up to Warehouse. Pre-migration is stock carried over from before receipts were recorded.',
      fbaAgeing: 'FBA Ageing',
      fbaAgeingHint:
        'Aged FBA units from Amazon’s FBA inventory planning report. Only these two buckets are reported, so unlike Warehouse Ageing they do not add up to FBA stock. "-" means not reported.',
      listing: 'Listing',
      listingHint:
        'The listing checks of the product page: Buy Box win rate over the range, and the sales rank, rating and reviews as last read. A SKU’s Buy Box is its listings’ wins over their checks, its rank its best listing’s, its rating weighted by reviews.',
      marketing: 'Marketing',
      marketingHint: 'Sponsored Ads in the range, for the ads resolved to this SKU or listing.',
      secondary: 'Secondary Data',
      secondaryHint:
        'Amazon weekly Sales & Traffic reports, covering the Sunday–Saturday weeks that overlap the selected range (weekly data cannot be trimmed to partial weeks). Vendor accounts report ordered figures and glance views; seller accounts report sessions and the conversion percentage. "-" = not reported. Not comparable with the order-based Sales figures.',
      secondaryInventory: 'Secondary Inventory',
      secondaryInventoryHint:
        'Amazon’s weekly vendor inventory report, over the same Sunday–Saturday weeks that overlap the selected range. On-hand, aged and open-PO figures are a snapshot from the most recent covered week — not a total for the period.',
    },
    columns: {
      asin: 'ASIN',
      retailerSku: 'Retailer SKU',
      seller_sku: 'MP SKU',
      sku_code: 'Internal SKU',
      sku_name: 'SKU name',
      title: 'Title',
      brand_name: 'Brand',
      category: 'Category',
      subcategory: 'Subcategory',
      data_source: 'Data Source',
      product_ref: 'ASIN',
      seller_units: 'Seller Qty',
      df_units: 'DF Qty',
      po_units: 'PO Qty',
      of_units: 'OF Qty',
      vo_units: 'Invoice Qty',
      total_units: 'Total Qty',
      drr: 'DRR',
      seller_amount: 'Seller $',
      df_amount: 'DF $',
      po_amount: 'PO $',
      of_amount: 'OF $',
      vo_amount: 'Invoice $',
      total_amount: 'Total $',
      average_price: 'AOV (Total)',
      payment_amount: 'Payment',
      returned_units: 'Returns',
      warehouse_units: 'Warehouse',
      fba_units: 'FBA',
      stock_cover_days: 'Stock Cover',
      age_0_30_units: '0-30d',
      age_31_60_units: '31-60d',
      age_61_90_units: '61-90d',
      age_91_180_units: '91-180d',
      age_181_270_units: '181-270d',
      age_over_270_units: '270d+',
      age_pre_migration_units: 'Pre-migration',
      age_unknown_units: 'Unknown',
      fba_aged_91_180_units: 'FBA 91-180d',
      fba_aged_181_270_units: 'FBA 181-270d',
      buybox_win_rate: 'Buybox %',
      sales_rank: 'Sales Rank',
      rating: 'Rating',
      rating_count: 'Reviews',
      impressions: 'Impressions',
      clicks: 'Clicks',
      ad_units: 'Units',
      ad_sales: 'Sales',
      ad_spend: 'Spend',
      ctr: 'CTR',
      cpc: 'CPC',
      clicks_per_order: 'Click/Order',
      cvr: 'CVR',
      ad_average_price: 'AOV (Mkt)',
      roas: 'ROAS',
      ad_groups: 'Ad Groups',
      vendor_ordered_units: 'Ordered Units',
      vendor_ordered_revenue: 'Ordered Rev',
      vendor_customer_returns: 'Cust. Returns',
      glance_views: 'Glance Views',
      sessions: 'Sessions (wk)',
      page_views: 'Page Views (wk)',
      buy_box_share: 'Buy Box % (wk)',
      unit_session_pct: 'Conversion % (wk)',
      traffic_units_ordered: 'Units Ordered (wk)',
      buy_box_page_views: 'Buy Box Page Views',
      vendor_sellable_units: 'Sellable On Hand',
      vendor_sellable_cost: 'On Hand Cost',
      vendor_unsellable_units: 'Unsellable',
      vendor_aged_90_plus_units: 'Aged 90d+',
      vendor_open_po_units: 'Open PO',
      vendor_sell_through: 'Sell-Through',
      view: 'View',
    },
    hints: {
      asin: 'Amazon’s product id. Opens the product drawer.',
      title:
        'The listing’s title as the marketplace lists it; the SKU’s name when it has no listing.',
      data_source:
        'Sales when the row sold in the range; Stock when it only holds stock. Sales takes precedence.',
      seller_units: 'Units sold via your seller account',
      df_units: 'Direct Fulfilment units (invoice retailer type D)',
      po_units: 'Purchase Order units (not included in Total)',
      of_units: 'Offline units (invoice retailer type O)',
      vo_units: 'Invoice sales units (invoice retailer type V)',
      total_units: 'Seller + DF + OF + Invoice (PO excluded)',
      drr: 'Daily run rate: Total Qty ÷ days in range',
      seller_amount: 'Revenue from seller-account sales',
      df_amount: 'Revenue from Direct Fulfilment (invoice type D)',
      po_amount: 'Revenue from Purchase Orders (not included in Total)',
      of_amount: 'Revenue from Offline sales (invoice type O)',
      vo_amount: 'Revenue from invoice sales (invoice type V)',
      total_amount: 'Seller + DF + OF + Invoice revenue (PO excluded)',
      average_price: 'Average Order Value = Total Revenue ÷ Total Qty',
      payment_amount: 'Payment received from the marketplace',
      returned_units: 'Number of units returned',
      warehouse_units: 'Total warehouse stock on hand',
      fba_units: 'Amazon FBA inventory',
      stock_cover_days:
        'Days the current stock (warehouse + FBA) will last at the selected range’s sales rate = (Warehouse + FBA) ÷ (Total Qty ÷ days in range). "-" = no sales in range.',
      age_0_30_units: 'Units received in the last 30 days',
      age_31_60_units: 'Units received 31-60 days ago',
      age_61_90_units: 'Units received 61-90 days ago',
      age_91_180_units: 'Units received 91-180 days ago',
      age_181_270_units: 'Units received 181-270 days ago',
      age_over_270_units:
        'Units received more than 270 days ago. The ladder stops here by design — older stock is not split further.',
      age_pre_migration_units:
        'Units carried over from before receipts were recorded, so their true age is unknown.',
      age_unknown_units: 'Units with no usable receipt document at all, so no age can be stated.',
      fba_aged_91_180_units:
        'FBA units aged 91-180 days, as reported by Amazon. "-" = not reported.',
      fba_aged_181_270_units:
        'FBA units aged 181-270 days, as reported by Amazon. "-" = not reported.',
      buybox_win_rate:
        'Buy Box win percentage: the listing checks in the range that found our offer in the Buy Box',
      sales_rank: 'Best-Sellers rank, as last read. A SKU ranks as its best listing.',
      rating: 'Average star rating, as last read. A SKU’s is weighted by its listings’ reviews.',
      rating_count: 'Number of customer reviews, as last read',
      impressions: 'Sponsored Ads impressions',
      clicks: 'Sponsored Ads clicks',
      ad_units: 'Units sold via Sponsored Ads',
      ad_sales: 'Revenue from Sponsored Ads',
      ad_spend: 'Ad spend',
      ctr: 'Click-Through Rate = clicks ÷ impressions',
      cpc: 'Cost Per Click = spend ÷ clicks',
      clicks_per_order: 'Clicks per ad-attributed order = clicks ÷ ad units',
      cvr: 'Conversion Rate = ad units ÷ clicks',
      ad_average_price: 'Avg Order Value for Marketing = ad sales ÷ ad units',
      roas: 'Return on Ad Spend = ad sales ÷ ad spend',
      ad_groups: 'Open the product panel at the list of ad groups advertising this ASIN',
      vendor_ordered_units:
        'Vendor weekly sales report: units end customers ordered. "-" = not reported that week.',
      vendor_ordered_revenue: 'Vendor weekly sales report: revenue from customer orders',
      vendor_customer_returns: 'Vendor weekly sales report: customer returns',
      glance_views:
        'Vendor traffic report: Amazon’s detail-page view count (vendor accounts only — not comparable with seller session counts).',
      sessions: 'Seller Sales & Traffic report: sessions over the covered weeks',
      page_views: 'Seller Sales & Traffic report: page views over the covered weeks',
      buy_box_share:
        'Seller Sales & Traffic report: Buy Box page views ÷ page views over the covered weeks',
      unit_session_pct:
        'Seller Sales & Traffic report: units ordered ÷ sessions over the covered weeks. Amazon-reported; can exceed 100%.',
      traffic_units_ordered: 'Seller Sales & Traffic report: units ordered over the covered weeks',
      buy_box_page_views: 'Seller Sales & Traffic report: page views where you held the Buy Box',
      vendor_sellable_units:
        'Vendor inventory report: sellable units Amazon held at the end of the most recent covered week. A snapshot, not a period total.',
      vendor_sellable_cost:
        'Vendor inventory report: Amazon’s cost value of that sellable on-hand stock, same latest-week snapshot.',
      vendor_unsellable_units:
        'Vendor inventory report: unsellable units held at the end of the most recent covered week.',
      vendor_aged_90_plus_units:
        'Vendor inventory report: units aged 90 days or more at the end of the most recent covered week.',
      vendor_open_po_units:
        'Vendor inventory report: units on open purchase orders at the end of the most recent covered week.',
      vendor_sell_through:
        'Vendor inventory report, most recent covered week: net shipped units ÷ (opening sellable + net received units).',
      view: 'Jump to this SKU in the other row grain — a SKU-view row opens its listings, a listing row opens its SKU rollup. The grid filters to that SKU; clear the filter to return.',
    },
    empty: {
      generateTitle: 'Generate the view',
      pickHint: 'Pick a marketplace or a warehouse, then click Generate Business View.',
      generateHint: 'Click Generate Business View to load every SKU that sold or holds stock.',
      noneTitle: 'Nothing sold or in stock',
      noneHint: 'No SKU sold in this range or holds stock. Try a longer date range.',
      filteredTitle: 'Nothing matches these filters',
      filteredHint: 'Try fewer brands or filters, or a longer date range.',
    },
  },
  dataVault: {
    notLoaded: {
      title: 'Nothing loaded yet',
      hint: 'Choose your filters, then press {{action}} to load the data.',
    },
    records: 'Records',
    title: 'Data Vault',
    subtitle: 'Query, filter, and download raw data across every connected source.',
    home: 'Home',
    clearSearch: 'Clear search',
    query: {
      dataType: 'Data type',
      store: 'Store',
      chooseStore: 'Choose a store',
      allStores: 'All stores',
      storeRequired: 'This data is shown one store at a time.',
      load: 'Load data',
      uploadExcel: 'Upload Excel',
      noStore: 'Choose a store, then load the data',
      noStoreHint: 'This data is shown one store at a time. Pick one above and press Load data.',
    },
    storeOption: '{{name}} ({{marketplace}})',
    search: 'Search',
    searchPlaceholder: 'Search…',
    rows: 'Rows',
    pageSize: 'Rows per page',
    pageSizeOption_one: '{{count}} row',
    pageSizeOption_other: '{{count}} rows',
    total: 'Total',
    totalFor: 'Total ({{currency}})',
    openOnAmazon: 'Open {{asin}} on Amazon',
    mode: {
      editChip: 'EDIT MODE',
      comingSoon: 'Coming with the next client',
      noPermission: 'You cannot edit this dataset',
      submitted_one: 'Submitted {{count}} change for approval · Ticket #{{ticket}}',
      submitted_other: 'Submitted {{count}} changes for approval · Ticket #{{ticket}}',
      copyTicket: 'Copy ticket number',
      saveFailed: 'The changes could not be submitted.',
      deleteSubmitted: 'Deletion submitted for approval · Ticket #{{ticket}}',
      openTicket: 'Open ticket',
      deleteFailed: 'The deletion could not be submitted.',
      // A row of an order as edit mode names it to a screen reader.
      lineLabel: '{{order}}, {{sku}}',
    },
    preview: {
      file_one: '{{name}}: {{count}} row',
      file_other: '{{name}}: {{count}} rows',
      problems_one: '{{count}} validation error. The offending cells are bordered red below.',
      problems_other: '{{count}} validation errors. The offending cells are bordered red below.',
      row: 'Row {{row}}',
      header: 'Header',
      sheet: 'Sheet',
      unplaced_one:
        '{{count}} of these could not be tied to a cell. Fix it at the row or column named, and upload again.',
      unplaced_other:
        '{{count}} of these could not be tied to a cell. Fix them at the rows or columns named, and upload again.',
      truncated: 'Showing the first {{shown}} of {{count}} rows; all rows will be uploaded.',
      editCell: 'Edit {{column}} on row {{row}}',
    },
    uploadDialog: {
      title: 'Upload {{dataset}}',
      file: 'Sheet (.xlsx or .csv)',
      mode: 'Mode',
      insert: 'Insert',
      modify: 'Modify',
      insertHint: 'Adds new rows; existing rows are left alone.',
      modifyHint: 'Overwrites the rows the sheet names.',
      chip: { insert: 'Insert only', modify: 'Will overwrite' },
      commitMode: { insert: 'Commit insert', modify: 'Commit modifications' },
      pickFile: 'Pick file',
      replaceFile: 'Replace file',
      reading: 'Reading the sheet…',
      drop: 'Pick an Excel file, or drop it here, to preview it before committing',
      formats: 'Excel (.xlsx, .xlsm) or CSV',
      fixFirst: 'Fix the problems in the sheet, then pick it again to commit.',
      another: 'Upload another sheet',
      committing: 'Uploading…',
      preview: 'Preview',
      uploaded_one: 'Uploaded {{count}} row for processing · upload {{id}}',
      uploaded_other: 'Uploaded {{count}} rows for processing · upload {{id}}',
      uploadedNoId_one: 'Uploaded {{count}} row for processing',
      uploadedNoId_other: 'Uploaded {{count}} rows for processing',
      rejected_one: '{{count}} row was rejected.',
      rejected_other: '{{count}} rows were rejected.',
      failed: 'The file could not be processed.',
      template: 'Template',
      templateDownloaded: 'Downloaded {{name}}',
      templateFailed: 'Could not download the template.',
      upload: 'Upload Excel',
      description: 'Pick a sheet to preview it. A commit sends it to ingestion for processing.',
    },
    chips: {
      search: 'Search: “{{query}}”',
      sort: 'Sort: {{column}} {{direction}}',
      columnFilters_one: '{{count}} column filter',
      columnFilters_other: '{{count}} column filters',
      columns: 'Custom columns',
    },
    costs: {
      subtitle: 'Bulk add/update vendor or retailer SKU costs from a spreadsheet.',
      bulkUpload: 'Bulk upload',
      viewCosts: 'View SKU costs',
      costType: 'Cost type',
      vendorCosts: 'Vendor costs',
      retailerCosts: 'Retailer costs',
      tabs: 'Costs',
      vendor: 'Vendor',
      retailer: 'Retailer',
      currency: 'Currency',
      warehouse: 'Warehouse',
      skuName: 'SKU name',
      skuCode: 'SKU code',
      skuOrRetailerSku: 'SKU code or retailer SKU',
      brand: 'Brand',
      any: 'Any',
      filters: 'Cost filters',
      getDetails: 'Get details',
      downloadTemplate: 'Download template',
      filterUnavailable: 'Filtering by this needs the next API release',
    },
    internalSkus: {
      scope: 'Warehouse and store',
      title: 'SKU Management',
      subtitle: 'Upload, edit, and audit SKU metadata across every catalogue.',
      skus: 'SKUs',
      skuCosts: 'SKU Costs',
      uploadDimensions: 'Upload Dimensions',
      uploadDimensionsHint:
        'A sheet of SKU codes with their length, width, height and weight. Rows modify the SKUs they name.',
      tabs: 'Internal SKUs',
    },
    products: {
      noStore: 'Pick a store to see its listings',
      noStoreHint: 'Listings are shown one store at a time. Choose one in the Store filter.',
      actions: {
        template: 'Template',
        listingsTemplate: 'Listings upload template',
        drrTemplate: 'DRR template',
        upload: 'Upload Excel',
        drr: 'DRR Upload',
        downloaded: 'Downloaded {{name}}',
        downloadFailed: 'Could not download the file. Try again.',
        needsStore: 'Pick a store first',
      },
      upload: {
        title: 'Upload listings',
        description:
          'Add or update {{store}}’s listings from an Excel or CSV sheet. Start from the template, or edit an export from this page.',
        file: 'Sheet (.xlsx or .csv)',
        submit: 'Upload',
        uploading: 'Uploading…',
        received_one: '{{count}} row received',
        received_other: '{{count}} rows received',
        processing:
          'New and changed rows appear in the table within a few minutes, once they are processed.',
        unchanged: 'Every row was already here. Nothing changed.',
        duplicate: 'This exact file was uploaded before. Nothing changed.',
        quarantined: 'The file could not be read. Check it has the template’s header row.',
        rejected_one: '{{count}} row skipped: it needs a product id and a SKU.',
        rejected_other: '{{count}} rows skipped: each needs a product id and a SKU.',
        failed: 'The upload failed.',
      },
      drrUpload: {
        title: 'DRR upload',
        description:
          'Set the daily run rate of {{store}}’s listings from a sheet of product ids and DRR. The file is checked first; nothing is saved until you confirm.',
        file: 'Sheet (.xlsx or .csv)',
        check: 'Check file',
        checking: 'Checking…',
        save_one: 'Save DRR for {{count}} listing',
        save_other: 'Save DRR for {{count}} listings',
        saving: 'Saving…',
        summary: '{{rows}} rows · {{matched}} listings matched',
        ready: 'The file is valid. Saving sets the DRR and marks these listings as focused SKUs.',
        problems_one:
          '{{count}} problem. Fix the file and check it again; nothing is saved while any remain.',
        problems_other:
          '{{count}} problems. Fix the file and check them again; nothing is saved while any remain.',
        saved_one: 'DRR saved for {{count}} listing.',
        saved_other: 'DRR saved for {{count}} listings.',
        row: 'Row',
        productId: 'Product ID',
        problem: 'Problem',
        failed: 'The DRR file could not be processed.',
      },
    },
    empty: {
      filteredTitle: 'No rows match these filters',
      filteredHint: 'Try fewer filters or a different search.',
      noneTitle: 'No data yet',
      noneHint: 'Nothing has been loaded into this dataset so far.',
    },
    aggregate: {
      currency: 'Currency',
      unassigned: 'Unassigned',
      top: 'Top {{count}} by {{metric}}',
      topFor: 'Top {{count}} by {{metric}} ({{currency}})',
      share: 'Share of {{metric}}',
      shareFor: 'Share of {{metric}} ({{currency}})',
      charts: 'Charts',
    },
    metrics: {
      listings: 'Listings',
      focused: 'Focused',
      avg_regular_price: 'Avg regular price',
      skus: 'SKUs',
      avg_purchase_cost: 'Avg purchase cost',
      avg_shipping_cost: 'Avg shipping cost',
      avg_selling_price: 'Avg selling price',
      avg_total_fee: 'Avg total fee',
      revenue: 'Revenue',
      orders: 'Orders',
      units_ordered: 'Units ordered',
      units_shipped: 'Units shipped',
      units_cancelled: 'Units cancelled',
      buying_cost: 'Buying cost',
      refund_amount: 'Refund amount',
      returns: 'Returns',
      quantity: 'Quantity',
      amount: 'Amount',
      transactions: 'Transactions',
    },
    trend: {
      title: 'Orders over time',
      metric: 'Metric',
      granularity: 'Granularity',
      collapse: 'Hide trend',
      expand: 'Show trend',
      metrics: { units: 'Units', revenue: 'Revenue', orders: 'Orders' },
      granularities: { day: 'Daily', week: 'Weekly', month: 'Monthly' },
      series: '{{metric}} ({{currency}})',
    },
    overview: {
      listings: 'Listings',
      skus: 'Internal SKUs',
      ordersNoCurrency: 'Orders',
      revenue: 'Revenue ({{currency}})',
      revenueNoCurrency: 'Revenue',
      ordersHint: '{{units}} units · {{window}}',
      window_one: 'Last {{count}} day',
      window_other: 'Last {{count}} days',
      trendTitle: 'Orders, last 30 days',
      datasets: 'Datasets',
      noDatasets: 'No datasets to show',
      noDatasetsHint: 'Ask an administrator for catalogue or orders access.',
    },
    datasets: {
      products: {
        title: 'Products (Listings)',
        description: 'Every listing across your stores, with its prices and status.',
      },
      internalSkus: {
        title: 'Internal SKUs',
        description: 'Your own catalogue: codes, barcodes, dimensions and flags.',
      },
      costs: {
        title: 'Costs',
        description: 'Purchase, shipping and additional cost per SKU and vendor.',
      },
      retailerCosts: {
        title: 'Retailer costs',
        description: 'Net landed cost and RRP per SKU as each retailer quotes it.',
      },
      fees: {
        title: 'Fees',
        description: 'Marketplace fee estimates per listing.',
      },
      orders: {
        title: 'Orders',
        description: 'Order lines from every marketplace, with prices and costs.',
      },
      returns: {
        title: 'Returns',
        description: 'Returned items, their reason, condition and refund.',
      },
      settlements: {
        title: 'Settlements',
        description: 'Settlement transactions as the marketplaces paid them out.',
      },
      ticketExceptions: {
        title: 'Ticket exceptions',
        description: 'SKUs excluded from a ticket type, and why.',
      },
      mailExceptions: {
        title: 'Mail exceptions',
        description: 'Ad groups kept off the missing ad groups mail, per store, and why.',
      },
    },
    columns: {
      id: 'ID',
      store_name: 'Store',
      marketplace_short_code: 'Marketplace',
      asin: 'ASIN',
      sku_code: 'SKU',
      sku_name: 'SKU name',
      marketplace_sku: 'Marketplace SKU',
      brand_name: 'Brand',
      category: 'Category',
      subcategory: 'Subcategory',
      condition: 'Condition',
      listing_status: 'Status',
      title: 'Title',
      manufacturer: 'Manufacturer',
      is_fba: 'FBA',
      is_focused: 'Focused',
      drr: 'DRR',
      currency_code: 'Currency',
      regular_price: 'Regular price',
      sale_price: 'Sale price',
      min_price: 'Min price',
      max_price: 'Max price',
      mfn_shipping_price: 'MFN shipping',
      vat_rate: 'VAT rate',
      updated_at: 'Updated',
      sku_model: 'Model',
      ean: 'EAN',
      upc_code: 'UPC',
      isbn: 'ISBN',
      hsn_code: 'HSN',
      is_exclusive: 'Exclusive',
      is_eol: 'End of life',
      length_cm: 'Length (cm)',
      width_cm: 'Width (cm)',
      height_cm: 'Height (cm)',
      weight_g: 'Weight (g)',
      color: 'Colour',
      size: 'Size',
      material: 'Material',
      is_fragile: 'Fragile',
      is_hazardous: 'Hazardous',
      country_of_origin: 'Origin',
      stores_count: 'Stores',
      warehouse_name: 'Warehouse',
      vendor_name: 'Vendor',
      purchase_cost: 'Purchase cost',
      shipping_cost: 'Shipping cost',
      additional_cost: 'Additional cost',
      vendor_vat_rate: 'Vendor VAT rate',
      vendor_rrp: 'Vendor RRP',
      status: 'Status',
      valid_from: 'Valid from',
      valid_to: 'Valid to',
      is_valid: 'Valid',
      retailer_name: 'Retailer',
      retailer_product_code: 'Retailer product code',
      nlc: 'NLC',
      rrp: 'RRP',
      created_at: 'Created',
      selling_price: 'Selling price',
      fee_fixed: 'Fixed fee',
      fee_variable_frac: 'Variable fee',
      fee_return_frac: 'Return fee',
      fee_shipping: 'Shipping fee',
      total_fee: 'Total fee',
      estimate_date: 'Estimated',
      order_id: 'Order',
      order_item_id: 'Order item',
      purchase_date: 'Purchased',
      last_update_date: 'Last update',
      order_status: 'Order status',
      order_type: 'Order type',
      quantity_ordered: 'Qty ordered',
      quantity_shipped: 'Qty shipped',
      quantity_cancelled: 'Qty cancelled',
      item_price: 'Item price',
      buying_cost: 'Buying cost',
      import_cost: 'Import cost',
      amount_tax: 'Tax',
      amount_shipping: 'Shipping',
      amount_discount: 'Discount',
      return_order_id: 'Return order',
      return_date: 'Returned',
      return_status: 'Return status',
      return_reason: 'Reason',
      quantity: 'Quantity',
      item_condition: 'Item condition',
      refund_amount: 'Refund',
      settlement_id: 'Settlement',
      start_date: 'Start',
      end_date: 'End',
      deposit_date: 'Deposited',
      settlement_total: 'Settlement total',
      transaction_type: 'Transaction type',
      amount_type: 'Amount type',
      amount: 'Amount',
      posted_date: 'Posted',
      search_term: 'Search term',
      price: 'Price',
      rating: 'Rating',
      review_count: 'Reviews',
      is_sponsored: 'Sponsored',
      scraped_at: 'Scraped',
      rating_count: 'Ratings',
      critical_reviews: 'Critical reviews',
      ticket_type_name: 'Ticket type',
      retailer_sku_code: 'Retailer SKU',
      mp_sku_code: 'Marketplace SKU',
      comments: 'Comments',
      list_type: 'List type',
      recipients: 'Recipients',
      user_id: 'User',
      ad_group_id: 'Ad group ID',
      ad_group_name: 'Ad group name',
      reason: 'Reason',
    },
  },
  marketingRules: {
    viewOnly: 'View only: adding or removing these needs the ads:edit permission.',
    // Shared across the three rules pages.
    breadcrumb: 'Marketing',
    scope: { PROFILE: 'Profile', AD_GROUP: 'Ad group' },
    mode: { LIVE: 'Live', SHADOW: 'Shadow', OFF: 'Off' },
    executionMode: { manual: 'Manual', test: 'Test', scheduled: 'Scheduled' },
    allRules: 'All rules',
    never: 'Never',
    none: '—',
    expired: 'Expired',
    dryRun: 'Dry run',
    live: 'Live',
    run: {
      store: 'Store',
      allStores: 'All stores',
      allStoresHint: 'Run the rules across every store (default)',
      validation:
        'Validation mode — runs for this store are forced to dry-run; ticket creation is disabled.',
      runAllDry: 'Run all — dry run',
      runAllTickets: 'Run all — create tickets',
      dryRunRule: 'Dry-run {{code}}',
      ticketsRule: 'Run {{code}} & create tickets',
      storeScopedHint: 'Store-scoped runs are validation-only (dry run)',
      allRulesLabel: 'ALL active rules',
      viewOnly: 'Running the rules needs the ads:edit permission across the organization.',
      confirmTitle: 'Run & create tickets — {{label}}',
      confirmMasterOn:
        'The master "dry-run only" switch is ON, so this run will be forced to dry-run and create no tickets. Disable it in the Engine Config tab first if you want tickets.',
      confirmLive:
        'This will create real T22 tickets for every matched row (up to 20 rows per ticket). A full registry run can produce hundreds of tickets — prefer running a single rule first.',
      confirmScope: 'Scope: {{label}}',
      confirmForced: 'Run anyway (forced dry-run)',
      confirmCreate: 'Create tickets',
      cancel: 'Cancel',
      running: 'running…',
      forcedToast:
        'Run was forced to dry-run: the master "dry-run only" switch is ON (Engine Config tab).',
      createdToast_one: 'Created {{count}} ticket',
      createdToast_other: 'Created {{count}} tickets',
      dryToast: 'Dry run complete',
      failedToast: 'Execution failed',
      resultTitle: 'Execution {{id}} ({{kind}})',
      resultBody:
        '{{rows}} rows matched · {{suggestions}} suggestions · {{tickets}} tickets created · {{seconds}}s',
      dry: 'dry run',
      live: 'live',
      downloadJson: 'Download JSON',
      downloadExcel: 'Download Excel',
      downloadExcelRun: 'Download the workbook of the run from {{when}}',
      audit: 'Audit workbook for {{code}}',
      auditHint:
        'Audit workbook for {{code}}: the rows it fired on and the ones it did not, for this store',
      auditFailed: 'The audit workbook failed.',
      downloading: 'Downloading…',
      downloadFailed: 'The download failed.',
      downloadRun: 'Download the JSON of the run from {{when}}',
      viewResults: 'View results',
      close: 'Dismiss',
      log: {
        connecting: '⏳ connecting…',
        fallback: '⚠ live stream unavailable — running without live logs…',
        startedDry: '▶ execution {{id}} — {{count}} rules (dry run)',
        startedLive: '▶ execution {{id}} — {{count}} rules (LIVE — will create tickets)',
        forcedDry: '⚠ master dry-run switch is ON — forced to dry-run',
        ruleStarted: '… {{code}} running',
        ruleFinished: '✓ {{code}} — {{rows}} matches in {{seconds}}s',
        ruleFinishedWarnings: '✓ {{code}} — {{rows}} matches in {{seconds}}s (validation warnings)',
        ruleSkipped: '– {{code}} skipped ({{reason}})',
        ruleError: '✗ {{code}} failed: {{error}}',
        aggregating: '… aggregating matches',
        buildingItems: '… preparing ticket items for {{rows}} rows',
        creatingTickets:
          '… creating tickets ({{items}} items, ~{{tickets}} tickets) — this can take a while',
        ticketsCreated: '🎟 {{count}} tickets created',
        done: '✔ done — {{rows}} rows, {{tickets}} tickets, {{seconds}}s',
        finished: '✓ finished — {{rows}} rows matched',
        failed: '✗ run failed: {{message}}',
        executionFailed: '✗ execution failed',
      },
    },
    rules: {
      title: 'Marketing Rules (v2)',
      description:
        'Code-first rules engine — rules live in the codebase; this catalog is read-only.',
      tabs: {
        rules: 'Rules',
        automation: 'Automation',
        config: 'Engine Config',
      },
      empty: 'No rules registered',
      emptyHint: 'Rules are added in the backend codebase, not here.',
      // The accessible name of a row's expander: which rule, not just "expand".
      toggle: 'Details for {{code}}',
      uniqueKey: 'Unique key: {{columns}}',
      lookback_one: 'Lookback: {{count}} day',
      lookback_other: 'Lookback: {{count}} days',
      cron: 'Schedule: {{cron}}',
      dataset: 'Dataset: {{dataset}}',
      checkOnly: 'Asks a human — never reaches Amazon',
      displayColumns: 'Display columns',
      label: 'Label',
      column: 'Column',
      runtimeState: 'Runtime state',
      notRunYet: 'This rule has not run yet, so it has no learned state.',
      state: {
        coefficient: 'Coefficient',
        alpha: 'α (accepts)',
        beta: 'β (rejects)',
        shortlisted: 'Shortlisted',
        accepted: 'Accepted',
        executions: 'Executions',
        lastRun: 'Last run',
        master: 'Master',
        frozen: 'Frozen',
        inactive: 'Inactive',
      },
    },
    exclusions: {
      title: 'Ads Rule Exclusions',
      intro:
        'The engine raises nothing for an excluded profile or ad group — for every rule, or only the rules listed.',
      add: 'Add exclusion',
      empty: 'No exclusions',
      emptyHint: 'Every profile and ad group is open to every rule.',
      columns: {
        scope: 'Scope',
        target: 'Target',
        rules: 'Rules',
        reason: 'Reason',
        expires: 'Expires',
        addedBy: 'Added by',
        actions: 'Actions',
      },
      delete: 'Delete exclusion for {{target}}',
      confirmTitle: 'Delete this exclusion?',
      confirmBody:
        'The engine will start raising suggestions for {{target}} again on its next run.',
      confirm: 'Delete',
      cancel: 'Cancel',
      deleted: 'Exclusion deleted',
      created: 'Exclusion added',
      form: {
        title: 'Add exclusion',
        description: 'Stop the engine suggesting changes for a profile or an ad group.',
        scope: 'Scope',
        profileId: 'Profile ID',
        adGroupId: 'Ad group ID',
        ruleCodes: 'Rule codes',
        ruleCodesHint: 'Comma-separated, e.g. TR1, STR7. Leave empty to block every rule.',
        reason: 'Reason',
        expiresAt: 'Expires',
        expiresAtHint: 'Optional. Leave empty to keep it until it is deleted.',
        submit: 'Add exclusion',
        required: 'Required',
      },
    },
    feeds: {
      mode: { LIVE: 'Live', SHADOW: 'Shadow', BAND: '±band' },
      shadow: {
        title: 'Shadow report',
        intro_one:
          'What shadow mode said it would accept over the last {{days}} days, against what reviewers chose ({{count}} ticket read).',
        intro_other:
          'What shadow mode said it would accept over the last {{days}} days, against what reviewers chose ({{count}} tickets read).',
        introLoading: 'What shadow mode said it would accept, against what reviewers chose.',
        empty: 'Nothing in shadow yet',
        emptyHint:
          'Set a rule to Shadow on an ad group or profile, and its next real run shows here.',
        columns: {
          rule: 'Rule',
          adGroup: 'Ad group',
          would_accept: 'Would accept',
          agreed: 'Reviewer agreed',
          disagreed: 'Reviewer disagreed',
          pending: 'Undecided',
          agreement: 'Agreement',
        },
      },
      decisions: {
        title: 'Recent automated decisions',
        intro:
          'Every suggestion the engine decided without a reviewer: by a Live configuration, or by the ±band bid auto-accept.',
        search: 'Search',
        searchPlaceholder: 'Rule, ad group, store…',
        modes: 'Modes',
        empty: 'No automated decisions',
        emptyHint: 'Nothing has been decided without a reviewer on this page of tickets.',
        applied: 'On Amazon',
        notApplied: 'Not on Amazon',
        pagedByTicket:
          'Pages count tickets, not decisions: a ticket holds up to twenty rows, each with several suggestions.',
        columns: {
          when: 'When',
          adGroup: 'Ad group',
          rule: 'Rule',
          action: 'Action',
          mode: 'Mode',
          onAmazon: 'On Amazon',
          ticket: 'Ticket',
        },
      },
    },
    automation: {
      intro:
        'LIVE accepts and pushes to Amazon with no reviewer; SHADOW only records what it would have done; OFF keeps a rule in review.',
      add: 'Set automation',
      filter: 'Mode',
      allModes: 'All modes',
      empty: 'Nothing is automated',
      emptyHint: 'Every suggestion waits for a person.',
      columns: {
        scope: 'Scope',
        target: 'Target',
        mode: 'Mode',
        rules: 'Rules',
        expires: 'Expires',
        setBy: 'Set by',
        actions: 'Actions',
      },
      delete: 'Delete {{mode}} automation for {{target}}',
      confirmTitle: 'Return these rules to review?',
      confirmBody: 'Suggestions from {{rules}} on {{target}} will wait for a reviewer again.',
      confirm: 'Return to review',
      cancel: 'Cancel',
      deleted: 'Automation removed',
      saved: 'Automation saved',
      form: {
        title: 'Set automation',
        description: 'Rules already in another mode on the same target move to this one.',
        scope: 'Scope',
        mode: 'Mode',
        profileId: 'Profile ID',
        adGroupId: 'Ad group ID',
        ruleCodes: 'Rule codes',
        ruleCodesHint: 'Comma-separated, at least one, e.g. TR1, DC1.',
        reason: 'Reason',
        expiresAt: 'Expires',
        expiresAtHint: 'Optional. When it lapses back to human review.',
        submit: 'Save',
        required: 'Required',
        numeric: 'A profile ID is a number',
      },
    },
    config: {
      viewOnly: 'View only: changing the engine’s switches needs ads:edit across the whole tenant.',
      dryRunOn: 'The engine is in dry-run-only mode. No Amazon API calls, no tickets raised.',
      dryRunOff: 'The engine is LIVE. Runs with dry-run off will raise tickets.',
      dryRunOnly: 'Dry-run only',
      dryRunOnlyHint:
        'The engine refuses to raise tickets or call Amazon, whatever a run asks for.',
      reflectOnAms: 'Reflect accepted actions on AMS view',
      reflectOnAmsHint: 'Accepted actions are also written to the AMS view.',
      autoAccept: 'Bid auto-accept',
      autoAcceptHint:
        'The engine applies sole bid changes within ±{{pct}}% directly, with no reviewer.',
      autoAcceptWarning:
        'Bid auto-accept is on: small bid changes reach Amazon across every store without review.',
      pct: 'Auto-accept band (%)',
      pctHint: 'Greater than 0, at most 50.',
      pctSave: 'Save band',
      pctInvalid: 'Enter a number greater than 0 and at most 50',
      saved: 'Engine config saved',
      lastChanged: 'Last changed {{when}}',
      neverChanged: 'Every switch is at its default.',
    },
    executions: {
      title: 'V2 rule execution results',
      description: 'Every run of the marketing rules engine, newest first.',
      filter: 'Show',
      filterAll: 'All runs',
      filterDry: 'Dry runs',
      filterLive: 'Live runs',
      empty: 'No runs yet',
      emptyHint: 'Runs appear here once the engine has run for this organization.',
      columns: {
        started: 'Started',
        mode: 'Trigger',
        kind: 'Kind',
        rules: 'Rules',
        evaluated: 'Rows evaluated',
        matched: 'Rows matched',
        suggestions: 'Suggestions',
        duration: 'Duration',
        actions: 'Actions',
      },
      view: 'View run from {{when}}',
      running: 'Not finished',
    },
    execution: {
      title: 'Execution result',
      back: 'Back to executions',
      evaluated: 'Rows evaluated',
      matched: 'Rows matched',
      suggestions: 'Actions suggested',
      dryRun: 'This execution ran in dry-run mode — no tickets were raised, no Amazon calls made.',
      diagnostics_one: '{{count}} diagnostic recorded',
      diagnostics_other: '{{count}} diagnostics recorded',
      search: 'Search rows',
      searchPlaceholder: 'Search rows, rules, actions…',
      filterRules: 'Filter by rule',
      ruleChip: '{{code}} ({{count}})',
      empty: 'This run matched nothing',
      emptyHint: 'No row met any rule’s conditions.',
      noMatch: 'No row matches these filters',
      noMatchHint: 'Clear the search or the rule filter.',
      columns: {
        adGroup: 'Ad group',
        entity: 'Entity',
        clicks: 'Clicks',
        acos: 'ACOS',
        rules: 'Rules',
        matched: 'Matched rules',
      },
      openRow: 'Open details for {{row}}',
      rowTitle: 'Row detail — {{row}}',
      rowData: 'Row data',
      matchedRules_one: 'Matched rule ({{count}})',
      matchedRules_other: 'Matched rules ({{count}})',
      bidCalculation: 'Bid calculation',
      clamped: 'Clamped to marketplace minimum {{min}}',
      nudged: 'No-op nudge ×{{multiplier}}',
      trace_one: 'Trace ({{count}} step)',
      trace_other: 'Trace ({{count}} steps)',
      close: 'Close',
    },
    health: {
      title: 'Rules engine health',
      description:
        'What the engine has learned from reviewers, rule by rule, and how busy it has been.',
      liveWarning: 'The engine is live: runs with dry-run off will raise tickets.',
      autoAcceptWarning:
        'The engine is accepting its own bid changes within ±{{pct}}% — no human in the loop.',
      cards: {
        rulesRun: 'Rules that have run',
        rulesRunHint: '{{active}} active, of {{total}} in the catalogue',
        reviewed: 'Suggestions reviewed',
        reviewedHint: '{{accepted}} accepted',
        runs: 'Runs in the last 30 days',
        runsHint_one: '{{count}} could act',
        runsHint_other: '{{count}} could act',
        lastRun: 'Last run',
        lastRunNever: 'The engine has not run yet',
      },
      table: 'Rules',
      columns: {
        rule: 'Rule',
        status: 'Status',
        coefficient: 'Coefficient',
        reviewed: 'Reviewed',
        acceptRate: 'Accepted',
        decayed: 'Recent-weighted',
        runs: 'Runs',
        lastRun: 'Last run',
      },
      interval: '90% interval {{low}}–{{high}}',
      notRun: 'Not run',
      retired: 'Retired',
      footer:
        'Snapshot taken {{when}}. This page is read-only; nothing on it changes engine behaviour.',
      jobNeverRan:
        'The {{purpose}} job ({{task}}) has never run, so the numbers it moves are frozen.',
      jobStale: 'The {{purpose}} job ({{task}}) last ran {{when}}; the numbers it moves are stale.',
      loop: {
        title: 'Learning loop',
        awaiting: 'Awaiting review',
        awaitingHint: '{{decided}} decided by people',
        outcomes: 'Outcomes scored',
        outcomesHint: '{{pending}} waiting for their after window',
        selfAccepted: 'Self-accepted',
        selfAcceptedHint: '{{automated}} more by automation configs',
        pairs: 'Rule pairs learned',
        pairsHint: 'Co-decided on the same rows',
      },
      verdict: { automate: 'Automate', suppress: 'Suppress', review: 'Keep reviewing' },
      extra: {
        verdict: 'Verdict',
        human: 'Accepted by people',
        humanDetail: '{{rate}} of {{n}}',
        recent: 'Last 14 days',
        selfShare: 'Self-accepted',
        outcome: 'Measured outcome',
        outcomeDetail: '95% CI {{low}} to {{high}} · {{n}} scored',
        gates: 'Gates',
      },
      pairs: {
        title: 'Rules that land together',
        intro:
          'Pairs of rules people decided on the same rows (at least 25), most disagreement first: where a reviewer draws a distinction the engine has not learned.',
        empty: 'No pair has 25 shared decisions yet',
        emptyHint: 'Pairs appear once people have decided two rules on the same rows 25 times.',
        columns: {
          pair: 'Rules',
          co: 'Shared rows',
          both: 'Both accepted',
          disagree: 'Disagreed',
          a: 'First accepted',
          b: 'Second accepted',
        },
      },
      narrative: {
        title: 'What this says',
        ask: 'Explain this page',
        asking: 'Reading the numbers…',
        unavailable: 'No narrative: {{reason}}',
        by: 'Written by {{model}}',
      },
    },
  },
  ads: {
    // Shared across the advertising pages.
    store: 'Store',
    stores: 'Stores',
    allStores: 'All stores',
    storesSelected_one: '{{count}} store',
    storesSelected_other: '{{count}} stores',
    pickStore: 'Select a store',
    brands: 'Brands',
    allBrands: 'All brands',
    brandsSelected_one: '{{count}} brand',
    brandsSelected_other: '{{count}} brands',
    unbranded: 'Unbranded',
    from: 'From',
    to: 'To',
    preset: 'Range',
    presets: {
      '7': 'Last 7 days',
      '14': 'Last 14 days',
      '30': 'Last 30 days',
      '90': 'Last 90 days',
    },
    search: 'Search',
    none: '—',
    never: 'Never',
    all: 'All',
    clear: 'Clear',
    noStores: 'No Amazon store with an Ads profile is connected yet.',
    readOnly: 'Read-only: changes to Amazon are made from VentaHub until they are ported.',
    metric: {
      impressions: 'Impr.',
      clicks: 'Clicks',
      ctr: 'CTR',
      spend: 'Spend',
      cpc: 'CPC',
      units: 'Units',
      sales: 'Sales',
      asp: 'ASP',
      acos: 'ACOS',
      roas: 'ROAS',
      conversion: 'Conv.',
    },
    columns: {
      name: 'Name',
      store: 'Store',
      status: 'Status',
      adGroups: 'Ad groups',
      bid: 'Bid',
      target: 'Target',
      refreshed: 'Refreshed',
      // CSV-only columns.
      detail: 'Detail',
      brand: 'Brand',
      category: 'Category',
      subcategory: 'Subcategory',
      bidMin: 'Lowest bid',
      bidMax: 'Highest bid',
      currency: 'Currency',
    },
    totals: 'Totals ({{currency}})',
    previous: 'Previous: {{value}}',
    deltaNew: 'new',
    deltaTitle: 'Previously {{value}}',
    trend: 'Trend',
    trendEmpty: 'No data in this range.',
    noData: 'No data',
    // The level switcher's tabs, in the order every performance page keeps.
    level: {
      ad_group: 'Ad groups',
      campaign: 'Campaigns',
      sku: 'SKU',
      brand: 'Brand',
      category: 'Category',
      subcategory: 'Subcategory',
      asin: 'ASINs',
      target: 'Targets',
      search_term: 'Search terms',
      negatives: 'Negatives',
      alt_product: 'Alt products',
    },
    actions: 'Actions',
    moreFilters: 'More filters',
    levelMore: 'More',
    chip: '{{label}}: {{value}}',
    searchSkuAsin: 'SKU / ASIN / name',
    searchSkuAsinPlaceholder: 'Contains…',
    kpi: {
      label: '{{metric}} · {{currency}}',
      vsPrevious: 'vs {{from}} – {{to}}',
      noPrevious: 'no previous period',
    },
    trendCard: {
      title: 'Trend',
      metrics: 'Metrics',
      granularity: 'Granularity',
      collapse: 'Hide trend',
      expand: 'Show trend',
      granularities: {
        day: 'Day',
        week: 'Week',
        month: 'Month',
        quarter: 'Quarter',
        year: 'Year',
      },
      series: '{{metric}} ({{currency}})',
    },
    charts: {
      topSpend: 'Top {{count}} by spend · {{currency}}',
      spendShare: 'Spend share · {{currency}}',
      thisPage: 'From the rows on this page.',
    },
    table: {
      filtersThisPage: 'Column filters apply to the rows on this page.',
      total: 'Total · {{currency}}',
    },
    export: {
      page: 'Export page',
      all: 'Export all pages',
      allHint: 'Up to {{formatted}} rows',
    },
    adGroupChip: 'Ad group: {{name}}',
    removeAdGroup: 'Show every ad group again',
    drill: 'Open {{name}}',
    history: 'History of {{name}}',
    empty: 'Nothing matches these filters',
    emptyHint: 'Widen the dates, pick another store or clear the search.',
    negatives: {
      keyword: 'Negative keyword / target',
      match: 'Match',
      type: 'Type',
      lastSeen: 'Last seen',
      note: 'Current configuration — not scoped to the date range.',
    },
    amsView: {
      title: 'AMS View',
      description: 'Ad performance across your Amazon stores.',
      status: 'Ad group status',
      anyStatus: 'Any status',
      campaignPaused: 'Campaign paused',
    },
    review: {
      title: 'Review AMS Data',
      description: 'One store, this period against the one before.',
      current: 'Current period',
      compare: 'Compared with',
      type: 'Ad type',
      anyType: 'All types',
      pickStore: 'Pick a store to review.',
      summary: 'Summary',
    },
    changes: {
      title: 'AMS Change History',
      description:
        'Every change VentaHub applied to Amazon Ads — what changed, why, and who approved it.',
      coverage:
        'Shows changes VentaHub made. Edits made directly in the Amazon Ads console are not here.',
      coverageMore: 'What’s not here?',
      coverageConsole:
        'Edits made in the Amazon Ads console. This page reads VentaHub’s own change log — the rules engine, tickets, manual edits on AMS View and Review AMS, and dayparting. Amazon does not say who made a console edit, so those cannot be shown or attributed here.',
      coveragePlacements:
        'Placement bid adjustments are not tracked at all. Nothing changes them, and Amazon’s placement percentages are not kept, so no placement history exists to show.',
      headline_one: '{{count}} change',
      headline_other: '{{count}} changes',
      failed: '{{count}} rejected by Amazon',
      action: 'Action',
      outcome: 'Outcome',
      rule: 'Rule',
      anyAction: 'Any action',
      anyOutcome: 'Any outcome',
      anyRule: 'Any rule',
      person: 'Person',
      sourceFilter: 'Source',
      campaign: 'Campaign',
      grain: {
        ad_groups: 'Ad groups',
        asins: 'ASINs',
        targets: 'Targets',
        search_terms: 'Search terms',
        negatives: 'Negative keywords',
      },
      cards: { total: 'Changes', failed: 'Rejected by Amazon' },
      perDay: 'Changes per day',
      perDayHint: 'Every day of the selection, by outcome.',
      series: { executed: 'Applied', failed: 'Rejected', other: 'Other' },
      columns: {
        when: 'When',
        entity: 'Changed',
        action: 'Action',
        change: 'Change',
        adGroup: 'Ad group',
        account: 'Account',
        rule: 'Rule',
        who: 'Who',
        outcome: 'Outcome',
      },
      system: 'System',
      user: 'User #{{id}}',
      source: {
        rules_engine: 'Rules engine',
        manual: 'Manual',
        tickets: 'Ticket',
        'system-auto-accepted': 'Auto-accepted',
        new_targeting_page: 'New Targeting',
        dayparting: 'Dayparting',
        unknown: 'Not recorded',
      },
      status: {
        success: 'Applied',
        executed: 'Applied',
        duplicate_success: 'Already there',
        failed: 'Failed',
        skipped: 'Skipped',
        pending: 'Pending',
        attempting: 'Never returned',
        rolled_back: 'Rolled back',
      },
      details: 'Details for change {{id}}',
      reasoning: 'Why',
      evaluatedOn: 'Evaluated on ad group',
      triggeredBy: 'Triggered by',
      triggeredByData: '{{level}} data',
      originLevel: {
        target: 'Target',
        search_term: 'Search term',
        negative: 'Negative',
        ad_product: 'Product (ASIN)',
        ad_group: 'Ad group',
        campaign: 'Campaign',
      },
      amazonSaid: 'Amazon said',
      ticket: 'Ticket',
      ticketNumber: 'Ticket #{{id}}',
      ticketType: 'Type',
      raised: 'Raised',
      raisedBy: 'Raised by',
      reviewedBy: 'Reviewed by',
      resolved: 'Resolved',
      execution: 'Engine run',
      payload: 'Recorded payload',
      timeline: 'Full history of this {{level}}',
      empty: 'No changes in this range',
      emptyHint:
        'Nothing of this kind was applied. Try widening the dates, clearing a filter, or another tab.',
    },
    timeline: {
      back: 'Back to change history',
      pickStore: 'Open this page from the change history, or add a store to the address.',
      currentBid: 'Current bid',
      changes: 'Changes',
      firstSeen: 'First seen',
      lastActive: 'Last active',
      lastActiveUnknownShort: 'Needs hourly data',
      lastActiveUnknown:
        'Not known: the last active time comes from Amazon’s hourly data, which VentaWiz does not have yet.',
      empty: 'No changes recorded yet.',
      level: {
        target: 'Target',
        ad_group: 'Ad group',
        campaign: 'Campaign',
        search_term: 'Search term',
      },
      filter: {
        label: 'Show changes',
        all: 'All',
        bid_change: 'Bids',
        state_change: 'Status',
        added: 'Additions',
      },
      view: { label: 'Timeline view', list: 'List', chart: 'Chart' },
      chartHint: 'Oldest to newest · hover or focus a point for details',
      several: 'This target appears as {{count}} distinct entries — each is shown below.',
      entry: 'Target {{index}} of {{total}}',
      entryId: 'id {{id}}',
      allChanges: 'All changes to this {{level}}',
      missingTerm:
        'Open this page from AMS View or Review AMS Data: it needs a store, an ad group and a search term.',
      noBid: 'A search term has no bid of its own; the bid shown is its keyword’s.',
      event: {
        first_appeared: 'First appeared',
        added: 'Added',
        bid_change: 'Bid changed',
        state_change: 'State changed',
        budget_change: 'Budget changed',
      },
      observed: 'Seen in Amazon data',
      result: { success: 'Applied', error: 'Failed', skipped: 'Skipped' },
    },
    searchAnalytics: {
      title: 'Search analytics',
      description: 'Customer search queries: the whole market, our ASINs, and our ads.',
      show: 'Show',
      showAll: 'All queries',
      showWith: 'With ads data',
      showWithout: 'Without ads data',
      orderBy: 'Top by',
      source: 'Of',
      sourceTotal: 'the market',
      sourceOur: 'our ASINs',
      sourceMarketing: 'our ads',
      weeks: 'Brand Analytics reports whole weeks: counting from {{start}} to {{end}}.',
      group: { total: 'Market', our: 'Ours', marketing: 'Our ads' },
      purchases: 'Purchases',
      query: 'Search query',
      topQueries: 'Top {{count}} queries by impressions',
      topQueriesHint: 'The market’s impressions, from the queries on this page.',
      keywords: 'Our keywords on “{{query}}”',
      keyword: 'Keyword',
      match: 'Match',
      noKeywords: 'No individual keyword served on this query.',
      expand: 'Keywords for {{query}}',
      empty: 'No search queries in this range',
      emptyHint: 'Brand Analytics data may not have arrived for these weeks yet.',
    },
    reportSync: {
      title: 'Report Sync',
      description: 'Amazon Ads report pulls: what arrived, what failed, what is still on its way.',
      dateField: 'Date applies to',
      dateFields: {
        created_at: 'Request time',
        last_updated_at: 'Status last updated',
        report_start_date: 'Report start date',
        report_end_date: 'Report end date',
      },
      types: 'Report types',
      allTypes: 'All report types',
      tabs: { all: 'All', successful: 'Successful', failed: 'Failed', pending: 'Pending' },
      cards: {
        successful: 'Successful',
        failed: 'Failed',
        pending: 'Pending',
        polls: 'Polls',
        pollsHint: '{{average}} per report',
        turnaround: 'Average turnaround',
        turnaroundHint: 'minutes, successful reports',
        perDay: 'Reports per day',
      },
      failedBanner_one: '{{count}} report pull failed in this selection.',
      failedBanner_other: '{{count}} report pulls failed in this selection.',
      columns: {
        type: 'Report type',
        status: 'Status',
        period: 'Data period',
        requested: 'Requested',
        updated: 'Last updated',
        turnaround: 'Turnaround',
        rows: 'Rows',
        inserted: 'Inserted',
        error: 'Error',
      },
      minutes: '{{value}} min',
      empty: 'No report pulls match these filters.',
      help: {
        open: 'Help & my permissions',
        title: 'Report Sync help',
        intro:
          'Report Sync shows the state of the automated report pulls from Amazon Ads: which arrived, which are still on their way and which failed.',
        useTitle: 'How to use this page',
        use: {
          dates:
            'Pick a date range, then choose in More filters what it applies to: the request time (when the pull was triggered — the default), when its status last changed, or the report start or end date (the Amazon data days the report covers).',
          filters:
            'Narrow the list by store and by one or more report types. Only stores with an Amazon Ads connection are listed; a store without one never has report pulls.',
          loads: 'The list follows the filters as you change them; there is nothing else to press.',
        },
        resultsTitle: 'Understanding the results',
        results: {
          cards:
            'The cards at the top count the successful, failed and pending pulls in the selection, the polls they took and the average turnaround.',
          list: 'The table lists every pull. Use the tabs to see only the failed, pending or successful ones; a failed pull shows why in its Error column.',
          rows: 'Rows is how many records Amazon returned; Inserted is how many passed the filters (unknown ad groups, blacklisted stores, archived targets) and were written. A large gap usually means the AdGroup Master is behind.',
        },
        permissionsTitle: 'Your permissions',
        permissions:
          'The stores you can choose come from your access. If a store you expect is missing, ask an administrator for access to it.',
        close: 'Close',
      },
    },
    uploads: {
      button: 'Upload',
      template: 'Download template',
      templating: 'Downloading…',
      templateFailed: 'The template could not be downloaded.',
      choose: 'Choose file…',
      checking: 'Checking…',
      uploading: 'Uploading…',
      failed: 'The file could not be uploaded.',
      raised: 'Ticket #{{number}} created and sent for approval',
      raisedCounts: '{{new}} new, {{updated}} changed, {{unchanged}} unchanged.',
      allTickets: 'View all tickets',
      form: {
        title: 'Upload ad data',
        hint: 'Pick the marketplace and store; the store’s platform decides the file. Download its template, fill it in, then choose the file. Nothing changes until the approval ticket is accepted.',
        marketplace: 'Marketplace',
        marketplacePlaceholder: 'Pick a marketplace',
        marketplaceRequired: 'Pick the marketplace the file is for.',
        store: 'Store',
        storePlaceholder: 'Pick a store',
        storeAfterMarketplace: 'Pick a marketplace first',
        storeRequired: 'Pick the store the file is for.',
        upload: 'File',
        reportDate: 'Report date',
        reportDateRequired: 'Pick the day this report covers.',
        reportDateHintRequired:
          'This report has no dates of its own; its figures are saved for this day.',
        reportDateHintOptional: 'Used for any row without a Start or End Date.',
        summary: 'What this upload does',
        noStores: 'No store takes an ads upload',
        noStoresHint:
          'Amazon stores with Ads connected, and every Otto and Noon store you can reach, appear here.',
      },
      kinds: {
        adgroup_master:
          'Ad-group settings: targets, budget, brand, category and ASP for each ad group. New ad groups are added and changed values replace the old ones once approved.',
        otto_ads_report:
          'Otto’s ads report: campaigns, advertised products, keywords and negatives, with each row’s figures for its own dates. A line already saved for that day is replaced, not added twice.',
        noon_ads_report:
          'Noon’s ads report: the campaign, SKU and target sheets, all saved for the report date. A line already saved for that day is replaced, not added twice.',
        fallback: 'This platform’s ads file. It is checked first and saved once approved.',
      },
      warningsTitle: 'Validation warnings',
      warningsHint:
        'Some values look unusual. Upload anyway, or go back and fix the file. Nothing changes until the ticket is approved.',
      warnings: { row: 'Row', field: 'Field', value: 'Value', issue: 'Issue' },
      problemsTitle: 'The file was not uploaded',
      problemsHint: 'Fix the file and upload it again.',
      confirm: 'Upload anyway',
      back: 'Back',
      history: {
        title: 'My uploads',
        empty: 'No upload tickets found. Upload a file to create one.',
        uploadOf: '{{platform}} · {{upload}}',
        columns: {
          ticket: 'Ticket',
          upload: 'Upload',
          file: 'File name',
          store: 'Store',
          created: 'Uploaded',
          rows: 'Lines',
          outcome: 'Outcome',
          reviewedBy: 'Reviewed by',
        },
        outcome: {
          pending: 'Pending review',
          accepted: 'Applied',
          rejected: 'Rejected',
          failed: 'Not applied',
        },
      },
    },
    master: {
      rules: {
        column: 'Rules engine',
        pause: 'Pause',
        pausedLabel: 'Paused',
        pauseAll: 'Pause every rule on {{name}}',
        resumeAll: 'Resume every rule on {{name}}',
        pauseMenu: 'Rule pauses for {{name}}',
        pauseTitle: 'Rule pauses',
        noPauses: 'No rule is paused on this ad group.',
        allRules: 'All rules',
        lapsed: 'Lapsed',
        until: 'Until {{date}}',
        noExpiry: 'No expiry',
        days_one: '{{count}} day',
        days_other: '{{count}} days',
        removePause: 'Remove the pause of {{rules}}',
        rules: 'Rules',
        reason: 'Reason',
        howLong: 'For how long',
        pauseRules: 'Pause these rules',
        paused: 'Rules paused on {{name}}',
        resumed: 'Rules resumed on {{name}}',
        viewOnly: 'Changing this needs the ads:edit permission across the organization.',
        automationMenu: 'Rule automation for {{name}}',
        automationTitle: 'Rule automation',
        notAutomated: 'Not automated',
        noAutomation: 'No rule is automated here, on its own or through its profile.',
        effective: 'Effective modes',
        inherited: 'profile',
        removeAutomation: 'Remove the {{mode}} configuration',
        modeLegend: 'Mode',
        mode: { LIVE: 'Live', SHADOW: 'Shadow', OFF: 'Off' },
        modeHelp: {
          LIVE: 'The engine accepts these rules here and pushes the change to Amazon; the ticket opens already reviewed, as a record.',
          SHADOW:
            'Nothing is applied. Each suggestion says what would have happened, so you can watch a week of it before arming.',
          OFF: 'Never automate these rules on this ad group, even if the profile says to. Also stops the ±band bid auto-accept for them.',
        },
        saveAutomation: 'Set the mode',
        automationSaved: 'Automation saved for {{name}}',
      },
      title: 'AdGroup Master',
      description:
        'Amazon ad-group targets and linked ad groups per store. Upload takes every platform’s file: the AdGroup Master for Amazon, the ads report for Otto and Noon.',
      columns: {
        store: 'Store name',
        id: 'ID',
        adGroupId: 'AdGroup ID',
        adGroup: 'AdGroup name',
        tickets: 'Tickets',
        automation: 'Automation',
        brand: 'Brand name',
        state: 'State',
        budget: 'Budget',
        targetAcos: 'Target ACOS',
        targetCtr: 'Target CTR',
        targetConversion: 'Target conversion',
        asp: 'ASP',
        targeting: 'Targeting type',
        category: 'Category',
        subcategory: 'Subcategory',
        type: 'Ad group type',
        sibling: '{{role}} ad group name',
        label: 'Ad group label',
        campaignId: 'Campaign ID',
        campaign: 'Campaign name',
      },
      role: {
        exact_match: 'Exact match',
        product_target: 'Product target',
        branded: 'Branded',
        competition: 'Competition',
        longtail: 'Long tail',
        high_priority: 'High priority',
      },
      otherPlatform: '{{platform}} has no ad groups to list',
      otherPlatformHint:
        'The master holds Amazon ad groups. {{platform}} ads come in by report: use Upload, pick the store, and send its ads report.',
      empty: 'No ad groups in the master',
      emptyHint: 'Download the template, fill it in and upload it for a store.',
    },
    newTargeting: {
      title: 'New Targeting',
      description:
        'Price keyword and product targets for an ad group from its master targets, and catch ones it already has.',
      adGroup: 'Ad group',
      pickAdGroup: 'Select an ad group',
      adGroupsFailed: 'Ad groups could not be loaded',
      searchAdGroups: 'Find an ad group',
      targets: 'Keywords or ASINs',
      targetsHint: 'One per line. Up to 200.',
      check: 'Check targets',
      required: 'Enter at least one target',
      tooMany: 'At most 200 targets at a time',
      amazonNote:
        'Discovering suggestions and pushing targets to Amazon are not available here yet.',
      calculatedBid: 'Calculated bid',
      calculatedBidHint: 'Target ACOS × target conversion × ASP, from the master.',
      noBid: 'The master has no target ACOS, conversion or ASP for this ad group.',
      columns: { target: 'Target', kind: 'Kind', duplicate: 'Already targeted' },
      kind: { keyword: 'Keyword', product: 'Product' },
      duplicateOf: 'Yes — “{{target}}”',
      notDuplicate: 'No',
    },
  },
  // AMS View and Review AMS Data, ported from VentaHub (modules under ./en/).
  amsShared,
  amsActions,
  amsView: { ...amsViewKit, ...amsViewList, ...amsViewDrill, ...amsViewPage, ...amsViewCharts },
  reviewAms,
  bidSimulator,
  // VentaHub's Focus Search Term sheet and Search Term Data form (./en/focusTerms).
  focusTerms,
  // Operations > Auto ordering (./en/autoOrdering).
  autoOrdering,
  // Operations > Get stock (./en/stock).
  stock,
  // Operations > Stock ageing (./en/stockAgeing).
  stockAgeing,
  // Operations > Cashflow (./en/cashflow).
  cashflow,
  // Operations > PO fulfilment (./en/poFulfilment), and its retailer view.
  poFulfilment,
  retailerPoFulfilment,
  // Operations > SP invoices (./en/spInvoices).
  spInvoices,
  // Operations > SP payments (./en/spPayments).
  spPayments,
  // Operations > Vendor payments (./en/vendorPayments).
  vendorPayments,
  // Operations > Vendor invoices (./en/vendorInvoices).
  vendorInvoices,
  // Operations > Retailer receipts (./en/retailerReceipts).
  retailerReceipts,
  // Operations > Seller flex stock (./en/sellerFlex).
  sellerFlex,
  // Operations > Replenishment (./en/replen).
  replen,
  // Operations > Global shipments (./en/globalShipments).
  globalShipments,
  // Operations > Credit / debit notes (./en/creditDebitNotes).
  creditDebitNotes,
  // Operations > Box history (./en/boxHistory).
  boxHistory,
  // Operations > Purchase orders (./en/purchaseOrders).
  purchaseOrders,
  // Operations > Vendor orders (./en/vendorOrders).
  vendorOrders,
  // Operations > Sales invoices (./en/salesInvoices).
  salesInvoices,
  // Operations > Review order/invoice (./en/opsReview).
  opsReview,
  // Operations > Boxes (./en/boxes).
  boxes,
  // Operations > Block stock (./en/blockStock).
  blockStock,
  // Operations > Warehouse transfer (./en/warehouseTransfer).
  warehouseTransfer,
  // Data Vault > Product / Price Sync (./en/productPriceSync).
  productPriceSync,
  // Data Vault > Product / Price Sync > Shopify (./en/shopifySync).
  shopifySync,
  optimization: {
    title: 'Optimization',
    description:
      'Bid recommendations for ad channels with no bid API — eBay, Otto, Bol, Trendyol, Shopify and the like.',
    howTitle: 'How it works',
    how: 'Everything happens in one workbook. Nothing you upload is stored.',
    steps: {
      download: 'Download the template.',
      fill: 'On its "Input Data" sheet, fill in the Configuration, Current Targeting, Current Search Term and Previous Targeting blocks.',
      upload:
        'Upload it here. The same workbook comes back with its Optimization, Summary and comparison sheets filled in.',
    },
    template: 'Download template',
    templateDownloaded: 'Template downloaded',
    templateFailed: 'The template could not be downloaded.',
    file: 'Filled-in workbook (.xlsx)',
    run: 'Get optimization',
    running: 'Running…',
    done: 'Optimization complete — the results have downloaded.',
    rulesNote:
      'VentaHub also listed which Marketing Rules matched each row. That is not available here yet; the workbook carries the bid recommendations only.',
  },
  dayparting: {
    title: 'Dayparting',
    description:
      'Schedule time-of-day and day-of-week changes to bids, budgets and states on Amazon Ads.',
    store: 'Store',
    pickStore: 'Pick a store',
    viewOnlyTitle: 'View only',
    viewOnly: 'Creating and changing schedules needs the ads:edit permission.',
    noStoreTitle: 'Select a store to begin',
    noStoreDescription:
      'Dayparting works on one Amazon store with an Ads profile at a time: its campaigns, ad groups and targets.',
    scope: { CAMPAIGN: 'Campaigns', AD_GROUP: 'Ad groups', TARGET: 'Targets' },
    days: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
    daysLong: {
      mon: 'Monday',
      tue: 'Tuesday',
      wed: 'Wednesday',
      thu: 'Thursday',
      fri: 'Friday',
      sat: 'Saturday',
      sun: 'Sunday',
    },
    hour: '{{hour}}:00',
    picker: {
      scopeLabel: 'What to schedule',
      adType: 'Ad type',
      search: 'Search',
      selectAll: 'Select all listed',
      clear: 'Clear selection',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      capped:
        'Showing the first {{shown}} of {{total}} matches. Refine your search to reach the rest; "Select all" only selects what is listed.',
      empty: 'Nothing found',
      emptyHint: 'Nothing at this level matches the store, ad type and search.',
      inAdGroup: '{{adGroup}} · {{campaign}}',
      bid: 'Bid {{bid}}',
    },
    profile: {
      range: 'Date range',
      title: 'Performance by day of week',
      dailyNote:
        'Built from daily data. Hour-of-day needs hourly reports, which are not available yet.',
      averageNote:
        'Counts, spend and sales are the average per weekday in the range; rates are computed from the totals.',
      metric: 'Metric',
      start: 'First day',
      end: 'Last day',
      metrics: {
        clicks: 'Clicks',
        impressions: 'Impressions',
        spend: 'Spend',
        sales: 'Sales',
        units: 'Units',
        ctr: 'CTR',
        cpc: 'CPC',
        acos: 'ACOS',
        roas: 'ROAS',
      },
      pickEntities: 'Nothing selected',
      pickEntitiesHint: 'Select one or more entities to see how they perform by weekday.',
      tooLong: 'The range is longer than the {{days}} days the profile can read. Narrow it.',
      reversed: 'The range ends before it starts.',
      empty: 'No data in this range',
      emptyHint: 'The selection had no recorded activity between these days.',
      chartLabel: '{{metric}} by day of week',
      unknown: 'n/a',
    },
    builder: {
      title: 'New schedule',
      titleOne: 'New schedule for “{{label}}”',
      titleMany_one: 'New schedule for {{count}} selected',
      titleMany_other: 'New schedule for {{count}} selected',
      pickFirst: 'Nothing selected',
      pickFirstHint: 'Select one or more entities to configure a schedule.',
      name: 'Schedule name',
      action: 'Action',
      actions: {
        INCREASE_BID: 'Increase bid',
        DECREASE_BID: 'Decrease bid',
        INCREASE_BUDGET: 'Increase budget',
        DECREASE_BUDGET: 'Decrease budget',
        PAUSE_STATE: 'Pause',
        ENABLE_STATE: 'Enable',
      },
      budgetCampaignOnly: 'Budget can only be scheduled on campaigns.',
      stateHint: 'Restores the previous state when the window closes.',
      percent: 'Percent',
      windows: 'Windows',
      window: 'Window {{number}}',
      daysOfWeek: 'Days',
      from: 'From',
      to: 'To',
      addWindow: 'Add window',
      removeWindow: 'Remove window {{number}}',
      timezone: 'Timezone',
      timezoneHint: 'Window hours are read in this IANA zone, for example Europe/London.',
      submit: 'Create schedule',
      submitting: 'Creating…',
      created: 'Schedule saved. It applies from the next hourly run.',
      appliedHourly: 'Applied hourly while a window is open, and reverted when it closes.',
      errors: {
        nameRequired: 'Enter a name',
        percentRequired: 'Enter a percent above 0',
        decreaseBelow100: 'A decrease must be below 100%',
        pickDay: 'Pick at least one day',
        endAfterStart: 'The end must be after the start',
        timezoneRequired: 'Enter a timezone',
      },
    },
    list: {
      title: 'Schedules',
      empty: 'No schedules yet',
      emptyHint: 'Pick an entity above and create one.',
      columns: {
        enabled: 'On',
        name: 'Name',
        scope: 'Applies to',
        createdBy: 'Created by',
        targets: 'Targets',
        adjusted: 'Adjusted now',
        action: 'Action',
        when: 'When',
        actions: 'Actions',
      },
      toggle: 'Schedule “{{name}}” on',
      showEntities: 'Show what “{{name}}” applies to',
      count: {
        CAMPAIGN_one: '{{count}} campaign',
        CAMPAIGN_other: '{{count}} campaigns',
        AD_GROUP_one: '{{count}} ad group',
        AD_GROUP_other: '{{count}} ad groups',
        TARGET_one: '{{count}} target',
        TARGET_other: '{{count}} targets',
      },
      action: {
        INCREASE_BID: '+{{percent}}% bid',
        DECREASE_BID: '−{{percent}}% bid',
        INCREASE_BUDGET: '+{{percent}}% budget',
        DECREASE_BUDGET: '−{{percent}}% budget',
        PAUSE_STATE: 'Pause',
        ENABLE_STATE: 'Enable',
      },
      everyDay: 'Every day',
      windows_one: '{{count}} window',
      windows_other: '{{count}} windows',
      window: '{{days}} {{start}}–{{end}}',
      dates: '{{start}} to {{end}}',
      delete: 'Delete “{{name}}”',
      onlyCreator: 'Only {{creator}} can delete it',
      confirmTitle: 'Delete this schedule?',
      confirmBody:
        '“{{name}}” will be removed, and anything it has adjusted on Amazon is put back to its base.',
      confirm: 'Delete',
      cancel: 'Cancel',
      deleted: 'Schedule deleted; adjustments reverting',
      turnedOn: 'Schedule turned on',
      turnedOff: 'Schedule turned off; adjustments reverting',
    },
    events: {
      title: 'Event log',
      statusFilter: 'Status',
      status: { all: 'All', success: 'Success', failed: 'Failed', skipped: 'Skipped' },
      transition: { APPLY: 'Apply', REVERT: 'Revert' },
      columns: {
        when: 'When',
        target: 'Target',
        transition: 'Transition',
        change: 'Change',
        status: 'Status',
      },
      empty: 'No events logged yet',
      emptyHint: 'Every change the hourly worker applies or reverts is logged here.',
    },
  },
  tickets: {
    // Reporting a problem with a ticket (ReportTicketDialog) — raises a T8.
    report: {
      button: 'Report a problem with ticket #{{number}}',
      title: 'Report a problem with #{{number}}',
      description:
        'Raises a Technical Issues ticket for the technical team, with this ticket’s state and your browser attached.',
      category: 'Category',
      categoryRequired: 'Choose a category.',
      choose: 'Choose…',
      optional: 'Optional',
      severity: 'Severity',
      reason: 'What is wrong',
      steps: 'Steps to reproduce',
      expected: 'What you expected',
      actual: 'What happened',
      send: 'Report',
      sending: 'Reporting…',
      done: 'Reported as ticket #{{number}}',
      open_one: 'Open report',
      open_other: 'Open reports',
      categories: {
        Bug: 'Bug',
        'Data Issue': 'Data issue',
        'UI Problem': 'UI problem',
        Performance: 'Performance',
        Other: 'Other',
      },
      severities: { Low: 'Low', Medium: 'Medium', High: 'High', Critical: 'Critical' },
    },
    // VentaHub's TicketListPage copy, sentence-cased.
    title: 'Tickets',
    subtitle: 'Take tickets from the pool, work them, and send them for review.',
    home: 'Home',
    dashboard: 'Dashboard',
    breadcrumb: 'Breadcrumb',
    sectionTabs: 'Ticket sections',
    // The section tab's count: what waits on the reader in that section.
    sectionWaiting_one: '{{count}} ticket waits on you',
    sectionWaiting_other: '{{count}} tickets wait on you',
    raise: 'Raise ticket',
    me: 'Me',
    system: 'System',
    cancel: 'Cancel',
    overdue: 'Past its ETA',
    refreshing: 'Refreshing tickets…',
    nearlyDue: 'Nearly at its ETA',
    age: '{{days}}d {{hours}}h',
    ageHint: 'Created {{created}}',
    ageHintEta_one: 'Created {{created}} · ETA {{count}} day',
    ageHintEta_other: 'Created {{created}} · ETA {{count}} days',
    percent: '{{value}}%',
    etaDaysShort_one: '{{count}} day',
    etaDaysShort_other: '{{count}} days',
    unreadOf: '{{unread}} new · {{total}}',
    reviewed: 'Ticket #{{number}} marked reviewed',
    noMatches: 'No tickets match these filters',
    noMatchesQuery: 'No tickets match “{{query}}”',
    noMatchesHint: 'Try a broader term, or clear a filter or the search.',
    emptyHint: 'Other queues are in the list beside this one.',
    sections: {
      business: { title: 'Business tickets' },
      ads: { title: 'Ads tickets' },
      approvals: { title: 'Approvals' },
    },
    // The Approvals tab: Data Upload Approvals (T16) as an inbox.
    approvals: {
      queues: 'Approval queues',
      views: { review_pending: 'Waiting', reviewed_all: 'Decided' },
      empty: {
        review_pending: 'Nothing is waiting for approval',
        reviewed_all: 'Nothing was decided in the last two weeks',
      },
      emptyHint: 'Uploads that need a decision appear here as they arrive.',
      pick: 'Pick an upload to check it',
      pickHint: 'Its changes, the file and the decision open here.',
      back: 'Back to approvals',
      title: 'Upload #{{number}}',
      number: '#{{number}}',
      uploadedBy: 'Uploaded by {{name}}',
      nowhere: 'No store or brand',
      openFull: 'Open full ticket',
      next: 'Next upload',
      steps: {
        label: 'How to work an approval',
        check: 'Check the upload',
        decide: 'Accept or reject',
        comment: 'Comment if needed',
        done: '(done)',
      },
    },
    // An administrator's preview of another role's list (ViewAsSwitch).
    viewAs: {
      label: 'View as',
      own: 'Admin (you)',
      reviewer: 'Reviewer',
      member: 'Team member',
      previewing: {
        reviewer:
          'Showing the queues and actions a reviewer gets. The tickets and every action are still yours.',
        member:
          'Showing the queues and actions a team member gets. The tickets and every action are still yours.',
      },
    },
    // The queue rail: one queue per API view, grouped by where a ticket is
    // in its life. One name per state — "Awaiting review" wherever a ticket
    // has been resolved and waits for a reviewer.
    rail: {
      label: 'Ticket queues',
      team: 'Team',
      mine: 'My work',
      raised: 'Raised by me',
      notifications: 'Notifications',
      // Under each group's name: whose tickets it holds.
      hints: {
        team: 'Everyone’s tickets, shared',
        mine: 'Assigned to you',
        raised: 'Created by you',
        notifications: 'Alerts sent to you',
      },
      allNotifications: 'All notifications',
      countLoading: 'Counting…',
      option: '{{queue}} ({{n}})',
      // The open queue's heading over the list: "My work · Awaiting review".
      current: '{{section}} · {{queue}}',
    },
    views: {
      unassigned: 'Unassigned',
      assigned: 'In progress',
      review_pending: 'To review',
      reviewed_all: 'Reviewed',
      auto_solved: 'Auto-approved',
      my_pending: 'Not started',
      my_inprogress: 'In progress',
      my_resolved: 'Awaiting review',
      my_reviewed: 'Reviewed',
      raised_pending_inprogress: 'Open',
      raised_open_to_review: 'Awaiting review',
      raised_reviewed: 'Reviewed',
      notifications: 'Notifications',
    },
    empty: {
      unassigned: 'You’re all caught up — nothing is waiting for an owner',
      assigned: 'No open tickets are assigned',
      review_pending: 'Nothing is waiting for review or approval',
      reviewed_all: 'No reviewed tickets in the last two weeks',
      auto_solved: 'Nothing was approved automatically',
      my_pending: 'You have nothing waiting to start',
      my_inprogress: 'You have nothing in progress',
      my_resolved: 'Nothing of yours is awaiting review',
      my_reviewed: 'None of your tickets were reviewed recently',
      raised_pending_inprogress: 'Nothing you raised is open',
      raised_open_to_review: 'Nothing you raised is waiting for review',
      raised_reviewed: 'Nothing you raised was reviewed recently',
      notifications: 'No notifications',
    },
    status: {
      PENDING: 'Not started',
      INPROGRESS: 'In progress',
      RESOLVED: 'Awaiting review',
      REVIEWED: 'Reviewed',
    },
    // A ticket's sub-type, keyed by its `data_type`: the kind of upload or
    // change a Data Upload Approval (T16) carries — VentaHub's
    // TICKET_SUB_TYPE_CONFIG labels — then the kind of change an AMS
    // Optimization v2 (T22) suggests, one per rule action.
    subTypes: {
      invoice_header_upload: 'Invoice Upload',
      adgroup_master_upload: 'AdGroup Master',
      product_data_upload: 'Product Data',
      product_data_change: 'Product Data Change',
      vendor_cost_change: 'Vendor Cost',
      retailer_cost_change: 'Retailer Cost',
      sku_master_upload: 'SKU Master',
      sku_cost_upload: 'SKU Cost',
      replen_focus_change: 'Replen-Focus',
      buying_cost_backfill: 'Cost Backfill',
      sku_dimensions_change: 'SKU Edit',
      otto_settlement_ingestion: 'Otto Settlement',
      purchase_order_upload: 'PO Upload',
      purchase_order_lines_upload: 'PO Lines',
      purchase_order_allotment_upload: 'PO Allotment',
      vendor_order_lines_upload: 'VO Lines',
      invoice_movement_upload: 'Invoice Movements',
      seller_flex_stock_upload: 'Seller Flex Count',
      retailer_receipt_upload: 'Retailer Receipts',
      vendor_payment_upload: 'Vendor Payments',
      sp_payment_upload: 'SP Payments',
      ams_bid_change: 'Bid Change',
      ams_target_pause: 'Pause Target',
      ams_ad_product_pause: 'Pause Ad Product',
      ams_ad_group_pause: 'Pause Ad Group',
      ams_new_target: 'New Target',
      ams_negative: 'Add Negative',
      ams_check: 'Performance Check',
      ams_adgroup_master: 'AdGroup Master Update',
      ams_mixed: 'Mixed Actions',
    },
    // What a T22 sub-type asks for, the rules that raise it, and the retired
    // ticket type it replaced — shown on hover or focus of its badge.
    subTypeHints: {
      ams_bid_change: 'Raise or lower the bid on a target. Raised by TR1, TR3, TR11, TR12 and DC1.',
      ams_target_pause:
        'Pause a target that spends without selling, or that another profile already runs. Raised by TR7 and TR10.',
      ams_ad_product_pause:
        'Pause an advertised product that is low on stock, undercut on price, poorly rated or off the buy box. Raised by APR4, APR5, APR6 and APR8. Replaces T12 AMS Product Check.',
      ams_ad_group_pause:
        'Pause an ad group whose spend jumped while its clicks did not. Raised by AGR1.',
      ams_new_target:
        'Add a search term, a competitor ASIN or an alternate product as a new target. Raised by AlPR1, APR7, STR1 and STR8.',
      ams_negative:
        'Block an irrelevant search term, or copy a negative to linked ad groups. Raised by NR1 and STR7.',
      ams_check:
        'Look into a target, search term or product whose ACOS, conversion or CTR is off — nothing changes on Amazon. Raised by TRX1, STRX1 and APR2. Replaces T11 Deviation of ACOS/Conversion/CTR.',
      ams_adgroup_master:
        "Update an ad group's target CTR and conversion to match Brand Analytics. Raised by AGR2.",
      ams_mixed:
        'Rules of more than one kind matched the same row. Decide each suggestion on its own.',
    },
    columns: {
      select: 'Select',
      pin: 'Pin',
      number: 'Ticket #',
      type: 'Type',
      subType: 'Sub-type',
      brand: 'Brand',
      marketplace: 'Marketplace',
      store: 'Store',
      items: '# Items',
      status: 'Status',
      owner: 'Owner',
      reviewer: 'Reviewer',
      creator: 'Creator',
      created: 'Date created',
      age: 'Age',
      updated: 'Last updated',
      timeline: 'Created → Assigned → Completed',
      people: 'Creator → Owner → Reviewer',
      assigned: 'Date assigned',
      completed: 'Date completed',
      progress: 'Progress',
      actions: 'Actions',
    },
    filters: {
      label: 'Filters',
      more: 'More filters',
      created: 'Created',
      createdRange: 'Created range',
      createdPresets: {
        all: 'All time',
        today: 'Today',
        '1d': 'Last 1 day',
        '3d': 'Last 3 days',
        '7d': 'Last 7 days',
        '30d': 'Last 30 days',
        '90d': 'Last 90 days',
        custom: 'Custom',
      },
      brand: 'Brand',
      marketplace: 'Marketplace',
      storeLabel: 'Store',
      clearCount_one: 'Clear filter',
      clearCount_other: 'Clear {{count}} filters',
      search: 'Search tickets',
      searchPlaceholder: 'Search tickets, ASINs, SKUs…',
      searchHint:
        'Space means AND, comma means OR: “asin1 asin2,asin3” finds tickets with both asin1 and asin2, or with asin3.',
      searchButton: 'Search',
      searchParsed: 'How the search is read',
      and: 'AND',
      or: 'OR',
      removeTerm: 'Remove “{{term}}” from the search',
      searchHelp: {
        open: 'How search works',
        title: 'Search syntax',
        searches:
          'Matches ticket numbers, types, brands, marketplaces, stores, people, statuses, the ticket’s contents and its comments.',
        spaceKey: 'space',
        space: 'AND — every word must match',
        comma: 'OR — either side may match',
        example: '(asin1 AND asin2) OR asin3 — tickets mentioning both asin1 and asin2, or asin3.',
      },
      clearSearch: 'Clear search',
      status: 'Status',
      type: 'Type',
      storeOption: '{{name}} ({{marketplace}})',
      sku: 'SKU / ASIN',
      skuPlaceholder: 'Contains…',
      creator: 'Creator',
      owner: 'Owner',
      picked: '{{label}} ({{count}})',
      ownedByMe: 'Owned by me',
      eta: 'ETA',
      etaAny: 'Any',
      overdue: 'Past ETA',
      onTime: 'Within ETA',
      pageSize: 'Rows',
      clear: 'Clear filters',
      clearAll: 'Clear all',
      sheetHint: 'Narrow the list. Changes apply as you pick.',
      showResults: 'Show results',
      store: 'Store #{{id}}',
      removeStore: 'Remove the store #{{id}} filter',
      // The chips under the bar: one per filter set.
      chip: '{{label}}: {{value}}',
      // The # Items, Progress and Ticket age column filters work on the rows
      // already loaded; the API has no such filters.
      chipPageOnly: '{{label}}: {{value}} (this page)',
      between: '{{min}}–{{max}}',
    },
    export: {
      label: 'Export CSV',
      ageDays: 'Age (days)',
      overdue: 'Past ETA',
      empty: 'No tickets to export',
    },
    row: {
      menu: 'Actions for ticket #{{number}}',
      take: 'Take ticket #{{number}}',
      takeShort: 'Take',
      takeOptions: 'More ways to assign ticket #{{number}}',
      assignTo: 'Assign to…',
      lockedOwn: 'You raised this ticket, so someone else takes it',
      unread: 'Unread',
      items_one: '{{n}} item',
      items_other: '{{n}} items',
      reassign: 'Reassign ticket #{{number}}',
      reassignShort: 'Reassign',
      review: 'Mark ticket #{{number}} reviewed',
      reviewShort: 'Mark reviewed',
      edit: 'Edit ticket #{{number}}',
      editShort: 'Edit',
      pin: 'Pin ticket #{{number}}',
      pinShort: 'Pinned to top',
      pinned: 'Pinned',
      unpin: 'Unpin ticket #{{number}}',
      markRead: 'Mark ticket #{{number}} read',
      markReadShort: 'Mark read',
      markUnread: 'Mark ticket #{{number}} unread',
      markUnreadShort: 'Mark unread',
      lockedPicked: '{{name}} has picked this up, so it can no longer be edited',
      pinnedDivider_one: 'Pinned · {{count}} ticket',
      pinnedDivider_other: 'Pinned · {{count}} tickets',
      restDivider: 'All tickets',
    },
    // The Timeline column: a ticket's dates as steps.
    flow: {
      label: 'Timeline',
      created: 'Created',
      assigned: 'Assigned',
      completed: 'Completed',
      notYet: 'Not yet',
    },
    // The People column: who a ticket has passed through.
    people: {
      label: 'People',
      creator: 'Creator',
      owner: 'Owner',
      reviewer: 'Reviewer',
      nobody: 'Nobody yet',
    },
    // The ticket page's next-step bar (NextStepBar): where the ticket stands,
    // and the one thing it asks of the reader now.
    next: {
      label: 'Next step',
      pool: 'Nobody has taken this yet.',
      poolOwn: 'You raised this ticket, so someone else takes it.',
      someoneElse: '{{name}} is working on it.',
      mineNotStarted: 'This ticket is yours. Start when you begin work on it.',
      mineInProgress: 'You’re working on this. Submit it for review when it’s done.',
      mineByItems_one: 'It completes when its item is decided — {{decided}} of {{count}} so far.',
      mineByItems_other:
        'It completes when every item is decided — {{decided}} of {{count}} so far.',
      reviewNow: '{{name}} submitted this for review.',
      awaitingReview: 'Waiting for a reviewer.',
      decideUpload: 'Check the upload, then accept or reject it below.',
      reviewed: 'Reviewed by {{name}} on {{date}}.',
      autoApproved: 'Approved automatically on {{date}}; nobody reviewed it by hand.',
      take: 'Take',
      taking: 'Taking…',
      start: 'Start work',
      starting: 'Starting…',
      submit: 'Submit for review',
      submitting: 'Submitting…',
      review: 'Mark reviewed',
      reviewing: 'Marking reviewed…',
      reopen: 'Reopen…',
    },
    // Reopening a ticket (ReopenDialog): the reason goes on the thread.
    reopen: {
      title: 'Reopen ticket #{{number}}?',
      description:
        'It goes back to Not started. Say why, so whoever picks it up knows what to change — the reason is posted as a comment.',
      reason: 'Why it needs more work',
      required: 'Say why it is being reopened.',
      confirm: 'Reopen ticket',
      reopening: 'Reopening…',
      comment: 'Reopened: {{reason}}',
    },
    // Taking a ticket from the pool: one click, undoable from the toast.
    take: {
      done: 'You took ticket #{{number}}',
      undo: 'Undo',
      undone: 'Ticket #{{number}} is back in the pool',
      openMine: 'My work',
    },
    bulk: {
      label: 'Selected tickets',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      select: 'Select ticket #{{number}}',
      selectAll: 'Select every ticket on this page',
      take_one: 'Take {{count}}',
      take_other: 'Take {{count}}',
      assign_one: 'Assign {{count}}…',
      assign_other: 'Assign {{count}}…',
      review_one: 'Mark {{count}} reviewed',
      review_other: 'Mark {{count}} reviewed',
      reviewTitle_one: 'Mark {{count}} ticket reviewed?',
      reviewTitle_other: 'Mark {{count}} tickets reviewed?',
      reviewDescription:
        'Reviewing closes each ticket. Open a ticket first if you have not checked its work.',
      markRead: 'Mark read',
      dismiss: 'Dismiss',
      clear: 'Clear selection',
      done_one: '{{count}} ticket updated',
      done_other: '{{count}} tickets updated',
      partial: '{{done}} updated, {{failed}} refused: {{numbers}}. Open them to see why.',
      andMore_one: '{{numbers}} and {{count}} more',
      andMore_other: '{{numbers}} and {{count}} more',
      refusedNumber: '#{{number}}',
    },
    assign: {
      title: 'Assign ticket #{{number}}',
      titleMany_one: 'Assign {{count}} ticket',
      titleMany_other: 'Assign {{count}} tickets',
      description: 'Pick who works it, or take it yourself.',
      restarts: 'Assigning moves a ticket that is in progress back to Not started.',
      selfOnly:
        'You can take it yourself. Assigning other people needs the tickets:edit permission.',
      person: 'Person',
      choose: 'Choose a person',
      toMe: 'Assign to me',
      confirm: 'Assign',
      assigning: 'Assigning…',
    },
    help: {
      open: 'Help & my permissions',
      title: 'Ticket dashboard help',
      intro:
        'The ticket dashboard is the hub for operational work: tickets assigned to you, tickets you raised and — if your permissions allow — tickets to assign and review for your team.',
      listTitle: 'Working with the list',
      tips: {
        tabs: 'Business tickets and Ads tickets are the two sections; each tab counts what waits on you there. Beside the list, the queues follow a ticket’s life: Team, My work, Raised by me and Notifications.',
        sorting:
          'Click a sortable column header to sort by it; click again to flip ascending and descending.',
        pinning:
          'The pin at the start of a row keeps that ticket at the top of the first page whatever the sorting. Pins are kept in this browser.',
        actions:
          'Open a ticket from its number. Take puts an unassigned ticket in My work at once, and Undo in the message puts it back. Reviewers can assign to someone else and mark tickets awaiting review as reviewed; creators can edit a ticket nobody has picked up.',
        columns:
          'Columns, above the list, switches columns on and off; the choice is part of the link.',
      },
      searchTitle: 'Searching and filtering',
      search:
        'Type a term and press Enter. It looks through ticket numbers, types, brands, stores, people and what the tickets contain.',
      searchExample: 'Space means AND, comma means OR. For example',
      searchExampleQuery: 'asin1 asin2,asin3',
      filters:
        'Store, type and owner sit beside the search. More filters holds marketplace, brand, created date, status and creator.',
      typesTitle: 'Ticket types in this section',
      code: 'Code',
      type: 'Ticket type',
      eta: 'Standard ETA',
      permissionsTitle: 'Your ticket permissions',
      canView: 'View and work tickets',
      canEdit: 'Assign, review and change ETAs (tickets:edit)',
      close: 'Got it',
    },
    detail: {
      title: '#{{number}} · {{type}}',
      aboutType: 'About this type',
      errorTitle: 'Couldn’t open this ticket',
      facts: 'Who and when',
      progress: 'Progress',
      itemsDecided_one: '{{decided}} of {{total}} item decided',
      itemsDecided_other: '{{decided}} of {{total}} items decided',
      moveBack: 'Move back to Not started',
      etaShort_one: 'ETA {{count}} day',
      etaShort_other: 'ETA {{count}} days',
      back: 'Back',
      backToList: 'Back to tickets',
      loading: 'Loading the ticket…',
      edit: 'Edit',
      description: 'Description',
      noTypeDescription: 'No description available.',
      about: 'About this ticket',
      overview: 'Overview',
      ownerLabel: 'Owner',
      parts: 'Ticket sections',
      tabs: { details: 'Details', conversation: 'Conversation', actions: 'Actions' },
      raised: 'Raised by {{who}} · {{date}}',
      created: 'Created',
      age: 'Ticket age',
      sidebar: 'Ticket actions',
      progressTitle: 'Progress & activity',
      progressLabel: 'Progress (%)',
      saveProgress: 'Save progress',
      progressHint: 'Percent complete, 0 to 100.',
      activity: 'Activity',
      eventBy: '{{event}} — {{who}}',
      events: {
        created: 'Created',
        assigned: 'Assigned',
        resolved: 'Submitted for review',
        reviewed: 'Reviewed',
      },
      ago: '{{age}} ago',
      statusChanged: 'Ticket #{{number}} is now {{status}}',
      progressSaved: 'Progress saved',
      unassignedDone: 'Ticket #{{number}} unassigned',
      review: {
        title: 'Mark ticket #{{number}} reviewed?',
        description:
          'Reviewing closes the ticket. It leaves the review queue and its owner’s open work.',
        confirm: 'Mark reviewed',
      },
      assigneeTitle: 'Assignee & status',
      assignedTo: 'Assigned to',
      you: '{{name}} (you)',
      someoneElse: 'Another user',
      assign: 'Assign',
      assigned: 'Ticket #{{number}} assigned',
      reassign: 'Reassign…',
      unassign: 'Unassign',
      unassigned: 'Nobody is assigned yet.',
      reviewerActions: 'Reviewer actions',
      markReviewed: 'Mark reviewed',
      reopen: 'Reopen ticket',
      details: 'Details',
      eta: 'ETA',
      noEta: 'No ETA',
      never: 'Not yet',
      notApplicable: 'N/A',
      warehouse: 'Warehouse',
    },
    items: {
      title_one: 'Ticket items ({{count}})',
      title_other: 'Ticket items ({{count}})',
      empty: 'No items',
      emptyHint: 'This ticket carries no item data.',
      export: 'Export CSV',
    },
    openOnAmazon: 'Open {{asin}} on Amazon (opens in a new tab)',
    itemScope: {
      title: 'Item details',
      profileIds: 'Profile ID',
      stores: 'Store',
      none: 'None given',
    },
    comments: {
      title: 'Comments',
      thread: 'Comment thread',
      empty: 'No comments yet.',
      add: 'Add a comment',
      placeholder: 'Add a comment… Type @ to mention a teammate.',
      mention: 'Mention a teammate',
      mentioned: 'Mentioned',
      mentionHint: 'Type @ to mention a teammate.',
      post: 'Post comment',
      posting: 'Posting…',
    },
    eta: {
      title: 'ETA for {{type}}',
      titleAny: 'Edit ticket type ETA',
      editTypes: 'Edit type ETA',
      description:
        'Days from creation a ticket of this type has before it is overdue. 0 means no SLA. This applies to every ticket of the type.',
      type: 'Ticket type',
      chooseType: 'Choose a ticket type',
      days: 'Number of days (ETA)',
      invalid: 'Whole days, from 0 to 3650.',
      change: 'Change',
      edit: 'Change the ETA of {{type}}',
      saved: 'ETA for {{type}} saved',
    },
    form: {
      required: 'Required',
      tooLong: 'At most {{max}} characters',
      raise: 'Raise ticket',
      save: 'Save',
      saving: 'Saving…',
    },
    create: {
      crumb: 'Create',
      title: 'Create a new ticket',
      description: 'Choose a type, fill in its form, and the ticket is raised in your name.',
      selectType: 'Select ticket type',
      type: 'Ticket type',
      chooseType: 'Choose a type',
      searchTypes: 'Search types by code or name',
      noTypeMatches: 'No type matches that search.',
      eta_one: 'Standard ETA {{count}} day',
      eta_other: 'Standard ETA {{count}} days',
      noEta: 'No standard ETA',
      details: 'Details for {{type}}',
      noTypes: 'No ticket types can be raised by hand yet.',
      nothingNew: 'Every item is already in an open ticket, so nothing new was raised.',
      done_one: 'Ticket #{{number}} raised',
      done_other: '{{count}} tickets raised',
      automaticTitle: 'Raised automatically',
      automaticHint:
        'These types are not raised by hand. A reviewer can run a type’s check now rather than wait for its schedule.',
      raisedBy: 'Raised by',
      source: {
        rulesEngine: 'Raised by the rules engine',
        upload: 'Raised when a data upload needs approval',
        labels: 'Raised by the shipping-label run',
        check: 'Raised automatically by its scheduled check',
        alert: 'Raised automatically as an alert',
        noForm: 'Its form is not in VentaWiz yet',
        job: 'Raised automatically by a scheduled job',
      },
      run: 'Run check now',
      runFor: 'Run the {{type}} check now',
      ran_one: '{{type}}: {{count}} ticket raised',
      ran_other: '{{type}}: {{count}} tickets raised',
      ranNothing: '{{type}}: nothing new to raise',
    },
    edit: {
      title: 'Edit ticket #{{number}}',
      description: 'You can edit a ticket you raised until somebody picks it up.',
      back: 'Back to the ticket',
      notEditable: 'This ticket cannot be edited',
      notEditableHint:
        'Only its creator can edit a ticket, and only before it is assigned, for types that have a form.',
      saved: 'Ticket saved',
    },
  },
  // T1 Catalogue Issues: the create form.
  ticketsT01: {
    marketplace: 'Marketplace',
    chooseMarketplace: 'Choose a marketplace',
    brand: 'Brand',
    chooseBrand: 'Choose a brand',
    description: 'Main description',
    descriptionHint: 'Anything the resolver should know. It is shown on every ticket raised.',
    sheet: 'Items',
    sheetHint:
      'Download the template, fill in one retailer SKU per row with an optional comment, and upload it. Check the rows below before raising.',
    downloadTemplate: 'Download template',
    uploadSheet: 'Upload sheet',
    reading: 'Reading…',
    noRows: 'Upload a sheet with at least one row.',
    summary_one: '{{count}} item will raise {{tickets}} ticket.',
    summary_other: '{{count}} items will raise {{tickets}} ticket(s).',
    skuOfRow: 'Retailer SKU, row {{row}}',
    commentsOfRow: 'Comments, row {{row}}',
    removeRow: 'Remove row {{row}} ({{sku}})',
    columns: {
      retailerSku: 'Retailer SKU',
      comments: 'Comments',
      actions: 'Actions',
    },
  },
  // T2 Buybox Issues: the items table.
  ticketsT02: {
    columns: {
      retailerSku: 'Retailer SKU',
      ourPrice: 'Our selling price',
      competitorPrice: 'Competitor price',
    },
  },
  // T4 Missing Product Data: the raiser's note above the items.
  ticketsT04: {
    note: 'What to fix',
  },
  // T17 Warning: Shipments Pending!: the countdown table.
  ticketsT17: {
    columns: {
      store: 'Store',
      order: 'Order',
      checked: 'Seen unshipped',
      shipBy: 'Ship by',
      remaining: 'Time left to ship',
    },
    countdown: '{{hours}}:{{minutes}}:{{seconds}}',
    pastDue: 'Past due',
  },
  // T15's own copy. Each ticket type keeps its strings in a top-level
  // `ticketsT<nn>` block, so ports of different types never edit the same lines.
  ticketsT15: {
    task: 'What needs doing',
    owner: 'Owner',
    ownerHint: 'The ticket is assigned to them as soon as it is raised.',
    ownerSelfOnly:
      'Assigned to you. Picking someone else needs permission to list your organization’s people.',
    me: '{{name}} (me)',
    ownerName: 'Owner name',
    ownerNameHint: 'How the owner is named on the ticket. Starts as the person picked.',
  },
  // T8 Technical Issues.
  ticketsT08: {
    details: 'Details',
    detailsHint:
      'Explain the issue in depth, including where you ran into it. You can paste a link to a Drive folder with screenshots of the error.',
    reported: 'Reported issue {{number}}',
    noComment: 'No comment was given for this issue.',
    context: {
      title: 'Report details',
      source: 'Source ticket',
      ticket: 'Ticket',
      ticketNumber: '#{{number}}',
      type: 'Type',
      statusThen: 'Status when reported',
      reporter: 'Reporter',
      name: 'Name',
      email: 'Email',
      userId: 'User ID',
      reportedAt: 'Reported at',
      classification: 'Classification',
      category: 'Category: {{value}}',
      severity: 'Severity: {{value}}',
      reproduction: 'Reproduction details',
      steps: 'Steps to reproduce',
      expected: 'Expected behaviour',
      actual: 'Actual behaviour',
      system: 'System information',
      browser: 'Browser',
      os: 'OS',
      viewport: 'Viewport',
      screen: 'Screen',
      platform: 'Platform',
      language: 'Language',
      userAgent: 'User agent',
      consoleErrors: 'Console errors',
    },
  },
  // T9 Marketing Request; `sheet` is shared with T10's form.
  ticketsT09: {
    sheet: {
      marketplace: 'Marketplace',
      marketplaceOptional: 'Marketplace (optional)',
      marketplaceOptionalHint: 'Leave it out to ask for these SKUs on every marketplace.',
      chooseMarketplace: 'Choose a marketplace',
      anyMarketplace: 'Any marketplace',
      noMarketplaces: 'Your organization has no marketplaces yet.',
      description: 'Main description',
      descriptionHint: 'Anything the resolver should know about the whole request.',
      title: 'SKUs',
      hint: 'Download the template, fill in retailer_sku (required) and comments, and upload it as .xlsx or .csv. You can correct or remove rows before raising.',
      template: 'Download template',
      upload: 'Upload sheet',
      reading: 'Reading the sheet…',
      readFailed: 'The sheet could not be read',
      templateFailed: 'The template could not be downloaded',
      noRows: 'Upload a sheet with at least one SKU.',
      tooManyRows: 'At most {{max}} SKUs in one request.',
      summaryTitle: 'What this raises',
      skuCount_one: '{{n}} SKU',
      skuCount_other: '{{n}} SKUs',
      summary_one:
        '{{skus}} will raise at most {{n}} ticket. SKUs already in an open ticket are skipped.',
      summary_other:
        '{{skus}} will raise at most {{n}} tickets. SKUs already in an open ticket are skipped.',
      preview_one: 'Preview ({{count}} row)',
      preview_other: 'Preview ({{count}} rows)',
      sku: 'Retailer SKU',
      comments: 'Comments',
      actions: 'Actions',
      skuFor: 'Retailer SKU, row {{row}}',
      commentsFor: 'Comments, row {{row}}',
      remove: 'Remove row {{row}} ({{sku}})',
      noSku: 'no SKU',
    },
  },
  // T10 Content Request.
  ticketsT10: {
    contentType: 'Requested content type',
    contentTypeRequired: 'Choose at least one.',
    driveLink: 'Drive link (optional)',
    driveLinkInvalid: 'Enter a full link starting with https://',
    requestedContent: 'Requested content type',
    noContentType: 'None given.',
    driveLinkTitle: 'Drive link',
    opensInNewTab: '(opens in a new tab)',
    noDriveLink: 'No Drive link was given.',
  },
  // T16 Data Upload Approval.
  ticketsT16: {
    noItem: {
      title: 'No upload on this ticket',
      description: 'This approval carries no upload data to review.',
    },
    metadata: { title: 'Upload details' },
    unattributed: {
      title_one: '{{count}} charge with no order — total {{total}}',
      title_other: '{{count}} charges with no order — total {{total}}',
      hint: 'These amounts are part of the payout, but Otto does not attach them to an order, so they are not staged as settlement rows. Review them here.',
      columns: {
        charge: 'Charge',
        type: 'Type',
        document: 'Document',
        date: 'Date',
        amount: 'Amount',
      },
    },
    replen: {
      title: 'Replen / focus changes',
      warehouse: 'Warehouse',
      empty: 'No changes recorded.',
      columns: { sku: 'SKU', name: 'Name', replen: 'Replen', focus: 'Focus' },
    },
    changes: {
      title: 'Proposed changes ({{fields}} across {{rows}})',
      titleCost: 'Proposed cost changes ({{fields}} across {{rows}})',
      titleBackfill_one: 'Costs to apply ({{count}} SKU)',
      titleBackfill_other: 'Costs to apply ({{count}} SKUs)',
      titleNewSkus_one: 'New SKUs ({{count}} row)',
      titleNewSkus_other: 'New SKUs ({{count}} rows)',
      fields_one: '{{count}} field',
      fields_other: '{{count}} fields',
      rows_one: '{{count}} row',
      rows_other: '{{count}} rows',
      context: 'What the change applies to',
      invoice: 'Invoice: {{value}}',
      warehouse: 'Warehouse: {{value}}',
      currency: 'Currency: {{value}}',
      tillDate: 'Till date: {{value}}',
      rowsToUpdate: 'Rows to update: {{value}}',
      rowCount: 'Rows: {{value}}',
      newCount: 'New: {{value}}',
      updatedCount: 'Updated: {{value}}',
      vendor: 'Vendor: {{value}}',
      retailer: 'Retailer: {{value}}',
      marketplace: 'Marketplace: {{value}}',
      store: 'Store: {{value}}',
      file: 'File: {{value}}',
      reportDate: 'Report date: {{value}}',
      scope: 'Scope: {{value}}',
      truncated:
        'Showing the first {{shown}} of {{total}} rows. Review the rest in the uploaded file before approving.',
      legendInsert:
        'Every row creates a new SKU — this upload cannot modify an existing one, so there are no previous values to compare.',
      legendDiff: 'Changed cells are highlighted — hover one to see its previous value.',
      expand: 'Expand columns',
      collapse: 'Collapse columns',
      empty: 'No field changes recorded.',
      sku: 'SKU',
      retailerProductCode: 'Retailer product code',
      adGroupId: 'Ad group ID',
      campaign: 'Campaign',
      newRow: 'New',
      previous: 'Previously',
      previousValue: 'Previous value: {{value}}',
      newNoPrevious: 'New — no previous value',
    },
    file: {
      title: 'File review & decision',
      name: 'File name',
      noFile: 'No file',
      download: 'Download',
      downloadStaged: 'Download staged rows',
      unavailable:
        'Uploaded files are not stored in VentaWiz yet, so the file cannot be downloaded here.',
    },
    review: {
      title: 'Review status',
      accept: 'Accept',
      accepting: 'Accepting…',
      reject: 'Reject',
      retryAccept: 'Retry accept',
      waiting: 'Waiting for review…',
      ownerCannot: 'Someone other than the ticket’s owner reviews this upload.',
      accepted: 'Accepted',
      rejected: 'Rejected',
      acceptNotApplied: 'Accept not applied',
      acceptNotAppliedHint:
        'The decision is recorded, but nothing was written. See the result below for why, then retry or reject.',
      writesNote:
        'Accepting writes these changes to the Data Vault now. If a row changed since it was proposed, nothing is written and this stays open. Rejecting is final and closes the ticket.',
      appliedToast: 'Approved and written to the Data Vault',
      failedToast: 'Nothing was written: some rows changed since this was proposed',
      notExecutedNote:
        'Accepting records your decision only. VentaWiz cannot yet write uploads into their tables, so an accepted upload stays open as not applied. Rejecting is final and closes the ticket.',
      acceptedToast:
        'Accept recorded. Nothing was written: the upload pipeline is not in VentaWiz yet.',
      rejectedToast: 'Upload rejected',
      results_one: 'Review result',
      results_other: 'Review results',
      status: {
        rejected: 'Rejected',
        error: 'Error',
        notExecuted: 'Not applied',
        applied_one: 'Applied · {{count}} row written',
        applied_other: 'Applied · {{count}} rows written',
        failed: 'Not written',
      },
    },
    rejectDialog: {
      title: 'Reject upload',
      description: 'Say why this data is being rejected. The uploader will see it.',
      reason: 'Rejection reason',
      cancel: 'Cancel',
      submit: 'Reject upload',
      submitting: 'Rejecting…',
    },
  },
  // T21 Shipping Labels.
  ticketsT21: {
    noItem: {
      title: 'No batch on this ticket',
      description: 'This ticket carries no label batch.',
    },
    offline: {
      title: 'Read-only in VentaWiz',
      otto: 'Downloading labels, retrying or regenerating them, uploading dimensions and sending tracking to Otto call DHL, S3 and Otto, which VentaWiz does not connect to yet. The batch is shown as the label run recorded it.',
      amazon_de:
        'Downloading labels, retrying them and confirming shipments on Amazon call DHL, S3 and Amazon, which VentaWiz does not connect to yet. The batch is shown as the label run recorded it.',
    },
    summary: {
      title: {
        otto: 'Shipping labels — {{batch}}',
        amazon_de: 'Amazon DE shipping labels — {{batch}}',
      },
      counts: 'Batch counts',
      copyAll: 'Copy all tracking',
      downloadZip: 'Download all (ZIP)',
      downloadCsv: 'Download CSV',
      success: '{{value}} success',
      failed: '{{value}} failed',
      pending: '{{value}} pending',
      total: 'Total: {{value}}',
    },
    tabs: {
      successful: 'Successful ({{value}})',
      failed: 'Failed ({{value}})',
    },
    actions: {
      label: 'Batch actions',
      sendToOtto: 'Send to Otto',
      confirmOnAmazon: 'Confirm on Amazon',
      regenerate: 'Regenerate',
      uploadDimensions: 'Upload dimensions',
      dimensionTemplate: 'Dimension template',
      retrySelected: 'Retry selected',
      downloadLabel: 'Download label for {{order}}',
      downloadReturnLabel: 'Download return label for {{order}}',
      voidAndRegenerate: 'Void & regenerate label for {{order}}',
      retryLabel: 'Retry label for {{order}}',
    },
    export: {
      orderItem: 'Order item',
      status: 'Label status',
      sku: 'SKU',
      asin: 'ASIN',
      product: 'Product',
      quantity: 'Quantity',
      returnStatus: 'Return label',
      labelVersion: 'Label version',
      errors: 'DHL errors',
    },
    template: {
      done_one: 'Template downloaded with {{count}} shipment',
      done_other: 'Template downloaded with {{count}} shipments',
    },
    columns: {
      details: 'Details',
      order: 'Order',
      product: { otto: 'SKU / product', amazon_de: 'SKU / ASIN' },
      tracking: 'Tracking number',
      returnTracking: 'Return tracking',
      status: { otto: 'Otto status', amazon_de: 'Amazon confirm' },
      dimensions: 'Dimensions',
      girth: 'Girth',
      label: 'Label',
      actions: 'Actions',
    },
    status: {
      pending: 'Pending',
      otto: {
        done: 'Notified',
        failed: 'Failed',
        stale: 'Old number',
        staleHint:
          'The label was regenerated. Otto still has the old, cancelled tracking number until the new one is sent.',
      },
      amazon_de: { done: 'Confirmed', failed: 'Unconfirmed' },
    },
    successful: {
      empty: 'No successful labels',
      details: 'Show package details for {{order}}',
    },
    failed: {
      empty: 'No failed labels',
      details: 'Show recipient and package for {{order}}',
      quantity: 'qty {{value}}',
      retries_one: '{{count}} retry',
      retries_other: '{{count}} retries',
    },
    groupedItems_one: '{{count}} item',
    groupedItems_other: '{{count}} items',
    returnOk: 'Return label printed',
    returnFailed: 'Return label failed',
    label: {
      version: 'v{{version}}',
      reissued: 'Label has been reissued',
      superseded: 'Superseded: {{numbers}}',
    },
    regenFailed: {
      title: 'No valid label',
      description:
        'The old DHL label was voided but the replacement failed. This parcel cannot ship until a new label is issued.',
    },
    copy: {
      one: 'Tracking number copied',
      many_one: '{{count}} tracking number copied',
      many_other: '{{count}} tracking numbers copied',
      none: 'No tracking numbers available',
      failed: 'Could not copy',
      tracking: 'Copy tracking number for {{order}}',
      returnTracking: 'Copy return tracking number for {{order}}',
    },
    package: {
      title: 'Package',
      recipient: 'Recipient',
      box: '{{l}} × {{w}} × {{h}} cm',
      weight: '{{kg}} kg',
      cm: '{{value}} cm',
      weight_kg: 'Weight (kg)',
      length_cm: 'Length (cm)',
      width_cm: 'Width (cm)',
      height_cm: 'Height (cm)',
      max: 'Over DHL’s maximum of {{limit}}',
      min: 'Under DHL’s minimum of {{limit}}',
      girth: 'Girth: {{value}} cm (max {{limit}} cm)',
      girthOver: 'Girth: {{value}} cm (exceeds max {{limit}} cm)',
      overLimit: 'Over DHL limits',
    },
    recipient: {
      name1: 'Name',
      street: 'Street',
      houseNumber: 'House number',
      additionalInfo: 'Additional info',
      postalCode: 'Postal code',
      city: 'City',
      country: 'Country',
      email: 'Email',
    },
  },
  // VentaHub's "Analyze focused SKUs" ops sheet. T is the period being
  // viewed, T-1 the one before; {{period}} is "week" or "month".
  focusedSkus: {
    manage: {
      title: 'Manage focused SKUs',
      description: 'Pick which SKUs to track competitor pricing and ad performance for.',
      crumb: 'Manage',
      tabsLabel: 'Focused listings or listings to focus',
      tabs: { list: 'Focused', add: 'Not focused' },
      allWarehouses: 'All warehouses',
      allStores: 'All stores',
      brands: 'Brands',
      allBrands: 'All brands',
      bulkUpload: 'Bulk upload',
      searchList: 'Search focused SKUs by ASIN, SKU, seller SKU or title',
      searchListings: 'Search SKUs to focus by ASIN, SKU, seller SKU or title',
      selectPage: 'Select every row on this page',
      selectRow: 'Select {{name}}',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      clearSelection: 'Clear selection',
      approvalNote: 'Changes go for approval and apply once approved.',
      remove: 'Unfocus',
      removeCount_one: 'Unfocus {{count}}',
      removeCount_other: 'Unfocus {{count}}',
      add: 'Focus',
      removeOne: 'Unfocus {{name}}',
      focusOne: 'Focus {{name}}',
      findToFocus: 'Find SKUs to focus',
      openTicket: 'Open ticket',
      pendingFocus: 'Focus awaiting approval · #{{number}}',
      pendingRemove: 'Unfocus awaiting approval · #{{number}}',
      pendingHint: 'Sent for approval in this visit. Opens the ticket.',
      help: {
        button: 'How it works',
        title: 'Focusing SKUs',
        steps: {
          scope:
            'Pick a warehouse, then one of its stores. A SKU is focused per store: Focused shows what is focused in that store; Not focused shows the rest of its listings.',
          change:
            'Use Focus or Unfocus on a row, or tick several rows and use the bar that appears.',
          approve:
            'Each change raises an approval ticket. The products change once a reviewer approves it; until then the row shows the ticket it waits on.',
          upload:
            'For many SKUs at once, use Bulk upload: a sheet of SKU and Focus (TRUE or FALSE).',
        },
      },
      cards: {
        focused: 'Focused',
        focusedHint: 'Listings focused in this scope.',
        notFocused: 'Not focused',
        notFocusedHint: 'Listings in this scope not yet focused.',
        share: 'Share focused',
        shareHint: 'Of all listings in this scope.',
        awaiting: 'Awaiting approval',
        awaitingHint: 'Sent from this page, not yet approved.',
      },
      addCount_one: 'Focus {{count}}',
      addCount_other: 'Focus {{count}}',
      sending: 'Sending…',
      cancel: 'Cancel',
      focusSent_one: 'Sent {{count}} SKU to be focused — {{tickets}}',
      focusSent_other: 'Sent {{count}} SKUs to be focused — {{tickets}}',
      unfocusSent_one: 'Sent {{count}} SKU to be un-focused — {{tickets}}',
      unfocusSent_other: 'Sent {{count}} SKUs to be un-focused — {{tickets}}',
      ticketList_one: 'approval ticket {{list}}',
      ticketList_other: 'approval tickets {{list}}',
      sendFailed: 'Could not send the change for approval.',
      confirmRemoveTitle_one: 'Un-focus {{count}} SKU?',
      confirmRemoveTitle_other: 'Un-focus {{count}} SKUs?',
      confirmRemoveDescription:
        'This raises an approval ticket. Once approved, they stop being focused for tickets and the analysis sheet; their DRR is kept in the history.',
      export: 'Export',
      rowsPerPage: 'Rows',
      drrHint: 'Planned units a day, set by the DRR sheet.',
      trendHint:
        'Units sold in each of the last 8 completed weeks, oldest first. Green when the recent four weeks sold more than the four before, red when less.',
      trendLabel: 'Units sold per week, oldest first: {{values}}',
      stockHint:
        'Warehouse stock plus what the marketplace holds, now. Unknown when neither has been read.',
      stockIn: 'In stock',
      stockOut: 'Out of stock',
      stockUnknown: 'Unknown',
      pickFilterTitle: 'Choose a warehouse and a store',
      pickFilterDescription:
        'A SKU is focused per store. Pick a warehouse, then one of its stores, to load that store’s focused SKUs.',
      pickFilterAddDescription:
        'A SKU is focused per store. Pick a warehouse, then one of its stores, to find its listings to focus.',
      pickWarehouseFirst: 'Choose a warehouse first',
      pickStore: 'Choose a store',
      priority: 'Priority {{priority}}',
      focusAppliesTo: 'Focus and Unfocus apply to {{store}} only.',
      noMatchesTitle: 'No focused SKUs here',
      noMatchesDescription:
        'Nothing in this scope is focused yet. Try another search, or add some.',
      noListingsTitle: 'No listings to focus',
      noListingsDescription: 'Every listing here is focused already, or none match the search.',
      columns: {
        select: 'Select',
        asin: 'ASIN',
        sku: 'SKU',
        title: 'Title',
        store: 'Store',
        brand: 'Brand',
        status: 'Status',
        drr: 'DRR',
        trend: 'Trend',
        stock: 'Stock',
        action: 'Action',
      },
      upload: {
        title: 'Bulk upload focused SKUs',
        description:
          'A sheet of SKU and Focus: TRUE focuses the SKU, FALSE un-focuses it. Every listing of the SKU in the store takes the flag. Check the file, then save.',
      },
    },
    analysis: {
      title: 'Analyze focused SKUs',
      description:
        'Performance trends, competitor positions, and issue signals across your focused SKUs.',
      crumb: 'Analysis',
      warehouse: 'Warehouse',
      store: 'Store',
      allStores: 'All stores',
      brands: 'Brands',
      go: 'Go',
      upToDate: 'Up to date',
      weekly: 'Weekly',
      monthly: 'Monthly',
      weeklyHint: 'Weekly goal tracker: T = the completed week, T-1 = the week before',
      monthlyHint: 'Monthly goal tracker: T = the completed month, T-1 = the month before',
      grainLabel: 'Focus SKU sheet period',
      week: 'Week',
      month: 'Month',
      periodWord: { weekly: 'week', monthly: 'month' },
      search: 'Search {{sku}}, SKU or title',
      readOnly: 'Read-only — past {{period}}',
      frozenOn: 'Frozen {{date}}',
      frozenHint:
        'This {{period}} was frozen when it ended: its figures, stock and price included, are as they were then.',
      clearFilters_one: 'Clear {{count}} filter',
      clearFilters_other: 'Clear {{count}} filters',
      clearFiltersHint: 'Remove every column filter',
      pin: 'Pin columns',
      export: 'Excel',
      exportHint:
        'Download every row of this sheet, as filtered and sorted, as a CSV that opens in Excel',
      exportDirty: 'Save or discard your edits before exporting',
      discard: 'Discard',
      discardEditsTitle: 'Discard unsaved edits?',
      discardEditsDescription_one:
        'You have {{count}} unsaved note on this sheet. Changing the scope drops it.',
      discardEditsDescription_other:
        'You have {{count}} unsaved notes on this sheet. Changing the scope drops them.',
      discardEditsConfirm: 'Discard and continue',
      save: 'Save',
      saveCount: 'Save ({{count}})',
      saving: 'Saving…',
      saved_one: 'Saved {{count}} focused SKU row',
      saved_other: 'Saved {{count}} focused SKU rows',
      saveFailed: 'Could not save the sheet changes.',
      stale:
        'Someone else changed these notes. The sheet has been reloaded; enter your changes again.',
      noValue: 'None',
      choose: 'Select…',
      write: 'Add…',
      unassigned: 'Unassigned',
      notAvailable: 'N/A',
      yes: 'Yes',
      no: 'No',
      unknown: 'Unknown',
      total: 'Total',
      lockedDrr: 'Locked: this SKU met its DRR plan, so there is no miss to explain.',
      editNote: 'Edit {{field}} for {{sku}}',
      lockedStock:
        'Locked: cover is between 60 and 120 days, so there is no stock issue to report.',
      pickWarehouseTitle: 'Please Select a Warehouse',
      pickWarehouseDescription:
        'Select a warehouse from the dropdown above to choose a linked store',
      pickStoreTitle: 'Please Select a Store',
      pickStoreDescription: 'Select a store linked to the chosen warehouse to view SKU data',
      emptyTitle: 'No focused SKUs found',
      emptyDescription:
        'Add listings on Manage Focused SKUs and they appear here after the next data build.',
      noListTitle: 'Your focused-SKU list is empty',
      noListDescription:
        'Add listings on Manage Focused SKUs to choose a warehouse and store here.',
      filter: {
        search: 'Search values',
        searchLabel: 'Search the values of {{column}}',
        selectAll: 'Select all',
        blank: '(Blanks)',
        noValues: 'No values match.',
        more: 'Showing {{shown}} of {{total}} values. Search to narrow the list.',
        clear: 'Clear',
        cancel: 'Cancel',
        apply: 'Apply',
      },
      pinMenu: 'Columns frozen while scrolling sideways',
      pinCount: 'Pin columns ({{count}})',
      viewLabel: 'View',
      viewSheet: 'Sheet',
      viewSheetHint: 'Every focused SKU for one period',
      viewHistory: 'Snapshot history',
      viewHistoryHint: 'One SKU across every period',
      pastNotes: 'History of {{sku}}: every period, with its notes',
      history: {
        sku: 'SKU',
        pickSku: 'Choose a SKU…',
        pickTitle: 'Choose a SKU',
        pickDescription:
          'Pick one of the focused SKUs in this scope to see it across every period, newest first, with the reasons and actions written against each.',
        emptyTitle: 'Not on any sheet',
        emptyDescription: 'This SKU was not focused in this scope in any period offered.',
        readOnly: 'Read-only: notes are edited on the sheet, for the latest period.',
        columns: {
          period: 'Period',
          totalUnits: 'Total Units',
          sellerUnits: 'Seller Units',
          salesValue: 'Sales Value',
          drrPlanned: 'DRR Planned',
          sales90d: '3M Sales',
          warehouseStock: 'Warehouse Stock',
          fbaStock: 'FBA Stock',
          stock: 'Stock',
          intransit: 'In Transit',
          coverDays: 'Cover (days)',
          price: 'Price',
          adClicks: 'Ad Clicks',
          adSpend: 'Ad Spend',
          adSales: 'Ad Sales',
          acos: 'ACOS',
          orderedUnits: 'Ordered Units',
          glanceViews: 'Glance Views',
          conversion: 'Conversion',
          drrMissReason: 'DRR Miss Reason',
          action: 'Action',
          stockIssue: 'Stock Issue',
          stockIssueAction: 'Stock Issue Action',
          editedBy: 'Last Edited By',
        },
      },
      groups: {
        identity: 'Identity',
        details: 'Details',
        salesHistory: 'Sales History',
        stock: 'Stock',
        salesUnits: 'Sales Units',
        drr: 'DRR',
        financial: 'Financial',
        opsAction: 'Ops Action',
        marketing: 'Marketing',
        secondary: 'Secondary Data',
        listing: 'Listing',
      },
      skuTypes: {
        asin: 'ASIN',
        ean: 'EAN',
        zsku: 'ZSKU',
        barcode: 'Barcode',
        item_number: 'Item Number',
        product_id: 'Product ID',
      },
      columns: {
        retailer_sku: 'ASIN',
        title: 'Title',
        sku_code: 'SKU Code (OC)',
        assigned_users: 'Assigned User',
        a_plus_updated: 'A+ Updated',
        parent_asin: 'Parent ASIN',
        sales_90d: '3M Sales',
        replen_status: 'Replen',
        warehouse_stock: 'Warehouse Stock',
        fba_stock: 'FBA Stock',
        secondary_stock: 'Secondary Stock',
        stock: 'Stock',
        unsellable: 'Unsellable',
        cover_days: 'Cover (days)',
        intransit_quantity: 'Intransit',
        intransit_eta_days: 'Shipment ETA (days)',
        stock_issue: 'Stock Issue',
        stock_issue_action: 'Stock Issue Action',
        seller_units_t1: 'Seller Units (T-1)',
        seller_units: 'Seller Units (T)',
        other_seller_units_t1: 'Other Seller Units (T-1)',
        other_seller_units: 'Other Seller Units (T)',
        df_units_t1: 'DF Units (T-1)',
        df_units: 'DF Units (T)',
        of_units_t1: 'OF Units (T-1)',
        of_units: 'OF Units (T)',
        vo_units_t1: 'VO INV (T-1)',
        vo_units: 'VO INV (T)',
        po_units_t1: 'PO Units (T-1)',
        po_units: 'PO Units (T)',
        total_units_t1: 'Total Units (T-1)',
        total_units: 'Total Units (T)',
        drr_planned: 'DRR Planned',
        sales_value: 'Sales Value',
        drr_miss_reason: 'DRR Miss Reason',
        action: 'Action',
        ams_clicks_t1: 'AMS Clicks (T-1)',
        ams_clicks: 'AMS Clicks (T)',
        ams_spend_t1: 'AMS Spend (T-1)',
        ams_spend: 'AMS Spend (T)',
        ams_acos_t1: 'ACOS (T-1)',
        ams_acos: 'ACOS (T)',
        secondary_ordered_units_t1: 'Sec. Ordered Units (T-1)',
        secondary_ordered_units: 'Sec. Ordered Units (T)',
        secondary_ordered_revenue_t1: 'Sec. Ordered Rev (T-1)',
        secondary_ordered_revenue: 'Sec. Ordered Rev (T)',
        secondary_customer_returns_t1: 'Sec. Cust. Returns (T-1)',
        secondary_customer_returns: 'Sec. Cust. Returns (T)',
        secondary_glance_views_t1: 'Glance Views (T-1)',
        secondary_glance_views: 'Glance Views (T)',
        secondary_unit_session_pct_t1: 'Conversion % (T-1)',
        secondary_unit_session_pct: 'Conversion % (T)',
        competitor_asin: 'Comp. Link (ASIN)',
        competitor_mrr: 'Comp. MRR',
        competitor_price: 'Comp. Price',
        current_price: 'Current Price',
        buy_box: 'Buy Box',
        delivery_promise_days: 'Delivery Promise (days)',
        rating: 'Rating',
        ranking_t1: 'Ranking (T-1)',
        ranking: 'Ranking',
        ranking_as_of: 'Rank As Of',
        critical_reviews: 'Critical Reviews (7d)',
        form_updates_30d: 'Form Updates (30d)',
      },
      hints: {
        retailer_sku:
          'Marketplace identifier of the listing: the lowest-numbered of your listings of this SKU.',
        title: 'Listing title.',
        sku_code:
          'Internal SKU this listing maps to. One row per internal SKU — several listings of the same SKU are merged.',
        assigned_users: 'Everyone whose focused-SKU list holds this SKU in these stores.',
        a_plus_updated: 'Whether the listing has A+ content. Not loaded into VentaWiz yet.',
        parent_asin: 'Parent of the variation family, from Amazon’s traffic report.',
        sales_90d: 'Total units over the 90 days ending on the last day of this {{period}}.',
        replen_status:
          'Replen class at this warehouse: R = replenishment, N = non-replenishment, T = temporarily replenish.',
        warehouse_stock:
          'Available units in the selected warehouse. Shown on the latest {{period}} only, as it is now.',
        fba_stock:
          'Sellable units marketplaces hold, each product once. Seller Flex is kept out — the counted Seller Flex units, or the -SF listings where nothing was counted — as it is already in warehouse stock.',
        secondary_stock:
          'Sellable units Amazon holds on vendor (1P) accounts, from its weekly inventory report. Counts towards Stock.',
        stock: 'Warehouse stock + FBA stock + Secondary Stock. Unsellable units are excluded.',
        unsellable:
          'Damaged or defective units marketplaces hold. Not counted in Stock or cover. Blank when none was reported, which is not a reported zero.',
        cover_days:
          'How long Stock lasts at the T and T-1 rate of Total and Other Seller Units. In-transit units are not counted. Blank when neither sold anything.',
        intransit_quantity: 'Units shipped towards the selected warehouse but not yet received.',
        intransit_eta_days: 'Days until the earliest expected delivery still ahead.',
        stock_issue:
          'What is wrong with this SKU’s stock — independent of the DRR miss. Locked while cover is between 60 and 120 days. Editable on the latest finished {{period}} only.',
        stock_issue_action:
          'Follow-up agreed for the stock issue. Locked on the same healthy-cover rows as Stock Issue.',
        seller_units_t1: 'Marketplace order units in the previous {{period}}.',
        seller_units: 'Marketplace order units in the {{period}} being viewed.',
        other_seller_units_t1:
          'Units the warehouse’s Shopify and TikTok shops sold in the previous {{period}}. Counted in cover, not in Total. Blank where it does not apply.',
        other_seller_units:
          'Units the warehouse’s Shopify and TikTok shops sold in the {{period}} being viewed. Counted in cover, not in Total. Blank where it does not apply.',
        df_units_t1: 'Direct-fulfilment invoice units in the previous {{period}}.',
        df_units: 'Direct-fulfilment invoice units in the {{period}} being viewed.',
        of_units_t1: 'Offline invoice units in the previous {{period}}.',
        of_units: 'Offline invoice units in the {{period}} being viewed.',
        vo_units_t1: 'Vendor-order invoice units in the previous {{period}}.',
        vo_units: 'Vendor-order invoice units in the {{period}} being viewed.',
        po_units_t1:
          'Retailer purchase-order units in the previous {{period}}. Never counted in Total.',
        po_units:
          'Retailer purchase-order units in the {{period}} being viewed. Never counted in Total.',
        total_units_t1: 'Seller + DF + OF + VO for the previous {{period}}. PO is excluded.',
        total_units: 'Seller + DF + OF + VO for the {{period}} being viewed. PO is excluded.',
        drr_planned:
          'Planned units a day: the lowest listing plan per store, summed across stores.',
        sales_value: 'Sales value for the {{period}} being viewed, per currency.',
        drr_miss_reason:
          'Why the planned DRR was missed. Locked when every planned store’s Total — or its Amazon sell-out — reached the plan × {{days}} days. Editable on the latest finished {{period}} only.',
        action:
          'Follow-up agreed for this SKU. Required when the reason is “Other”. Editable on the latest finished {{period}} only.',
        ams_clicks_t1: 'Sponsored-ads clicks in the previous {{period}}.',
        ams_clicks: 'Sponsored-ads clicks in the {{period}} being viewed.',
        ams_spend_t1: 'Sponsored-ads spend in the previous {{period}}, per currency.',
        ams_spend: 'Sponsored-ads spend in the {{period}} being viewed, per currency.',
        ams_acos_t1:
          'Ad spend as a share of ad sales in the previous {{period}}. Blank without ad sales or across currencies. Lower is better.',
        ams_acos:
          'Ad spend as a share of ad sales in the {{period}} being viewed. Blank without ad sales or across currencies. Lower is better.',
        secondary_ordered_units_t1:
          'Amazon’s vendor sales report: units customers ordered in the previous {{period}}. Blank = Amazon did not report.',
        secondary_ordered_units:
          'Amazon’s vendor sales report: units customers ordered in the {{period}} being viewed. Blank until Amazon reports it.',
        secondary_ordered_revenue_t1:
          'Amazon’s vendor sales report: ordered revenue in the previous {{period}}.',
        secondary_ordered_revenue:
          'Amazon’s vendor sales report: ordered revenue in the {{period}} being viewed.',
        secondary_customer_returns_t1:
          'Amazon’s vendor sales report: customer returns in the previous {{period}}.',
        secondary_customer_returns:
          'Amazon’s vendor sales report: customer returns in the {{period}} being viewed.',
        secondary_glance_views_t1:
          'Amazon’s traffic report: detail-page views in the previous {{period}}.',
        secondary_glance_views:
          'Amazon’s traffic report: detail-page views in the {{period}} being viewed.',
        secondary_unit_session_pct_t1:
          'Units ordered ÷ sessions in the previous {{period}}, as Amazon reports them — can exceed 100%.',
        secondary_unit_session_pct:
          'Units ordered ÷ sessions in the {{period}} being viewed, as Amazon reports them — can exceed 100%.',
        competitor_asin:
          'Competing listing tracked against this SKU. Not loaded into VentaWiz yet.',
        competitor_mrr: 'Competitor’s recommended retail price. Not loaded into VentaWiz yet.',
        competitor_price: 'Competitor’s current selling price. Not loaded into VentaWiz yet.',
        current_price: 'The lowest price among your listings of this SKU, per currency.',
        buy_box: 'Whether we hold the buy box. Not loaded into VentaWiz yet.',
        delivery_promise_days:
          'Delivery time promised on the listing. Not loaded into VentaWiz yet.',
        rating: 'Average customer rating out of 5. Not loaded into VentaWiz yet.',
        ranking_t1: 'Best-seller rank in the previous {{period}}. Not loaded into VentaWiz yet.',
        ranking: 'Best-seller rank. Not loaded into VentaWiz yet.',
        ranking_as_of: 'Date the ranking was read from Amazon. Not loaded into VentaWiz yet.',
        critical_reviews:
          'One- and two-star reviews in the last seven days. Not loaded into VentaWiz yet.',
        form_updates_30d:
          'Insert SKU Data submissions for this SKU in the last 30 days. Not loaded into VentaWiz yet.',
      },
    },
  },
  table: {
    // A grid's edit mode (DataGrid's `edit`): the View | Edit switch, the
    // unsaved count, Save, the editable cells and the deletion a row asks for.
    edit: {
      view: 'View',
      edit: 'Edit',
      editing: 'Editing',
      label: 'Mode',
      unsaved_one: '{{count}} unsaved',
      unsaved_other: '{{count}} unsaved',
      discard: 'Discard',
      save_one: 'Save {{count}}',
      save_other: 'Save {{count}}',
      saving: 'Saving…',
      editCell: 'Edit {{column}} of {{id}}',
      deleteRow: 'Request deletion of {{id}}',
      deleteTitle: 'Request deletion',
      deleteDescription:
        'Row {{id}} will be submitted for deletion. Nothing changes until the request is approved.',
      deleteConfirm: 'Request deletion',
      actionsColumn: 'Actions',
      outOfRange: 'Enter a number from 0 to {{max}}',
      fixInvalid: 'Fix the values marked in red first',
      // The spreadsheet keys: paste a block, fill down, clear and copy a range.
      sheetHint:
        'Paste a block from Excel or Sheets into a cell. Shift+↑/↓ selects a range; Ctrl+D fills it down, Delete clears it, Ctrl+C copies it.',
      pasted_one: 'Pasted {{count}} cell',
      pasted_other: 'Pasted {{count}} cells',
      pasteSkipped_one: '{{count}} cell fell outside the editable cells and was left out.',
      pasteSkipped_other: '{{count}} cells fell outside the editable cells and were left out.',
      copied_one: 'Copied {{count}} cell',
      copied_other: 'Copied {{count}} cells',
      selectedCell: 'Selected',
      noChoice: 'None',
      searchChoices: 'Search {{column}}',
      noChoiceMatches: 'Nothing matches',
    },
    columns: 'Columns',
    showColumns: 'Show columns',
    filter: {
      open: 'Filter {{label}}',
      active: 'Filter {{label}} ({{count}})',
      title: 'Filter {{label}}',
      clearAll: 'Clear column filters',
      clear: 'Clear',
      apply: 'Apply',
      condition: 'Condition',
      value: 'Value',
      min: 'Min',
      max: 'Max',
      contains: 'Contains',
      options: 'Options',
      op: {
        eq: 'Equals',
        ne: 'Does not equal',
        gt: 'Greater than',
        gte: 'At least',
        lt: 'Less than',
        lte: 'At most',
        between: 'Between',
      },
      noMatches: 'No rows match the column filters.',
    },
    rowAction: 'Open {{id}}',
    facet: {
      search: 'Search {{label}}…',
      list: '{{label}} values',
      selectAll: 'Select all values',
      selectMatching: 'Select all matching “{{search}}”',
      values_one: '{{formatted}} value',
      values_other: '{{formatted}} values',
      showing: 'Showing {{shown}} of {{total}} matching values',
      scrollMore: 'scroll to load more',
      ticked_one: '{{formatted}} selected · kept across pages and searches',
      ticked_other: '{{formatted}} selected · kept across pages and searches',
      none: 'No values match.',
      failed: "Couldn't load the values. Close and try again.",
      blank: '(Blank)',
    },
    toolbar: {
      search: 'Search…',
      searchPage: 'Search this page…',
      searchLabel: 'Search the table',
      filters: 'Filters',
      filterBy: 'Filter by column',
      sort: 'Sort',
      sortBy: 'Sort by',
      sortedBy: 'Sorted by {{label}}, {{direction}}',
      asc: 'Ascending',
      desc: 'Descending',
      clearSort: 'Clear sort',
      emptyTitle: 'Nothing here yet',
      emptyHint: 'There are no rows to show.',
      chip: {
        contains: '{{label}} contains “{{value}}”',
        in: '{{label}}: {{values}}',
        between: '{{label}} {{min}}–{{max}}',
        except: '{{label}}: all but {{values}}',
        containsExcept: '{{label}} contains “{{value}}”, but not {{values}}',
      },
    },
  },
  aiListing: {
    title: 'AI Listing',
    description: 'Create an Amazon listing — new photos and text — from any product you sell.',
    // Each step of the run, named for what the seller is waiting on.
    status: {
      pending: 'Waiting to start',
      analyzing: 'Reading your listing',
      crafting_prompts: 'Planning',
      generating_text: 'Writing the text',
      generating_lifestyle: 'Making lifestyle photos',
      generating_aplus: 'Making product photos',
      generating_infographics: 'Making infographics',
      validating: 'Checking the photos',
      completed: 'Ready',
      failed: 'Failed',
    },
    // The kitchen shown while a listing is being made.
    cooking: {
      eyebrow: 'In the kitchen',
      step: {
        pending: 'Warming up the kitchen',
        analyzing: 'Reading the recipe — your Amazon listing',
        crafting_prompts: 'Picking the ingredients',
        generating_text: 'Stirring in your title and bullet points',
        generating_lifestyle: 'Simmering your lifestyle photos',
        generating_aplus: 'Plating your product close-ups',
        generating_infographics: 'Adding the infographic garnish',
        validating: 'Taste test — checking Amazon’s photo rules',
      },
      readyIn: 'Ready in about',
      etaLabel: 'Time left',
      etaSr_one: 'About {{count}} minute left',
      etaSr_other: 'About {{count}} minutes left',
      almost: 'Almost ready…',
      overdue: 'Taking a little longer than usual — still cooking',
      courses: 'On the menu',
      course: {
        text: 'Listing text',
        lifestyle: 'Lifestyle photos',
        product: 'Product photos',
        infographic: 'Infographics',
      },
      courseState: {
        done: 'Ready',
        cooking: 'Cooking',
        queued: 'Up next',
      },
      tips: {
        leave:
          'You can close this page — the listing keeps cooking and will be here when you come back.',
        product:
          'Your real product stays exactly as it is in every photo; only the scene around it is new.',
        choose:
          'When it is ready, tick the photos you like and switch any text back to the original.',
        redo: 'Not happy with a photo? Ask for a new version and describe the scene you want.',
      },
    },
    stepHint: {
      pending: 'It starts in a moment',
      analyzing: 'Looking at your product as Amazon shows it',
      crafting_prompts: 'Deciding what to write and which photos to make',
      generating_text: 'Title, bullet points and description',
      generating_lifestyle: 'Your product in real scenes',
      generating_aplus: 'Close-up shots of your product',
      generating_infographics: 'Your product with its key features',
      validating: 'Making sure the photos meet Amazon’s rules',
      completed: 'Ready to review',
      failed: 'Stopped',
    },
    start: {
      heading: 'Create a new listing',
      store: '1. Store',
      storePlaceholder: 'Choose your Amazon store',
      storeOption: '{{name}} · {{marketplace}}',
      asin: '2. Product ASIN',
      asinPlaceholder: 'B0CQ4J4YFL',
      asinHint: 'The 10-character code on the Amazon product page, e.g. B0CQ4J4YFL',
      asinInvalid: 'An ASIN is 10 letters and numbers, like B0CQ4J4YFL.',
      storeRequired: 'Choose the Amazon store this product is sold in.',
      submit: 'Create listing',
      submitting: 'Creating…',
      started: 'Creating a listing for {{asin}}',
      noRunsLeft: 'You have created all of today’s listings. You can create more after {{time}}.',
      noStores: 'None of your stores is connected to Amazon yet',
      noStoresHint: 'Connect a store to your Amazon Seller account first, then come back here.',
    },
    storePicker: {
      search: 'Search stores, seller IDs',
      searchLabel: 'Search stores',
      list: 'Stores',
      count_one: '{{count}} store',
      count_other: '{{count}} stores',
      empty: 'No stores match “{{query}}”',
      emptyHint: 'Search by store name, marketplace or seller ID.',
      keyMove: 'to move ·',
      keyChoose: 'to choose ·',
      keyClose: 'to close',
    },
    allowances: {
      loading: 'Checking how many listings you can create today',
      runsLeft_one: 'You can create {{count}} more listing today',
      runsLeft_other: 'You can create {{count}} more listings today',
      runsNone: 'You have no listings left today',
      versionsLeft_one: '{{count}} new photo version left',
      versionsLeft_other: '{{count}} new photo versions left',
      versionsNone: 'No new photo versions left',
      unavailable: 'We could not check today’s limits. You can still try.',
      label: 'Today’s limits',
      runsShort: 'Listings',
      versionsShort: 'Photo redos',
      valueShort: '{{remaining}}/{{limit}}',
      resetsLocal: 'Resets at {{time}} (local time)',
    },
    jobs: {
      heading: 'Your listings',
      statusFilter: 'Status',
      allStatuses: 'All',
      pageSize: 'Rows',
      empty: 'No listings yet',
      emptyHint: 'Create your first one above.',
      emptyFiltered: 'No listings with this status',
      emptyFilteredHint: 'Choose another status, or show them all.',
      showAll: 'Show all listings',
      columns: {
        product: 'Product',
        status: 'Status',
        created: 'Created',
        actions: 'Actions',
      },
      untitled: 'Getting the product details…',
      open: 'Open',
      openLabel: 'Open listing for {{name}}',
      creating: 'Creating… {{percent}}',
    },
    job: {
      back: 'All listings',
      notAvailable: 'Not available',
      mainPhotoAlt: 'Main photo of {{name}}',
      noPhoto: 'No photo yet',
      asin: 'ASIN {{asin}}',
      storeLabel: 'Store',
      asinLabel: 'ASIN',
      createdLabel: 'Created',
      created: 'Created {{time}}',
      ready: 'Your listing is ready',
      creating: 'We’re creating your listing — this takes a few minutes',
      progressLabel: 'Progress',
      now: 'Now: {{step}}',
      stepDone: 'Done',
      stepActive: 'In progress',
      stepPending: 'Not started',
      stepFailed: 'Stopped here',
      runningHint: 'This page updates by itself — you can leave and come back.',
      failedTitle: 'We could not finish this listing',
      failedMessage:
        'Only this attempt was used. Create a new listing for the same ASIN to try again.',
      showDetail: 'Show details',
      hideDetail: 'Hide details',
      noDetail: 'No reason was given.',
      notes_one: '{{count}} thing to check',
      notes_other: '{{count}} things to check',
      noteDetails: 'Show details',
      noteHideDetails: 'Hide details',
      stepsLabel: 'Steps',
      steps: {
        photos: 'Choose photos',
        text: 'Review text',
        download: 'Download',
      },
      instructions: {
        photos:
          'Tick the photos you want on Amazon. Click a photo to see it larger, or ask for a new version.',
        text: 'We wrote a new title, bullet points and description. Keep ours or switch back to the original.',
        download: 'Here is everything in your download.',
      },
    },
    download: {
      photos_one: '{{count}} photo',
      photos_other: '{{count}} photos',
      title: 'Title',
      bullets_one: '{{count}} bullet point',
      bullets_other: '{{count}} bullet points',
      description: 'Description',
      keywords_one: '{{count}} search term',
      keywords_other: '{{count}} search terms',
      photosNone: 'No photos chosen',
      titleNone: 'No title',
      bulletsNone: 'No bullet points chosen',
      descriptionNone: 'No description',
      keywordsNone: 'No search terms',
      hint: 'Upload these to Amazon Seller Central.',
      checklist: 'What is included',
    },
    compare: {
      original: 'Original',
      ai: 'New',
      useOriginal: 'Original',
      useAi: 'New',
      inUse: 'In use',
      noOriginal: 'We could not get the original listing from Amazon.',
    },
    titleSection: {
      heading: 'Title',
      choose: 'Which title to use',
      editPhrases: 'Choose title words',
      counter: '{{count}} / {{max}}',
      tooLong: 'Too long for Amazon — remove a few words',
      noPhrases: 'We could not write a new title.',
      noPhrasesSelected: 'No words chosen.',
    },
    keyPhrases: {
      title: 'Title words',
      description: 'The title is made of the words you keep, joined with “ | ”, in this order.',
      selected: '{{count}} of {{total}} chosen',
      preview: 'Title preview',
      apply: 'Apply',
      cancel: 'Cancel',
    },
    bullets: {
      heading: 'Bullet points',
      hint: 'Shown under the title on Amazon. Choose up to {{max}}.',
      selectedCount: '{{count}} of {{max}} chosen',
      edit: 'Change bullet points',
      keep: 'Use new bullet point {{index}}',
      keepOriginal: 'Use original bullet point {{index}}',
      limitReached: 'You already have {{max}}. Untick one first.',
      keptLabel: 'Bullet points in use',
      noneKept: 'No bullet points chosen yet.',
      showMore_one: 'See {{count}} more suggestion',
      showMore_other: 'See {{count}} more suggestions',
      hideMore: 'Hide suggestions',
      dialogTitle: 'Change bullet points',
      dialogDescription: 'Choose up to {{max}} and change the wording if you like.',
      text: 'Text of bullet point {{index}}',
      length: '{{count}} / {{max}}',
      tooLong: 'Too long for Amazon — keep it under {{max}} characters.',
      add: 'Add a bullet point',
      apply: 'Apply',
      cancel: 'Cancel',
    },
    descriptionSection: {
      heading: 'Description',
      choose: 'Which description to use',
      edit: 'Pick a different version',
      version: 'Version {{index}} of {{total}}',
      variant: 'Version {{index}}',
      none: 'No description.',
      dialogTitle: 'Description',
      dialogDescription: 'Pick a version and change the wording if you like.',
      text: 'Text of version {{index}}',
      length: '{{count}} / {{max}}',
      tooLong: 'Too long for Amazon — keep it under {{max}} characters.',
      apply: 'Use version {{index}}',
      cancel: 'Cancel',
    },
    keywords: {
      heading: 'Search terms',
      hint: 'Hidden from shoppers — they help people find your product.',
      none: 'No search terms yet.',
    },
    copy: {
      title: 'Copy title',
      bullets: 'Copy bullet points',
      description: 'Copy description',
      keywords: 'Copy search terms',
      asin: 'Copy ASIN',
      done: 'Copied',
      failed: 'Could not copy. Your browser blocked it.',
    },
    images: {
      tabsLabel: 'Photo types',
      originalNone: 'This product has no photos on Amazon.',
      tabs: {
        lifestyle: 'Main & lifestyle',
        product: 'Product details',
        infographic: 'Infographics',
        originals: 'From Amazon',
      },
      tabCount: '{{label}} ({{total}})',
      tabEmpty: 'No photos here.',
      view: 'Layout',
      grid: 'Grid',
      carousel: 'One at a time',
      selected_one: '{{count}} photo chosen',
      selected_other: '{{count}} photos chosen',
      alt: '{{label}} photo {{index}}',
      originalAlt: 'Amazon photo {{index}}',
      use: 'Use this photo',
      keep: 'Use {{name}}',
      enlarge: 'See {{name}} larger',
      newVersion: 'New version',
      regenerate: 'New version of {{name}}',
      newVersionsAll: 'New versions of all',
      regenerateAll: 'New versions of all {{label}} photos',
      regenerating: 'Making a new version…',
      main: 'Main photo',
      passes: 'Ready for Amazon',
      fails: 'Needs a look',
      issuesLabel: 'Why {{name}} needs a look',
      size: '{{width}} × {{height}} pixels',
      previous: 'Previous photo',
      next: 'Next photo',
      show: 'Show {{name}}',
      position: '{{index}} of {{total}}',
    },
    regenerate: {
      titleImage: 'New version of {{name}}',
      titleCategory: 'New versions of all {{label}} photos',
      describe: 'Describe the photo you want',
      placeholder:
        'Close-up of the product on a wooden desk beside a laptop, soft morning light, no people',
      hint: 'Say where the product is, how close the shot is, the light and whether people appear. Leave it blank for a fresh take.',
      imageNote: 'Your real product always stays as it is — describe the scene around it.',
      categoryNote_one: 'Your description is used for the {{count}} photo in this group.',
      categoryNote_other:
        'Your description is used for each of the {{count}} photos in this group.',
      mainNote:
        'This is the main photo. Amazon wants your product alone on a pure white background, so that always stays — describe how the product should be shown.',
      mainPlaceholder:
        'Three-quarter view from the left, slightly from above, soft shadow underneath',
      mainHint:
        'Say the angle, how close the shot is and the light. Scenes, props and text are left out. Leave it blank for a clean white-background shot.',
      length: '{{count}} / {{max}}',
      tooLong: 'Keep the description under {{max}} characters.',
      cost: 'Only {{what}} changes; your text and other photos stay as they are. Each new version uses one from today’s allowance.',
      costImage: 'this photo',
      costCategory: 'the photos in this group',
      allowance: '{{remaining}} of {{limit}} new versions left today.',
      exhausted: 'You have used all of today’s new versions. More after {{time}}.',
      confirm: 'Make new version',
      cancel: 'Cancel',
      queued_one: 'Making {{count}} new version. It appears here when it is ready.',
      queued_other: 'Making {{count}} new versions. They appear here when they are ready.',
      timedOut_one: '{{count}} photo is taking longer than usual. Check back before asking again.',
      timedOut_other:
        '{{count}} photos are taking longer than usual. Check back before asking again.',
      failed: 'We could not start the new version.',
    },
    actions: {
      save: 'Save my choices',
      saving: 'Saving…',
      saved: 'Your choices are saved',
      saveFailed: 'Your choices were not saved.',
      discard: 'Undo changes',
      unsaved: 'Unsaved changes',
      export: 'Download listing',
      exporting: 'Preparing download…',
      exported: 'Downloaded {{file}}',
      exportPartial_one: '{{count}} photo could not be added to the download.',
      exportPartial_other: '{{count}} photos could not be added to the download.',
      exportFailed: 'The download could not be prepared.',
      exportScope_one: 'Includes {{count}} photo and your listing text',
      exportScope_other: 'Includes {{count}} photos and your listing text',
    },
    listingFile: {
      asin: 'ASIN',
      product: 'Product',
      title: 'Title',
      bullets: 'Bullet points',
      description: 'Description',
      keywords: 'Search terms',
    },
  },
  inventorySync: {
    title: 'Inventory sync',
    description:
      'Push each store’s stock to its marketplace, and see what became of every listing.',
    stores: {
      title: 'Stores',
      empty: 'No store can be pushed yet',
      emptyHint: 'A store appears here once it is on a marketplace with a stock push.',
      columns: { name: 'Store', marketplace: 'Marketplace', channel: 'Push' },
      open: 'Open {{name}}',
    },
    // The integration each store's stock is pushed through (`connector`).
    connectors: {
      amazon_seller: 'Amazon Seller',
      bol_retailer: 'bol Retailer',
      otto_seller: 'OTTO Seller',
      trendyol_seller: 'Trendyol Seller',
      shopify_admin: 'Shopify Admin',
      walmart_marketplace: 'Walmart Marketplace',
      noon_partner: 'noon Partner',
      flipkart_seller: 'Flipkart Seller',
    },
    actions: {
      pushNow: 'Push now',
      dryRun: 'Dry run',
      dryRunHint: 'See what would change, sending nothing.',
      uploadSheet: 'Upload sheet',
      template: 'Download template',
      queued: 'Push queued. It appears below in a moment.',
      queuedDryRun: 'Dry run queued. It appears below in a moment.',
    },
    sheet: {
      title: 'Upload a stock sheet',
      description:
        'Columns “SKU Code” and “Quantity”. The quantities are sent exactly as typed — the store’s buffer does not apply.',
      file: 'Sheet (.xlsx or .csv)',
      dryRun: 'Dry run first — preview what would change',
      submit: 'Upload',
      problems: 'The sheet was not pushed. Fix these rows and upload it again:',
      row: 'Row {{row}}',
      accepted_one: '{{count}} SKU taken from the sheet.',
      accepted_other: '{{count}} SKUs taken from the sheet.',
    },
    runs: {
      title: 'Runs',
      empty: 'No runs yet',
      emptyHint: 'Push now, or wait for the schedule.',
      columns: { started: 'Started', kind: 'Kind', status: 'Status' },
      requested: 'Requested',
      scheduled: 'Scheduled',
      dryRun: 'Dry run',
      open: 'Open run {{id}}',
    },
    run: {
      title: 'Run {{id}}',
      heading: {
        push: 'Push · {{when}}',
        dryRun: 'Dry run · {{when}}',
        scheduled: 'Scheduled push · {{when}}',
      },
      close: 'Close',
      empty: 'Every listing updated or already correct.',
      emptyDryRun: 'Nothing would change: every listing is already correct.',
      summary: {
        waiting: 'Waiting to start. This updates by itself.',
        running: 'Running now. The counts appear when it finishes.',
        canceling: 'Being cancelled.',
        canceled: 'Cancelled before it finished.',
      },
      failed: {
        title: 'This push didn’t finish',
        titleDryRun: 'This dry run didn’t finish',
        nothingSent: 'Nothing was sent to the marketplace.',
        nothingChanged: 'No listings were processed, so nothing changed on the marketplace.',
        someUpdated_one: '{{count}} listing was updated before it stopped.',
        someUpdated_other: '{{count}} listings were updated before it stopped.',
        mayHaveChanged: 'Some listings may have been updated before it stopped.',
        next: 'Try again. If it fails again, contact support and quote the reference below.',
        reference: 'Reference',
        runRef: 'Run',
        requestRef: 'Request',
        technical: 'Technical details',
      },
      truncated: 'Only the first rows are shown. The full outcome file is in the archive.',
      scheduledNote:
        'A scheduled run covers every store on this marketplace; only this store’s listings are shown.',
      counts: {
        updated: 'Updated',
        unchanged: 'Unchanged',
        unmatched: 'Unmatched',
        ambiguous: 'Ambiguous',
        failed: 'Failed',
        skipped: 'Skipped',
      },
      columns: {
        key: 'Listing',
        outcome: 'Outcome',
        desired: 'Quantity',
        previous: 'Was',
        error: 'Why',
      },
      outcomes: {
        updated: 'Updated',
        unchanged: 'Unchanged',
        planned: 'Would change',
        unmatched: 'Not listed on the marketplace',
        ambiguous: 'Ambiguous',
        skipped: 'Skipped',
        failed: 'Failed',
        not_attempted: 'Not attempted',
      },
    },
  },
  sessionEnded: {
    title: 'Your session ended',
    expired: 'For your security you have been signed out. Taking you to sign in…',
    signedOutElsewhere: 'You signed out in another tab. Taking you to sign in…',
    mfaRequired:
      'Your organization now requires two-step verification. Taking you to sign in to set it up…',
    signIn: 'Sign in now',
  },

  notFound: {
    title: 'Page not found',
    description: 'That address does not match anything in VentaWiz.',
    back: 'Back to the dashboard',
  },
  // T7 Margin Issues.
  ticketsT07: {
    line: 'Line {{sku}}',
    order: 'Order {{order}}',
    figures: {
      selling_price: 'Selling price',
      commission: 'Commission',
      purchase_cost: 'Purchase cost',
      shipping_cost: 'Shipping cost',
      additional_cost: 'Additional cost',
      buying_cost: 'Net purchase cost',
      net_margin: 'Net margin',
      net_margin_percent: 'Net margin %',
      expected_margin: 'Expected margin %',
    },
    footer: {
      buyingCost:
        'Net purchase cost = Purchase + Shipping + Additional = {{purchase}} + {{shipping}} + {{additional}} = {{total}}',
      netMargin:
        'Net margin = Selling price − Commission − Net purchase cost = {{selling}} − {{commission}} − {{buying}} = {{net}}',
      netMarginPercent:
        'Net margin % = Net margin ÷ Net purchase cost = {{net}} ÷ {{buying}} = {{percent}}',
      meets: '→ meets target ({{target}})',
      below: '→ below target ({{target}})',
      source: 'Selling price source: {{source}} · Updated: {{updated}}',
    },
    priceSources: {
      buybox: 'Buy box',
      order_item: 'Recent order',
      price_table: 'Catalogue price',
      none: 'Unknown',
    },
    fields: {
      verdict: 'Issue type',
      reviewNote: 'Correct data or fix',
      comment: 'Comment',
    },
    forSku: ' for {{sku}}',
    chooseVerdict: 'Choose an issue type',
    verdicts: {
      selling_price_issue: 'Selling price issue',
      cost_issue: 'Cost issue',
      commission_issue: 'Commission issue',
      others: 'Others',
      accept: 'Accept (legacy)',
      needs_work: 'Needs work (legacy)',
    },
    stamp: '{{by}}',
    stampAt: '{{by}} · {{at}}',
    saveFor: 'Save {{sku}}',
    saved: 'Saved {{sku}}',
  },
  // T23 Sales Drop.
  ticketsT23: {
    columns: {
      item_id: 'Item',
      asin: 'ASIN',
      retailer_sku: 'Seller SKU',
      product_title: 'Product',
      previous_run_rate: 'Previous run-rate',
      current_run_rate: 'Current run-rate',
      drop_pct: 'Drop',
      reason: 'Reason',
    },
  },
  // T24 Customer Interest Drop.
  ticketsT24: {
    columns: {
      item_id: 'Item',
      asin: 'ASIN',
      retailer_sku: 'Seller SKU',
      product_title: 'Product',
      signals: 'Signals',
      previous_rating: 'Previous rating',
      current_rating: 'Current rating',
      previous_reviews: 'Previous reviews',
      current_reviews: 'Current reviews',
      previous_clicks: 'Previous clicks',
      current_clicks: 'Current clicks',
      reason: 'Reason',
    },
    signals: {
      rating: 'Rating',
      reviews: 'Reviews',
      clicks: 'Clicks',
    },
  },
  // T25 Competitor Price Drop.
  ticketsT25: {
    columns: {
      item_id: 'Item',
      asin: 'ASIN',
      retailer_sku: 'Seller SKU',
      product_title: 'Product',
      competitor_seller: 'Competitor seller',
      previous_comp_price: 'Previous competitor price',
      current_comp_price: 'Current competitor price',
      our_price: 'Our price',
      drop_pct: 'Drop',
      reason: 'Reason',
    },
  },
  // T26 FBA Reimbursement Claim.
  ticketsT26: {
    howTo: {
      title: 'Working this ticket',
      intro:
        'Amazon marked these FBA units unfulfillable and does not tell you so. Reimbursement claims must be filed within about 60 days, so act promptly.',
      claimable:
        'Reimbursable units were damaged in Amazon’s warehouse or by its carrier: file a reimbursement claim in Seller Central with the row’s ASIN and FNSKU.',
      notClaimable:
        'Units damaged by a customer or distributor, defective or expired cannot be claimed: create a removal or disposal order.',
    },
    columns: {
      item_id: 'Item',
      retailer_sku: 'Seller SKU',
      asin: 'ASIN',
      product_title: 'Product',
      total_claimable_qty: 'Reimbursable',
      total_non_reimbursable_qty: 'Not reimbursable',
      total_unfulfillable_qty: 'Total',
      recommended_action: 'Recommendation',
    },
    hints: {
      total_claimable_qty:
        'Warehouse or carrier damaged (Amazon’s fault): file a reimbursement claim.',
      total_non_reimbursable_qty:
        'Customer or distributor damaged, defective or expired: not reimbursable; remove or dispose.',
      total_unfulfillable_qty: 'All unfulfillable units for this SKU.',
    },
    causes: {
      warehouse_damaged_qty: 'Warehouse damaged: {{count}}',
      carrier_damaged_qty: 'Carrier damaged: {{count}}',
      customer_damaged_qty: 'Customer damaged: {{count}}',
      distributor_damaged_qty: 'Distributor damaged: {{count}}',
      defective_qty: 'Defective: {{count}}',
      expired_qty: 'Expired: {{count}}',
    },
    alreadyReimbursed: 'Already reimbursed — do not re-file',
    recommendations: {
      file_claim_and_remove: 'File a claim and remove the rest',
      file_reimbursement_claim: 'File a claim',
      remove_or_dispose: 'Remove or dispose',
    },
    fileClaim: 'File claim',
    fileClaimFor: 'File a claim for {{sku}} in Seller Central (opens in a new tab)',
    copy: 'Copy details',
    copyFor: 'Copy the claim details for {{sku}}',
    copied: 'Claim details for {{sku}} copied',
    copyFailed: 'Could not copy the claim details.',
    dispose: 'Remove / dispose',
    disposeFor: 'Remove or dispose of {{sku}} in Seller Central (opens in a new tab)',
    packet: {
      heading: 'FBA Reimbursement — Amazon-damaged (reimbursable) units',
      product: 'Product: {{title}}',
      ids: 'ASIN: {{asin}}  |  Seller SKU: {{sku}}  |  FNSKU: {{fnsku}}',
      marketplace: 'Marketplace: {{name}}',
      quantities_one:
        'Warehouse-damaged: {{warehouse}}, Carrier-damaged: {{carrier}}  (total reimbursable: {{count}} unit)',
      quantities_other:
        'Warehouse-damaged: {{warehouse}}, Carrier-damaged: {{carrier}}  (total reimbursable: {{count}} units)',
      request:
        'These units were damaged by Amazon (warehouse/carrier) and are currently unfulfillable — requesting reimbursement.',
    },
  },
  ticketsT11: {
    openAdGroup: 'Open ad group {{name}} in the Amazon master (opens in a new tab)',
    result: {
      title_one: 'Data result ({{count}} entry)',
      title_other: 'Data result ({{count}} entries)',
    },
    notPushed:
      'Decisions are recorded in VentaWiz and are not sent to Amazon. Make the change in the Amazon Ads console.',
    columns: {
      adgroup_name: 'Ad group',
      target_or_search_term: 'Target or search term',
      item_type: 'Kind',
      targeting_type: 'Targeting',
      existing_bid: 'Current bid',
      impressions: 'Impressions',
      clicks: 'Clicks',
      ctr: 'CTR',
      units: 'Units',
      conversion: 'Conversion',
      spend: 'Spend',
      cpc: 'CPC',
      sales: 'Sales',
      acos: 'ACOS',
      action_type: 'Verdict',
      target_acos: 'Target ACOS',
      target_conversion: 'Target conversion',
      target_ctr: 'Target CTR',
      new_bid: 'New bid',
      bid_delta: 'Bid change',
      reasoning: 'Reasoning',
    },
    itemTypes: { target: 'Target', searchterm: 'Search term' },
    verdicts: { good: 'Good', bad: 'Bad' },
    actions: {
      new_target: 'Add as a new target',
      move_to_negative: 'Move to negative',
      move_to_negative_and_new_target: 'Move to negative and add a new target with this bid',
      adjust_bid: 'Adjust the bid',
      pause_target: 'Pause the target',
      do_nothing: 'Do nothing',
    },
    bid: {
      notANumber: 'Enter a bid above zero.',
      tooManyDecimals: 'Use at most two decimals.',
      belowMinimum: 'The bid must be at least the marketplace minimum of {{min}}.',
    },
  },
  ticketsT12: {
    notPushed:
      'A pause is recorded in VentaWiz and is not sent to Amazon. Pause the ad in the Amazon Ads console.',
    nonRetail: 'Non-retail',
    columns: {
      mp_sku_code: 'Marketplace SKU',
      asin: 'ASIN',
      warehouse_stock: 'Warehouse stock',
      fba_stock: 'Marketplace stock',
      buybox_win_percentage: 'Buy box won',
    },
    actions: { pause_adproduct: 'Pause the ad', do_nothing: 'Do nothing' },
    // The decide-per-row table T11, T12 and T14 share.
    table: {
      action: 'Action',
      actionFor: 'Action for {{row}}',
      choose: 'Choose an action…',
      editFor: '{{field}} for {{row}}',
      outcome: 'Outcome',
      reflected: 'Applied on Amazon',
      failed: 'Failed on Amazon',
      notExecuted: 'Recorded — apply it in the Amazon Ads console',
      save_one: 'Save {{count}} decision',
      save_other: 'Save {{count}} decisions',
      saving: 'Saving…',
      saved_one: 'Saved {{count}} decision.',
      saved_other: 'Saved {{count}} decisions.',
      reviewed: 'Every item is decided — the ticket is now reviewed.',
    },
  },
  ticketsT14: {
    columns: {
      item_id: '#',
      ad_group_name: 'Ad group',
      asin: 'ASIN',
      product_title: 'Product',
      previous_week_rank: 'Last week’s rank',
      new_rank: 'This week’s rank',
    },
    outcomes: {
      checked_relevance: 'Checked relevance',
      fixed_inputs: 'Fixed inputs (marketing or retail)',
      added_new_targets: 'Added new targets',
      adjusted_bids: 'Adjusted bids',
      other: 'Other (say what in a comment, with the ASIN)',
      did_nothing: 'Did nothing',
    },
  },
  ticketsT19: {
    columns: {
      campaign_id: 'Campaign',
      campaign_type: 'Type',
      budget_in_vh: 'Budget in the master',
      budget_on_ams: 'Budget on Amazon',
    },
  },
  ticketsT22: {
    notPushed:
      'Decisions are recorded in VentaWiz and are not sent to Amazon. Apply accepted changes in the Amazon Ads console.',
    readOnly:
      'These suggestions came from the first rules engine, which VentaWiz does not run. They are shown as they were decided and cannot be decided here.',
    run: {
      link: 'Open the rule run that raised this ticket',
      legacy: 'Raised by VentaHub run #{{id}}',
    },
    raisedByRun: 'Tickets raised by this run',
    items: {
      title_one: '{{count}} item',
      title_other: '{{count}} items',
      filtered: '{{shown}} of {{count}} items',
      empty: 'This ticket has no items',
      emptyHint: 'The engine raised it without any matched rows.',
      noMatch: 'No items match these filters',
      noMatchHint: 'Clear a filter to see more.',
    },
    toolbar: {
      expandPending: 'Open pending',
      expandAll: 'Open all',
      collapseAll: 'Close all',
      acceptSuggestions: 'Accept system suggestions',
      declineRest: 'Decline the rest',
      save_one: 'Save {{count}} decision',
      save_other: 'Save {{count}} decisions',
      saving: 'Saving…',
      status: 'Show items',
      statuses: { all: 'All', pending: 'Pending', done: 'Done', failed: 'Failed' },
      actionTypes: 'Only these actions',
      sort: 'Sort items',
      sortKeys: {
        impressions: 'Impressions',
        acos: 'ACOS',
        clicks: 'Clicks',
        progress: 'Progress',
      },
      sortBy: 'Sort by {{by}}',
      sortedDescending: 'Sorted by {{by}}, highest first',
      sortedAscending: 'Sorted by {{by}}, lowest first',
    },
    stats: {
      items: 'Items',
      pending: '{{count}} pending',
      done: '{{count}} done',
      failed: '{{count}} failed',
      suggestions: 'Suggestions',
      matched: '{{count}} matched',
      accepted: '{{count}} accepted',
      declined: '{{count}} declined',
    },
    columns: {
      expand: 'Open',
      adGroup: 'Ad group',
      type: 'Type',
      entity: 'Entity',
      impressions: 'Impressions',
      clicks: 'Clicks',
      ctr: 'CTR',
      units: 'Units',
      conversion: 'Conversion',
      spend: 'Spend',
      cpc: 'CPC',
      sales: 'Sales',
      acos: 'ACOS',
      bid: 'Bid',
      progress: 'Progress',
    },
    rowTypes: {
      TARGET: 'Target',
      SEARCH_TERM: 'Search term',
      AD_PRODUCT: 'Ad product',
      AD_GROUP: 'Ad group',
      NEGATIVE: 'Negative',
      CAMPAIGN: 'Campaign',
      OTHER: 'Other',
    },
    expand: 'Show the suggestions for {{name}}',
    collapse: 'Hide the suggestions for {{name}}',
    progress: '{{done}}/{{total}}',
    progressLabel: '{{done}} of {{total}} suggestions decided',
    suggestionHint: 'The system would pick: {{action}} via {{rule}}',
    matchedAt: 'Matched at',
    sharedOneRule: 'all {{count}} suggestions below are {{code}} on this key',
    sharedRules: 'all {{count}} suggestions below ({{rules}} rules) share this key',
    matchedRules_one: '{{count}} matched rule',
    matchedRules_other: '{{count}} matched rules',
    groupKey: {
      ag_id: 'Ad group',
      target_id: 'Target',
      search_term: 'Search term',
      ad_id: 'Ad',
      purchased_asin: 'Purchased ASIN',
      untargeted_competitor_asin: 'Competitor ASIN',
      comp_asin: 'Competitor ASIN',
      keyword_text: 'Keyword',
      linked_ag_id: 'Linked ad group',
    },
    card: {
      subject: '{{code}} {{slot}} on {{entity}}',
      openRule: 'Open rule {{code}} in the rules catalogue',
      suggested: 'Suggested',
      master: 'Master',
      by: 'by {{name}}',
      wouldAccept: 'Would auto-accept',
      wouldAcceptShadow:
        'An automation covers this rule in shadow mode. Nothing was applied; the engine would have accepted it.',
      wouldAcceptPreview:
        'This run was a preview. On a real run the automation would have accepted this.',
      automationFailed: 'Automation failed',
      automationFailedDefault: 'The automatic change failed.',
      coefficient: 'Priority {{value}}',
      liveCoefficient: 'Live priority {{value}}',
      lookback: 'Lookback',
      lookbackDays_one: '{{count}} day',
      lookbackDays_other: '{{count}} days',
      performance: 'Performance vs target',
      status: {
        recorded: 'Recorded',
        declined: 'Declined',
        notExecuted: 'Not sent to Amazon',
        reflected: 'Applied on Amazon',
        alreadyExists: 'Already on Amazon',
        failed: 'Failed: {{category}}',
        failedDefault: 'The change was not applied on Amazon.',
      },
      notExecutedHint: 'Recorded — apply it in the Amazon Ads console.',
      alreadyExistsHint: 'The target already exists in the ad group — nothing was changed.',
      details: 'Details',
      trace_one: 'Engine trace ({{count}} step)',
      trace_other: 'Engine trace ({{count}} steps)',
      accept: 'Accept',
      doNothing: 'Do nothing',
      acceptLabel: 'Accept {{label}}',
      doNothingLabel: 'Do nothing on {{label}}',
    },
    move: {
      anySource: 'Every matching ad group',
      pauseIn_one: 'Pause in {{count}} ad group',
      pauseIn_other: 'Pause in {{count}} ad groups',
    },
    bid: {
      current: 'Current',
      new: 'New bid',
      min: 'Minimum',
      delta: 'Change {{value}}',
      reset: 'Reset to the engine’s bid for {{label}}',
      formula: 'Formula',
      clamped: 'Raised to the marketplace minimum of {{min}}',
      nudged: 'No-op nudge ×{{multiplier}}',
      calculation: 'Bid calculation',
      errors: {
        notANumber: 'Enter a bid above zero.',
        tooManyDecimals: 'Use at most two decimals.',
        belowMinimum: 'The bid must be at least the marketplace minimum of {{min}}.',
      },
    },
    matchType: { label: 'Match type', saved: 'Match type: {{value}}' },
    reraise: {
      times: 'Raised {{count}}×',
      last: 'last #{{id}}',
      on: 'on {{date}}',
      decided: '({{decision}})',
      decidedOn: '({{decision}} {{date}})',
      accepted: 'accepted',
      declined: 'declined',
      because: 'back because:',
      reasons: {
        unreviewed: 'the previous ticket is still open',
        declinedLastTime: 'it was declined last time',
        noDispatch: 'it was accepted but never applied on Amazon',
        notInAmazon: 'it is not on Amazon',
      },
      dispatchFailed: 'Amazon rejected it: {{detail}}',
    },
    bulk: {
      accepted_one: 'Accept is pre-selected on {{count}} top suggestion. Review, then save.',
      accepted_other: 'Accept is pre-selected on {{count}} top suggestions. Review, then save.',
      declined_one: 'Do nothing is pre-selected on {{count}} suggestion. Review, then save.',
      declined_other: 'Do nothing is pre-selected on {{count}} suggestions. Review, then save.',
      nothing: 'Nothing is left to pre-select.',
    },
    save: {
      confirmTitle_one: 'Save {{count}} decision?',
      confirmTitle_other: 'Save {{count}} decisions?',
      confirmBody:
        'A saved decision cannot be changed. Accepted changes are recorded here, not sent to Amazon.',
      confirm: 'Save',
      cancel: 'Keep reviewing',
      saved_one: 'Saved {{count}} decision.',
      saved_other: 'Saved {{count}} decisions.',
      skipped_one: '{{count}} suggestion was already decided and was left as it was.',
      skipped_other: '{{count}} suggestions were already decided and were left as they were.',
      reviewed: 'Every suggestion is decided — the ticket is now reviewed.',
      invalid: 'Fix the bids marked in red before saving.',
    },
  },

  // AI Analyst, ported from VentaHub (modules under ./en/aiAnalyst*).
  aiAnalyst: {
    ...aiAnalyst,
    parts: aiAnalystParts,
    message: aiAnalystMessage,
    composer: aiAnalystComposer,
    history: aiAnalystHistory,
    shell: aiAnalystShell,
  },

  // The cookie banner and dialog. The English is the web kit's own bundle, so
  // the three apps say the same thing; a translation overrides it here, key for
  // key (`ConsentLabels`).
  cookieConsent: defaultConsentLabels,
} as const
